import React, { useState, useEffect } from 'react';
import { Camera, BarChart3, CheckSquare, FileText, Shield, Settings, Menu, X, ChevronRight, ChevronDown, Trash2, AlertTriangle, Search, Activity, Users, Clock, RefreshCw, Plus } from 'lucide-react';

// ============================================================================
// ROLE-BASED ACCESS CONTROL
// ============================================================================

// null = unrestricted (all modules). Array = only those module IDs are accessible.
const ROLE_MODULE_ACCESS = {
  developer:       ['devops', 'quicktrack', 'timeboard', 'leaveboard', 'learntrack', 'vendorevents'],
  dataops:         ['dataops', 'quicktrack', 'timeboard', 'leaveboard', 'learntrack'],
  project:         ['plantrack', 'quicktrack', 'timeboard', 'leaveboard', 'learntrack'],
  qa:              ['quality', 'quicktrack', 'timeboard', 'leaveboard', 'learntrack'],
  devops_manager:  ['devops', 'quicktrack', 'learntrack', 'vendorevents', 'timeboard', 'leaveboard'],
};

// Pages that are always visible regardless of module restrictions
const ALWAYS_ALLOWED_PAGES = ['dashboard', 'modules'];

// Nav items hidden per role
const ROLE_HIDDEN_NAV = {
  developer:      ['tasks', 'reports', 'audit', 'settings'],
  dataops:        ['tasks', 'reports', 'audit', 'settings'],
  project:        ['tasks', 'reports', 'audit', 'settings'],
  qa:             ['tasks', 'reports', 'audit', 'settings'],
  devops_manager: ['tasks', 'reports', 'audit', 'settings'],
};

const getAllowedModules = (role) => ROLE_MODULE_ACCESS[role] ?? null;
const canAccessPage = (role, page) => {
  if (ALWAYS_ALLOWED_PAGES.includes(page)) return true;
  const allowed = getAllowedModules(role);
  return !allowed || allowed.includes(page);
};

// ============================================================================
// MODELS (Data Layer)
// ============================================================================

const API_BASE = '/api'; // Cloudflare Workers endpoint

class BaseModel {
  static async fetch(endpoint) {
    const response = await fetch(`${API_BASE}${endpoint}`);
    if (!response.ok) throw new Error('API request failed');
    return response.json();
  }

  static async post(endpoint, data) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('API request failed');
    return response.json();
  }
}

class DashboardModel extends BaseModel {
  static async getStats() {
    try { return await this.fetch('/dashboard'); } catch { return { stats: [], activities: [], quickModules: [] }; }
  }
  static async getBurnCharts() {
    try { return await this.fetch('/dashboard/burncharts'); }
    catch { return null; }
  }
}

class ModulesModel extends BaseModel {
  static async getModules() {
    return [
      {
        id: 'quicktrack',
        name: 'QuickTrack',
        icon: '⚡',
        color: '#667eea',
        description: 'Rapid issue tracking and ticket management for quick turnaround items.',
        status: 'Active',
        records: 342
      },
      {
        id: 'plantrack',
        name: 'PlanTrack',
        icon: '📋',
        color: '#28a745',
        description: 'Project planning, sprint management, and milestone tracking system.',
        status: 'Active',
        records: 156
      },
      {
        id: 'devops',
        name: 'DevOps',
        icon: '🔧',
        color: '#0366d6',
        description: 'CI/CD pipeline management, deployment tracking, and infrastructure monitoring.',
        status: 'Active',
        records: 89
      },
      {
        id: 'dataops',
        name: 'DataOps',
        icon: '📊',
        color: '#6f42c1',
        description: 'Data pipeline orchestration, ETL workflows, and Power BI integration.',
        status: 'Active',
        records: 234
      },
      {
        id: 'quality',
        name: 'Quality',
        icon: '✓',
        color: '#20c997',
        description: 'Quality assurance, test case management, and defect tracking.',
        status: 'Active',
        records: 523
      },
      {
        id: 'learntrack',
        name: 'LearnTrack',
        icon: '🎓',
        color: '#fd7e14',
        description: 'Learning management, training courses, and skill development tracking.',
        status: 'Active',
        records: 178
      },
      {
        id: 'vendorevents',
        name: 'Vendor Events',
        icon: '📡',
        color: '#e74c3c',
        description: 'Vendor event observability dashboard and lead tracking.',
        status: 'Active',
        records: 100
      },
      {
        id: 'timeboard',
        name: 'TimeBoard',
        icon: '⏱️',
        color: '#17a2b8',
        description: 'Time tracking, timesheet management, and work hour analytics.',
        status: 'Active',
        records: 892
      },
      {
        id: 'leaveboard',
        name: 'LeaveBoard',
        icon: '🏖️',
        color: '#e83e8c',
        description: 'Leave management, vacation tracking, and absence calendar.',
        status: 'Active',
        records: 445
      }
    ];
  }

  static async getPlanTrackData() {
    try { return await this.fetch('/plantrack'); }
    catch { return { stats: { total: 0, active: 0, completed: 0, onHold: 0, planning: 0 }, projects: [], milestones: [], sprints: [], sprint: null }; }
  }

  static async sendProjectStatusEmail(to) {
    try {
      const response = await fetch(`${API_BASE}/plantrack/email-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to })
      });
      const data = await response.json();
      return data;
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  static async getAppSettings() {
    try { return await this.fetch('/settings'); }
    catch { return {}; }
  }

  static async saveAppSettings(data) {
    try {
      const response = await fetch(`${API_BASE}/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async sendEodSummary(module) {
    try {
      const response = await fetch(`${API_BASE}/eod/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module })
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async sendQualityEodSummary(userName) {
    try {
      const response = await fetch(`${API_BASE}/eod/send-quality`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_name: userName })
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async getProjectDetail(projectId) {
    try {
      return await this.fetch(`/plantrack/projects/${projectId}`);
    } catch {
      // Fallback mock data for dev
      const allData = await this.getPlanTrackData();
      const project = allData.projects.find(p => p.id === projectId);
      return {
        project,
        tasks: [
          { id: 'TASK-2024-016', title: 'Design REST endpoint contracts', type: 'task', priority: 'P1', status: 'completed', assignee: 'John Developer', estimateHours: 16, storyPoints: 3, progress: 100 },
          { id: 'TASK-2024-017', title: 'Set up API gateway configuration', type: 'task', priority: 'P2', status: 'in_progress', assignee: 'Mike Fullstack', estimateHours: 8, storyPoints: 2, progress: 40 }
        ],
        comments: [
          { id: 1, userName: 'John Developer', content: 'Kicked off the API modernization. Starting with the authentication endpoints first.', createdAt: '2025-01-28T10:00:00' },
          { id: 2, userName: 'Jim Manager', content: 'Make sure we maintain backward compatibility with the existing clients during migration.', createdAt: '2025-01-29T14:00:00' },
          { id: 3, userName: 'Mike Fullstack', content: 'I can help with the gateway config once the endpoint contracts are finalized.', createdAt: '2025-02-01T09:00:00' }
        ],
        updates: [
          { id: 1, userName: 'John Developer', updateType: 'status_change', content: 'Project moved to active development', oldValue: 'planning', newValue: 'active', createdAt: '2025-01-28T08:00:00' },
          { id: 2, userName: 'John Developer', updateType: 'progress', content: 'API design phase completed, moving to implementation', oldValue: null, newValue: '35%', createdAt: '2025-02-04T16:00:00' }
        ],
        milestones: allData.milestones.filter(m => m.projectId === projectId)
      };
    }
  }

  static async createProject(data) {
    try { return await this.post('/plantrack/projects', data); }
    catch { return { success: true, projectId: `PROJ-${String(Date.now()).slice(-6)}` }; }
  }

  static async updateProject(projectId, data) {
    try {
      const response = await fetch(`${API_BASE}/plantrack/projects/${projectId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  static async addComment(projectId, content) {
    try { return await this.post(`/plantrack/projects/${projectId}/comments`, { content }); }
    catch { return { success: true, id: Date.now() }; }
  }

  static async addUpdate(projectId, content) {
    try { return await this.post(`/plantrack/projects/${projectId}/updates`, { content, updateType: 'general' }); }
    catch { return { success: true, id: Date.now() }; }
  }

  static async postProjectUpdateReport(preparedBy, role) {
    try {
      const response = await fetch(`${API_BASE}/plantrack/post-update-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prepared_by: preparedBy, role })
      });
      return response.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async createSprint(data) {
    return await this.post('/plantrack/sprints', data);
  }

  static async updateSprint(sprintId, data) {
    try {
      const r = await fetch(`${API_BASE}/plantrack/sprints/${sprintId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return r.json();
    } catch { return { success: false }; }
  }

  static async createProjectTask(projectId, data) {
    try { return await this.post(`/plantrack/projects/${projectId}/tasks`, data); }
    catch { return { success: true, taskId: `TASK-${String(Date.now()).slice(-7)}` }; }
  }
  static async deleteProjectTask(projectId, taskId) {
    try { const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/tasks/${taskId}`, { method: 'DELETE' }); return r.json(); }
    catch { return { success: false }; }
  }

  static async getDevOpsPipeline() {
    try {
      return await this.fetch('/devops');
    } catch {
      return {
        capacity: { total: 160, committed: 156, completed: 46, utilization: 97.5 },
        workload: [
          { name: 'John Developer', assigned: 76, capacity: 40, percentage: 190 },
          { name: 'Sarah Frontend', assigned: 20, capacity: 40, percentage: 50 },
          { name: 'Mike Fullstack', assigned: 16, capacity: 40, percentage: 40 },
          { name: 'Jim Manager', assigned: 0, capacity: 40, percentage: 0 }
        ],
        pipeline: {
          backlog: [
            { id: 'PR3-2024-001', title: 'Settlement Vault API Modernization', type: 'PROJECT', priority: 'P1', assignee: 'John Developer', estimate: 80, storyPoints: 13 },
            { id: 'ADH-2024-012', title: 'Update Monday.com integration webhook', type: 'ADHOC', priority: 'P2', assignee: 'Sarah Frontend', estimate: 8, storyPoints: 3 }
          ],
          ready: [
            { id: 'BUG-2024-007', title: 'Fix calendar sync issues', type: 'BUG', priority: 'P1', assignee: 'Mike Fullstack', estimate: 16, storyPoints: 5 },
            { id: 'BUG-2024-009', title: 'API rate limiting not enforced', type: 'BUG', priority: 'P1', assignee: 'John Developer', estimate: 8, storyPoints: 3 }
          ],
          inProgress: [
            { id: 'PR3-2024-002', title: 'Build Negotiator Worklist Dashboard', type: 'PROJECT', priority: 'P2', assignee: 'John Developer', estimate: 60, storyPoints: 8, progress: 50 },
            { id: 'TASK-2024-017', title: 'Set up API gateway configuration', type: 'task', priority: 'P2', assignee: 'Mike Fullstack', estimate: 8, storyPoints: 2, progress: 40 },
            { id: 'TASK-2024-018', title: 'Create worklist filter components', type: 'task', priority: 'P2', assignee: 'Sarah Frontend', estimate: 12, storyPoints: 3, progress: 60 }
          ],
          review: [
            { id: 'ADH-2024-015', title: 'Azure DevOps API integration', type: 'ADHOC', priority: 'P3', assignee: 'Sarah Frontend', estimate: 12, storyPoints: 5 }
          ],
          done: [
            { id: 'TASK-2024-016', title: 'Design REST endpoint contracts', type: 'task', priority: 'P1', assignee: 'John Developer', estimate: 16, storyPoints: 3, progress: 100 },
            { id: 'TASK-2024-020', title: 'Write unit tests for settlement module', type: 'task', priority: 'P3', assignee: 'Mike Fullstack', estimate: 12, storyPoints: 3, progress: 100 }
          ]
        },
        sprint: { name: 'Sprint 24', number: 24, startDate: '2025-01-20', endDate: '2025-02-02', status: 'active', goal: 'Complete Settlement Vault modernization' },
        ops: { pipelines: [
          { id: 'etl-001', name: 'Daily Users ETL', type: 'ETL', status: 'running', progress: 67, recordsProcessed: 1200000, durationSeconds: 7200, lastRun: '2025-01-31T10:00:00' },
          { id: 'rep-001', name: 'Sales Dashboard Refresh', type: 'Report', status: 'running', progress: 45, recordsProcessed: 3500000, durationSeconds: 5400, lastRun: '2025-01-31T10:30:00' },
          { id: 'etl-002', name: 'Real-Time Events Pipeline', type: 'ETL', status: 'running', progress: 92, recordsProcessed: 8100000, durationSeconds: 1800, lastRun: '2025-01-31T11:30:00' },
          { id: 'dash-001', name: 'Executive KPI Dashboard', type: 'Dashboard', status: 'completed', progress: 100, recordsProcessed: 250000, durationSeconds: 300, lastRun: '2025-01-31T06:00:00' },
          { id: 'pbi-001', name: 'Financial Analysis Report', type: 'Power BI', status: 'completed', progress: 100, recordsProcessed: 1800000, durationSeconds: 1200, lastRun: '2025-01-31T00:00:00' },
          { id: 'etl-003', name: 'Customer Data Sync', type: 'ETL', status: 'failed', progress: 45, recordsProcessed: 500000, durationSeconds: 600, lastRun: '2025-01-31T04:00:00', errorMessage: 'Connection timeout to source DB' }
        ]}
      };
    }
  }

  static async getDevOpsKanban() {
    try {
      return await this.fetch('/devops/kanban');
    } catch {
      // Reuse sprint pipeline data as fallback
      const data = await this.getDevOpsPipeline();
      return { pipeline: data.pipeline };
    }
  }

  static async createDevOpsTask(data) {
    try { return await this.post('/devops/tasks', data); }
    catch { return { success: true, taskId: `TASK-${String(Date.now()).slice(-7)}` }; }
  }

  static async createDataOpsTask(data) {
    try { return await this.post('/dataops/tasks', data); }
    catch { return { success: true, taskId: `DOP-${String(Date.now()).slice(-7)}` }; }
  }

  static async updateDevOpsTask(taskId, data) {
    try {
      const response = await fetch(`${API_BASE}/devops/tasks/${taskId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  static async getDevOpsTask(taskId) {
    try { return await this.fetch(`/devops/tasks/${taskId}`); }
    catch { return null; }
  }

  // ---- DataOps Model Methods ----

  static async getDataOpsData() {
    try {
      return await this.fetch('/dataops');
    } catch {
      return {
        metrics: { activePipelines: 3, dataProcessed: '19.9TB', reportsGenerated: 3, successRate: '67%' },
        pipelineStatus: [{ status: 'running', count: 3 }, { status: 'completed', count: 2 }, { status: 'failed', count: 1 }],
        powerbiReports: [
          { title: 'Executive KPI Dashboard', icon: '📊', lastUpdated: '2 hours ago', status: 'Published', views: 1234 },
          { title: 'Sales Performance', icon: '💰', lastUpdated: '5 hours ago', status: 'Published', views: 892 }
        ],
        activePipelines: [
          { name: 'Daily Users ETL', type: 'ETL', status: 'running', progress: 67, records: 1200000, started: '2 hours ago' },
          { name: 'Sales Dashboard Refresh', type: 'Report', status: 'running', progress: 45, records: 3500000, started: '90 min ago' }
        ],
        allPipelines: [
          { pipelineId: 'etl-001', pipelineName: 'Daily Users ETL', pipelineType: 'ETL', status: 'running', lastRun: '2025-02-15T10:00:00', durationSeconds: 7200, recordsProcessed: 1200000, progress: 67 },
          { pipelineId: 'rep-001', pipelineName: 'Sales Dashboard Refresh', pipelineType: 'Report', status: 'running', lastRun: '2025-02-15T10:30:00', durationSeconds: 5400, recordsProcessed: 3500000, progress: 45 },
          { pipelineId: 'etl-003', pipelineName: 'Customer Data Sync', pipelineType: 'ETL', status: 'failed', lastRun: '2025-02-15T04:00:00', durationSeconds: 600, recordsProcessed: 500000, progress: 45, errorMessage: 'Connection timeout' }
        ],
        allReports: [
          { reportId: 'pbi-exec-kpi', reportName: 'Executive KPI Dashboard', reportIcon: '📊', category: 'Executive', status: 'Published', lastUpdated: '2025-02-15T10:00:00', viewCount: 1234, isFavorite: 1 },
          { reportId: 'pbi-sales', reportName: 'Sales Performance', reportIcon: '💰', category: 'Sales', status: 'Published', lastUpdated: '2025-02-15T07:00:00', viewCount: 892, isFavorite: 0 }
        ]
      };
    }
  }

  static async getPipeline(pipelineId) {
    try { return await this.fetch(`/dataops/pipelines/${pipelineId}`); }
    catch { return null; }
  }

  static async createPipeline(data) {
    try { return await this.post('/dataops/pipelines', data); }
    catch { return { success: true }; }
  }

  static async updatePipeline(pipelineId, data) {
    try {
      const response = await fetch(`${API_BASE}/dataops/pipelines/${pipelineId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deletePipeline(pipelineId) {
    try {
      const response = await fetch(`${API_BASE}/dataops/pipelines/${pipelineId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async getReport(reportId) {
    try { return await this.fetch(`/dataops/reports/${reportId}`); }
    catch { return null; }
  }

  static async createReport(data) {
    try { return await this.post('/dataops/reports', data); }
    catch { return { success: true }; }
  }

  static async updateReport(reportId, data) {
    try {
      const response = await fetch(`${API_BASE}/dataops/reports/${reportId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteReport(reportId) {
    try {
      const response = await fetch(`${API_BASE}/dataops/reports/${reportId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- QMS Model Methods ----

  static async getQualityData(projectId) {
    try { return await this.fetch(`/quality${projectId ? '?projectId=' + projectId : ''}`); }
    catch {
      return { projects: [], project: null, requirements: [], testCases: [], bugs: [], metrics: { requirements: { total: 0, approved: 0, pending: 0 }, testCases: { total: 0, passed: 0, failed: 0, pending: 0 }, bugs: { total: 0, open: 0, inProgress: 0, resolved: 0 }, passRate: 0 } };
    }
  }

  static async enableProjectQa(projectId, data) {
    try { return await this.post(`/quality/projects/${projectId}/enable`, data); } catch { return { success: true }; }
  }

  static async getProjectOverview(projectId) {
    try { return await this.fetch(`/projects/${projectId}/overview`); }
    catch { return null; }
  }

  static async clearData(modules) {
    try {
      const r = await fetch(`${API_BASE}/settings/data`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modules })
      });
      return r.json();
    } catch { return { success: false, error: 'Network error' }; }
  }

  // ---- Milestone Engine Model Methods ----
  static async getMilestones(projectId = null) {
    try { return await this.fetch(`/plantrack/milestones${projectId ? `?projectId=${projectId}` : ''}`); } catch { return []; }
  }
  static async getMilestoneDetail(milestoneId) {
    try { return await this.fetch(`/plantrack/milestones/${milestoneId}`); } catch { return null; }
  }
  static async createMilestone(data) {
    try { return await this.post('/plantrack/milestones', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async updateMilestone(milestoneId, data) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteMilestone(milestoneId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async completeMilestone(milestoneId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}/complete`, { method: 'PUT' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async getMilestoneTemplates() {
    try { return await this.fetch('/plantrack/milestones/templates'); } catch { return []; }
  }
  static async getTemplateItems(templateId) {
    try { return await this.fetch(`/plantrack/milestones/templates/${templateId}/items`); } catch { return []; }
  }
  static async generateMilestonesFromTemplate(data) {
    try { return await this.post('/plantrack/milestones/generate', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async createMilestoneTemplate(data) {
    try { return await this.post('/plantrack/milestones/templates', data); } catch (e) { return { success: false }; }
  }
  static async updateMilestoneTemplate(id, data) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/templates/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { return { success: false }; }
  }
  static async deleteMilestoneTemplate(id) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/templates/${id}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false }; }
  }
  static async createTemplateItem(templateId, data) {
    try { return await this.post(`/plantrack/milestones/templates/${templateId}/items`, data); } catch (e) { return { success: false }; }
  }
  static async deleteTemplateItem(templateId, itemId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/templates/${templateId}/items/${itemId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false }; }
  }
  static async getExecutiveSummary(projectId = null) {
    try { return await this.fetch(`/plantrack/milestones/executive-summary${projectId ? `?projectId=${projectId}` : ''}`); } catch { return { summary: {}, upcomingMilestones: [], delayedMilestones: [] }; }
  }
  static async createMilestoneDependency(data) {
    try { return await this.post('/plantrack/milestones/dependencies', data); } catch (e) { return { success: false, error: e.message }; }
  }
  static async deleteMilestoneDependency(predecessorId, successorId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/dependencies/${predecessorId}/${successorId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async linkTaskToMilestone(milestoneId, taskId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}/tasks/${taskId}`, { method: 'POST' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async unlinkTaskFromMilestone(milestoneId, taskId) {
    try { const r = await fetch(`${API_BASE}/plantrack/milestones/${milestoneId}/tasks/${taskId}`, { method: 'DELETE' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }
  static async calculateCriticalPath(projectId) {
    try { const r = await fetch(`${API_BASE}/plantrack/projects/${projectId}/critical-path`, { method: 'POST' }); return r.json(); } catch (e) { return { success: false, error: e.message }; }
  }

  static async updateQaProject(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/projects/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateQaProject failed:', e); return { success: false, error: e.message }; }
  }

  static async createRequirement(data) {
    try { return await this.post('/quality/requirements', data); } catch (e) { console.error('createRequirement failed:', e); return { success: false, error: e.message }; }
  }

  static async updateRequirement(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/requirements/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateRequirement failed:', e); return { success: false, error: e.message }; }
  }

  static async createTestCase(data) {
    try { return await this.post('/quality/testcases', data); } catch (e) { console.error('createTestCase failed:', e); return { success: false, error: e.message }; }
  }

  static async updateTestCase(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/testcases/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateTestCase failed:', e); return { success: false, error: e.message }; }
  }

  static async createBug(data) {
    try { return await this.post('/quality/bugs', data); } catch (e) { console.error('createBug failed:', e); return { success: false, error: e.message }; }
  }

  static async getBug(id) {
    try { return await this.fetch(`/quality/bugs/${id}`); } catch { return null; }
  }

  static async updateBug(id, data) {
    try { const r = await fetch(`${API_BASE}/quality/bugs/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); return r.json(); } catch (e) { console.error('updateBug failed:', e); return { success: false, error: e.message }; }
  }

  // ---- QuickTrack Model Methods ----

  static async getQuickTrackData() {
    try { return await this.fetch('/quicktrack'); }
    catch {
      return {
        tasks: [
          { id: 1, title: 'Sample task', priority: 'medium', status: 'open', category: 'general', assignedTo: 'John Developer', createdBy: 'Jim Manager', dueDate: null, createdAt: new Date().toISOString(), completedAt: null }
        ],
        metrics: { total: 1, open: 1, inProgress: 0, done: 0, overdue: 0 }
      };
    }
  }

  static async createQuickTask(data) {
    try { return await this.post('/quicktrack/tasks', data); }
    catch { return { success: true }; }
  }

  static async updateQuickTask(taskId, data) {
    try {
      const response = await fetch(`${API_BASE}/quicktrack/tasks/${taskId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteQuickTask(taskId) {
    try {
      const response = await fetch(`${API_BASE}/quicktrack/tasks/${taskId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- Vendor Events Model Methods ----

  static async getVendorEventsData(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.vendor) params.set('vendor', filters.vendor);
      if (filters.workflow) params.set('workflow', filters.workflow);
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      return await this.fetch(`/vendorevents${qs ? '?' + qs : ''}`);
    } catch {
      return { vendors: [], events: [], metrics: { totalFired: 0, totalAccepted: 0, acceptanceRate: 0 } };
    }
  }

  static async createVendor(data) {
    try { return await this.post('/vendorevents/vendors', data); }
    catch { return { success: true }; }
  }

  static async createVendorEvent(data) {
    try { return await this.post('/vendorevents/events', data); }
    catch { return { success: true }; }
  }

  static async updateVendorEvent(eventId, data) {
    try {
      const response = await fetch(`${API_BASE}/vendorevents/events/${eventId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteVendorEvent(eventId) {
    try {
      const response = await fetch(`${API_BASE}/vendorevents/events/${eventId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async receiveVendorWebhook(vendorId, payload) {
    try { return await this.post(`/vendorevents/vendors/${vendorId}/webhook`, payload); }
    catch (e) { return { success: false, error: e.message }; }
  }

  static async updateVendor(vendorId, data) {
    try {
      const r = await fetch(`${API_BASE}/vendorevents/vendors/${vendorId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  static async deleteVendor(vendorId) {
    try {
      const r = await fetch(`${API_BASE}/vendorevents/vendors/${vendorId}`, { method: 'DELETE' });
      return r.json();
    } catch (e) { return { success: false, error: e.message }; }
  }

  // ---- LearnTrack Model Methods ----

  static async getLearnTrackData() {
    try {
      return await this.fetch('/learntrack');
    } catch {
      return {
        skills: [
          { id: 1, skillName: 'JavaScript', category: 'technical' },
          { id: 2, skillName: 'React', category: 'technical' },
          { id: 3, skillName: 'Python', category: 'technical' }
        ],
        courses: [
          { id: 1, courseName: 'React Fundamentals', difficulty: 'beginner', durationHours: 8, instructor: 'Sarah Frontend', targetGroup: 'developer', status: 'published', badgeIcon: '⚛️', skillName: 'React', enrollmentCount: 2, completedCount: 2 },
          { id: 2, courseName: 'Advanced JavaScript', difficulty: 'advanced', durationHours: 12, instructor: 'Mike Fullstack', targetGroup: 'developer', status: 'published', badgeIcon: '🟨', skillName: 'JavaScript', enrollmentCount: 1, completedCount: 0 }
        ],
        myEnrollments: [
          { id: 1, status: 'completed', progress: 100, courseName: 'React Fundamentals', badgeIcon: '⚛️', difficulty: 'beginner', skillName: 'React', score: 92 },
          { id: 2, status: 'in_progress', progress: 65, courseName: 'Advanced JavaScript', badgeIcon: '🟨', difficulty: 'advanced', skillName: 'JavaScript', score: null }
        ],
        myBadges: [{ id: 1, badgeName: 'React Starter', badgeIcon: '⚛️', description: 'Completed React Fundamentals', awardedAt: '2025-02-01' }],
        teamProgress: [
          { userId: 2, userName: 'John Developer', totalEnrollments: 3, completedCourses: 1, inProgressCourses: 1, badgeCount: 1 },
          { userId: 3, userName: 'Sarah Frontend', totalEnrollments: 2, completedCourses: 1, inProgressCourses: 1, badgeCount: 1 }
        ]
      };
    }
  }

  static async getCourseDetail(courseId) {
    try { return await this.fetch(`/learntrack/courses/${courseId}`); }
    catch { return null; }
  }

  static async createCourse(data) {
    try { return await this.post('/learntrack/courses', data); }
    catch { return { success: true }; }
  }

  static async updateCourse(courseId, data) {
    try {
      const response = await fetch(`${API_BASE}/learntrack/courses/${courseId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteCourse(courseId) {
    try {
      const response = await fetch(`${API_BASE}/learntrack/courses/${courseId}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async enrollUser(data) {
    try { return await this.post('/learntrack/enroll', data); }
    catch { return { success: true }; }
  }

  static async updateEnrollment(enrollmentId, data) {
    try {
      const response = await fetch(`${API_BASE}/learntrack/enrollments/${enrollmentId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- TimeBoard Model Methods ----

  static async getTimeBoardData(weekStart) {
    try {
      const params = weekStart ? `?week=${weekStart}` : '';
      return await this.fetch(`/timeboard${params}`);
    } catch {
      const ws = weekStart || '2025-01-27';
      return {
        weekStart: ws,
        myTimesheet: { id: 4, userId: 2, userName: 'John Developer', weekStartDate: ws, status: 'draft', totalHours: 24, billableHours: 20, leaveHours: 0 },
        entries: [
          { id: 19, entryDate: `${ws}`, hours: 8, category: 'project', projectId: 'PROJ-001', description: 'Settlement API implementation', isBillable: 1 },
          { id: 20, entryDate: '2025-01-28', hours: 8, category: 'project', projectId: 'PROJ-001', description: 'REST endpoint development', isBillable: 1 },
          { id: 21, entryDate: '2025-01-29', hours: 6, category: 'project', projectId: 'PROJ-002', description: 'Worklist bulk actions', isBillable: 1 },
          { id: 22, entryDate: '2025-01-29', hours: 2, category: 'meeting', projectId: null, description: 'Sprint retrospective', isBillable: 0 }
        ],
        teamTimesheets: [
          { id: 4, userId: 2, userName: 'John Developer', weekStartDate: ws, status: 'draft', totalHours: 24, billableHours: 20, leaveHours: 0 },
          { id: 5, userId: 3, userName: 'Sarah Frontend', weekStartDate: ws, status: 'draft', totalHours: 16, billableHours: 12, leaveHours: 0 },
          { id: 6, userId: 4, userName: 'Mike Fullstack', weekStartDate: ws, status: 'draft', totalHours: 20, billableHours: 16, leaveHours: 8 },
          { id: 8, userId: 1, userName: 'Jim Manager', weekStartDate: ws, status: 'draft', totalHours: 32, billableHours: 8, leaveHours: 0 }
        ],
        leaveDays: [
          { userId: 4, userName: 'Mike Fullstack', leaveType: 'sick', startDate: '2025-01-28', endDate: '2025-01-29' }
        ]
      };
    }
  }

  static async createTimesheetEntry(data) {
    try { return await this.post('/timeboard/entries', data); }
    catch { return { success: true }; }
  }

  static async updateTimesheetEntry(id, data) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/entries/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  static async deleteTimesheetEntry(id) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/entries/${id}`, { method: 'DELETE' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async submitTimesheet(id) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/timesheet/${id}/submit`, { method: 'PUT' });
      return response.json();
    } catch { return { success: true }; }
  }

  static async approveTimesheet(id) {
    try {
      const response = await fetch(`${API_BASE}/timeboard/timesheet/${id}/approve`, { method: 'PUT' });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- LeaveBoard Model Methods ----

  static async getLeaveBoardData() {
    try { return await this.fetch('/leaveboard'); }
    catch {
      return {
        myBalance: { vacation_total: 15, vacation_used: 5, vacation_remaining: 10, sick_total: 10, sick_used: 2, sick_remaining: 8, personal_total: 5, personal_used: 1, personal_remaining: 4 },
        requests: [
          { id: 1, userId: 2, userName: 'John Developer', leaveType: 'vacation', startDate: '2025-01-06', endDate: '2025-01-10', totalDays: 5, status: 'approved', reason: 'Family vacation', approvedBy: 'Jim Manager', createdAt: '2024-12-15' },
          { id: 4, userId: 2, userName: 'John Developer', leaveType: 'personal', startDate: '2025-02-14', endDate: '2025-02-14', totalDays: 1, status: 'approved', reason: 'Personal appointment', approvedBy: 'Jim Manager', createdAt: '2025-02-10' },
          { id: 5, userId: 3, userName: 'Sarah Frontend', leaveType: 'vacation', startDate: '2025-02-24', endDate: '2025-02-28', totalDays: 5, status: 'pending', reason: 'Spring break vacation', createdAt: '2025-02-01' },
          { id: 3, userId: 4, userName: 'Mike Fullstack', leaveType: 'vacation', startDate: '2025-02-03', endDate: '2025-02-05', totalDays: 3, status: 'approved', reason: 'Personal trip', approvedBy: 'Jim Manager', createdAt: '2025-01-18' },
          { id: 6, userId: 4, userName: 'Mike Fullstack', leaveType: 'sick', startDate: '2025-01-28', endDate: '2025-01-29', totalDays: 2, status: 'approved', reason: 'Flu', approvedBy: 'Jim Manager', createdAt: '2025-01-28' },
          { id: 7, userId: 1, userName: 'Jim Manager', leaveType: 'vacation', startDate: '2025-03-10', endDate: '2025-03-14', totalDays: 5, status: 'pending', reason: 'Annual leave', createdAt: '2025-02-01' }
        ],
        teamBalances: [
          { userName: 'Jim Manager', vacationRemaining: 17, sickRemaining: 9, personalRemaining: 5 },
          { userName: 'John Developer', vacationRemaining: 10, sickRemaining: 8, personalRemaining: 4 },
          { userName: 'Mike Fullstack', vacationRemaining: 11, sickRemaining: 7, personalRemaining: 5 },
          { userName: 'Sarah Frontend', vacationRemaining: 13, sickRemaining: 10, personalRemaining: 3 }
        ],
        upcomingLeave: [
          { userId: 4, userName: 'Mike Fullstack', leaveType: 'sick', startDate: '2025-01-28', endDate: '2025-01-29', totalDays: 2 },
          { userId: 4, userName: 'Mike Fullstack', leaveType: 'vacation', startDate: '2025-02-03', endDate: '2025-02-05', totalDays: 3 },
          { userId: 2, userName: 'John Developer', leaveType: 'personal', startDate: '2025-02-14', endDate: '2025-02-14', totalDays: 1 }
        ]
      };
    }
  }

  static async createLeaveRequest(data) {
    try { return await this.post('/leaveboard/requests', data); }
    catch { return { success: true }; }
  }

  static async updateLeaveRequest(id, data) {
    try {
      const response = await fetch(`${API_BASE}/leaveboard/requests/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    } catch { return { success: true }; }
  }

  // ---- Audit Trail Model Methods ----

  static async getAuditLogs(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.module) params.set('module', filters.module);
      if (filters.action) params.set('action', filters.action);
      if (filters.entityType) params.set('entityType', filters.entityType);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.search) params.set('search', filters.search);
      if (filters.offset) params.set('offset', filters.offset);
      params.set('limit', filters.limit || 50);
      const qs = params.toString();
      return await this.fetch(`/audit?${qs}`);
    } catch { return { logs: [], hasMore: false, offset: 0 }; }
  }

  static async getAuditStats() {
    try { return await this.fetch('/audit/stats'); }
    catch { return { byModule: [], byAction: [], total: 0, uniqueUsers: 0, today: 0, lastWeek: 0 }; }
  }
  static async getReportsData() {
    try { return await this.fetch('/reports'); }
    catch { return {}; }
  }
  static async getTaskManageData(filters = {}) {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    try { return await this.fetch(`/tasks/manage?${p}`); }
    catch { return { tasks: [], summary: {}, assignees: [], projects: [] }; }
  }
  static async bulkUpdateTasks(tasks, updates) {
    try {
      const res = await fetch(`${API_BASE}/tasks/manage/bulk`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('orbit_token')}` },
        body: JSON.stringify({ tasks, updates }),
      });
      return await res.json();
    } catch { return { success: false }; }
  }

  static async getTeamMembers() {
    try {
      const teams = await this.fetch('/teams');
      return (teams || []).flatMap(team =>
        (team.members || []).map(m => ({ ...m, team_module: team.module, team_name: team.team_name }))
      );
    } catch { return []; }
  }
}

// ============================================================================
// VIEWS (Presentation Components)
// ============================================================================

const Header = ({ user, onMenuClick, onLogout, onChangePassword }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const initials = (user?.name || 'U').charAt(0).toUpperCase();

  return (
    <div className="bg-blue-600 text-white shadow-md relative z-40">
      <div className="max-w-7xl mx-auto px-4 py-3.5 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button onClick={onMenuClick} className="lg:hidden p-1">
            <Menu size={22} />
          </button>
          <div className="text-xl font-bold tracking-wide">🛰️ ORBIT</div>
        </div>

        <div className="relative">
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="flex items-center gap-2.5 hover:bg-blue-500 rounded-lg px-2 py-1.5 transition-colors"
          >
            <span className="hidden sm:inline text-sm opacity-90">{user?.name}</span>
            {user?.avatar ? (
              <img src={user.avatar} alt={user?.name} className="w-8 h-8 rounded-full object-cover border-2 border-blue-300" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center font-bold text-sm border-2 border-blue-300">
                {initials}
              </div>
            )}
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 min-w-[200px] overflow-hidden z-50">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <p className="font-semibold text-gray-800 text-sm">{user?.name}</p>
                  <p className="text-xs text-gray-500 capitalize mt-0.5">{user?.role || 'User'}</p>
                  {user?.email && <p className="text-xs text-gray-400 mt-0.5 truncate">{user.email}</p>}
                </div>
                <div className="py-1">
                  <button
                    onClick={() => { setMenuOpen(false); onChangePassword?.(); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5"
                  >
                    <Shield size={14} className="text-gray-400" /> Change Password
                  </button>
                  <div className="my-1 border-t border-gray-100" />
                  <button
                    onClick={() => { setMenuOpen(false); onLogout?.(); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5"
                  >
                    <X size={14} /> Sign Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Navigation = ({ currentPage, onNavigate, isOpen, onClose, user }) => {
  const allNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'modules', label: 'Modules', icon: Camera },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'audit', label: 'Audit', icon: Shield },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];
  const hiddenForRole = ROLE_HIDDEN_NAV[user?.role] || [];
  const navItems = allNavItems.filter(item => !hiddenForRole.includes(item.id));

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onClose} />
      )}
      
      {/* Navigation */}
      <nav className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 lg:w-auto
        bg-white border-b border-gray-200 shadow-sm
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="lg:hidden flex justify-between items-center p-4 border-b">
          <span className="font-semibold">Menu</span>
          <button onClick={onClose}><X size={24} /></button>
        </div>
        <div className="lg:max-w-7xl lg:mx-auto lg:px-4 lg:flex lg:gap-0">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  onNavigate(item.id);
                  onClose();
                }}
                className={`
                  w-full lg:w-auto flex items-center gap-3 px-6 py-4 lg:border-b-3
                  transition-colors
                  ${currentPage === item.id 
                    ? 'text-blue-600 bg-gray-50 lg:border-blue-600' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600 lg:border-transparent'
                  }
                `}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

const BurnChart = ({ title, subtitle, days, burndown, burnup, ideal, total, unit = 'pts' }) => {
  if (!days || days.length === 0) return (
    <div className="flex items-center justify-center h-40 text-gray-400 text-sm">No data available</div>
  );
  const W = 560, H = 200, padL = 42, padR = 12, padT = 14, padB = 32;
  const cW = W - padL - padR, cH = H - padT - padB;
  const n = days.length;
  const maxVal = Math.max(total || 0, ...(burnup || []), ...(burndown || []), 1);
  const x = i => padL + (i / Math.max(n - 1, 1)) * cW;
  const y = v => padT + cH - (Math.min(v, maxVal) / maxVal) * cH;
  const pts = arr => arr.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const yTicks = [0, Math.round(maxVal / 2), maxVal];
  const xIdx = n <= 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1];
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 200 }}>
        {/* Grid */}
        {yTicks.map(v => (
          <line key={v} x1={padL} y1={y(v).toFixed(1)} x2={W - padR} y2={y(v).toFixed(1)} stroke="#f3f4f6" strokeWidth="1" />
        ))}
        {/* Scope line */}
        {total > 0 && <line x1={padL} y1={y(total).toFixed(1)} x2={W - padR} y2={y(total).toFixed(1)} stroke="#818cf8" strokeWidth="1.5" strokeDasharray="5,3" />}
        {/* Ideal burndown */}
        {ideal && ideal.length > 1 && <polyline points={pts(ideal)} fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="4,3" />}
        {/* Burnup */}
        {burnup && burnup.length > 1 && <polyline points={pts(burnup)} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />}
        {/* Burndown */}
        {burndown && burndown.length > 1 && <polyline points={pts(burndown)} fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />}
        {/* Dots - burnup */}
        {burnup && burnup.map((v, i) => <circle key={i} cx={x(i).toFixed(1)} cy={y(v).toFixed(1)} r="2.5" fill="#10b981" />)}
        {/* Dots - burndown */}
        {burndown && burndown.map((v, i) => <circle key={i} cx={x(i).toFixed(1)} cy={y(v).toFixed(1)} r="2.5" fill="#ef4444" />)}
        {/* Axes */}
        <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="#d1d5db" strokeWidth="1" />
        <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#d1d5db" strokeWidth="1" />
        {/* Y labels */}
        {yTicks.map(v => (
          <text key={v} x={padL - 5} y={y(v) + 4} textAnchor="end" fontSize="10" fill="#6b7280">{v}{unit}</text>
        ))}
        {/* X labels */}
        {xIdx.map(i => (
          <text key={i} x={x(i)} y={H - padB + 14} textAnchor="middle" fontSize="10" fill="#6b7280">{days[i]}</text>
        ))}
      </svg>
      <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
        {ideal && <span className="flex items-center gap-1"><span className="inline-block w-5 border-t-2 border-dashed border-gray-400"></span>Ideal</span>}
        <span className="flex items-center gap-1"><span className="inline-block w-5 border-t-2 border-red-500"></span>Burndown</span>
        <span className="flex items-center gap-1"><span className="inline-block w-5 border-t-2 border-green-500"></span>Burnup</span>
        {total > 0 && <span className="flex items-center gap-1"><span className="inline-block w-5 border-t-2 border-dashed border-indigo-400"></span>Scope ({total}{unit})</span>}
      </div>
    </div>
  );
};

const DashboardView = ({ data, burnCharts, onNavigate, user }) => {
  const allowedModules = getAllowedModules(user?.role);
  const visibleQuickModules = allowedModules
    ? data.quickModules.filter(m => allowedModules.includes(m.id))
    : data.quickModules;
  return (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
      <p className="text-gray-600 mt-1">Welcome back! Here's what's happening today.</p>
    </div>

    {/* Stats Grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {data.stats.map((stat, i) => (
        <div key={i} className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-gray-600 text-sm mb-2">{stat.label}</div>
              <div className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
              <div className="text-green-600 text-sm mt-1">{stat.change} from last week</div>
            </div>
            <div className="w-16 h-16 rounded-xl" style={{ background: `${stat.color}20` }} />
          </div>
        </div>
      ))}
    </div>

    {/* Burn Charts */}
    {burnCharts && (burnCharts.devops || burnCharts.dataops) && (
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Burn Charts</h2>
        <div className="grid lg:grid-cols-2 gap-6">
          {burnCharts.devops ? (
            <BurnChart
              title={`DevOps Burndown / Burnup — ${burnCharts.devops.sprintName}`}
              subtitle={`Sprint: ${burnCharts.devops.startDate} → ${burnCharts.devops.endDate}`}
              days={burnCharts.devops.days}
              burndown={burnCharts.devops.burndown}
              burnup={burnCharts.devops.burnup}
              ideal={burnCharts.devops.ideal}
              total={burnCharts.devops.total}
              unit={burnCharts.devops.unit}
            />
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6 flex items-center justify-center text-gray-400 text-sm">
              No active sprint — DevOps burn chart unavailable
            </div>
          )}
          {burnCharts.dataops ? (
            <BurnChart
              title="DataOps Pipeline Burndown / Burnup — Last 14 Days"
              subtitle="Cumulative pipeline completions vs total scope"
              days={burnCharts.dataops.days}
              burndown={burnCharts.dataops.burndown}
              burnup={burnCharts.dataops.burnup}
              total={burnCharts.dataops.total}
              unit=" runs"
            />
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6 flex items-center justify-center text-gray-400 text-sm">
              No pipeline data — DataOps burn chart unavailable
            </div>
          )}
        </div>
      </div>
    )}

    <div className="grid lg:grid-cols-3 gap-6">
      {/* Recent Activity */}
      <div className="lg:col-span-2 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Recent Activity</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Action</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Item</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">User</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.activities.map((activity, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                      {activity.action}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-900">{activity.item}</td>
                  <td className="px-6 py-4 text-gray-600">{activity.user}</td>
                  <td className="px-6 py-4 text-gray-500 text-sm">{activity.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Access */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Quick Access</h2>
        </div>
        <div className="p-4 space-y-2">
          {visibleQuickModules.map((module, i) => (
            <div key={i} onClick={() => onNavigate(module.id)} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all cursor-pointer">
              <div className="text-3xl">{module.icon}</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{module.name}</div>
                <div className="text-sm text-gray-600">{module.count}</div>
              </div>
              <ChevronRight className="text-gray-400" size={20} />
            </div>
          ))}
        </div>
      </div>
    </div>

  </div>
  );
};

const ModulesView = ({ modules, onNavigate, user }) => {
  const allowedModules = getAllowedModules(user?.role);
  const visibleModules = allowedModules
    ? modules.filter(m => allowedModules.includes(m.id))
    : modules;
  return (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900">ORBIT Modules</h1>
      <p className="text-gray-600 mt-1">Core API modules and shared services for the ORBIT tracking system.</p>
    </div>

    <div className="mb-4">
      <h2 className="text-xl font-semibold text-gray-900 pb-3 border-b-2 border-gray-200">
        Core API Modules
      </h2>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {visibleModules.map(module => (
        <div key={module.id} onClick={() => onNavigate(module.id)} className="bg-white rounded-lg shadow border border-gray-200 p-6 hover:shadow-xl hover:border-blue-500 transition-all cursor-pointer">
          <div className="flex items-center justify-center w-14 h-14 rounded-xl text-2xl mb-4" 
               style={{ background: `${module.color}20`, color: module.color }}>
            {module.icon}
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{module.name}</h3>
          <p className="text-gray-600 text-sm mb-4 leading-relaxed">{module.description}</p>
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              {module.status}
            </span>
            <span className="text-gray-600 text-sm">{module.records.toLocaleString()} records</span>
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};

// ---- DevOps Badge Helpers ----
const getTypeBadge = (type) => {
  const normalized = (type || '').toUpperCase();
  const badges = {
    TASK:    { color: '#0366d6', bg: '#d4e4ff',  label: 'Task' },
    PROJECT: { color: '#6f42c1', bg: '#f3e8ff',  label: 'Project' },
    BUG:     { color: '#dc3545', bg: '#ffe5e9',  label: 'Bug' },
    ADHOC:   { color: '#e36209', bg: '#fff5cc',  label: 'Ad-hoc' },
    STORY:   { color: '#17a2b8', bg: '#d4f4ff',  label: 'Story' },
    FEATURE: { color: '#28a745', bg: '#dcffe4',  label: 'Feature' },
  };
  return badges[normalized] || { color: '#586069', bg: '#f6f8fa', label: type || 'Task' };
};

const getDevOpsPriorityBadge = (priority) => {
  const badges = {
    P1: { color: '#dc3545', bg: '#ffe5e9' },
    P2: { color: '#ffc107', bg: '#fff5cc' },
    P3: { color: '#28a745', bg: '#dcffe4' }
  };
  return badges[priority] || { color: '#586069', bg: '#f6f8fa' };
};

const getOpsStatusStyle = (status) => {
  const styles = {
    running: { color: '#0366d6', bg: '#d4e4ff', label: 'Running' },
    completed: { color: '#28a745', bg: '#dcffe4', label: 'Completed' },
    failed: { color: '#dc3545', bg: '#ffe5e9', label: 'Failed' },
    idle: { color: '#586069', bg: '#f6f8fa', label: 'Idle' },
    queued: { color: '#6f42c1', bg: '#f3e8ff', label: 'Queued' }
  };
  return styles[status] || { color: '#586069', bg: '#f6f8fa', label: status };
};

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

const formatRecords = (count) => {
  if (!count) return '0';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
  return count.toString();
};

// ---- DevOps Task Card ----
const DevOpsTaskCard = ({ item, currentStatus, onClick, onStatusChange }) => {
  const typeBadge = getTypeBadge(item.type);
  const priorityBadge = getDevOpsPriorityBadge(item.priority);
  const isQuickTask = !!item.isQuickTask || String(item.id).startsWith('QT-');
  const isBug = !!item.isBug || String(item.id).startsWith('BUG-');

  const allStatuses = isQuickTask
    ? ['backlog', 'in_progress', 'done']
    : isBug
      ? ['in_progress', 'review', 'done']
      : ['backlog', 'ready', 'in_progress', 'review', 'done'];

  // Exclude the column this card is already sitting in
  const moveOptions = allStatuses.filter(s => s !== currentStatus);

  const statusLabels = { backlog: 'Backlog', ready: 'Ready', in_progress: 'In Progress', review: 'Review', done: 'Done' };
  const cardBg = isBug ? 'bg-red-50 border-red-200' : isQuickTask ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200';
  const barColor = isBug ? 'bg-red-500' : isQuickTask ? 'bg-amber-500' : 'bg-blue-600';

  return (
    <div className={`border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer ${cardBg}`} onClick={() => onClick(item.id)}>
      <div className="flex items-start justify-between gap-1 mb-2">
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
          <span className="text-xs font-mono text-gray-400">{item.bugCode || item.id}</span>
          <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: typeBadge.bg, color: typeBadge.color }}>{typeBadge.label}</span>
          {isBug && item.severity && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">{item.severity}</span>}
          {isQuickTask && item.category && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">{item.category}</span>}
        </div>
        <span className="px-2 py-0.5 rounded text-xs font-semibold shrink-0" style={{ background: priorityBadge.bg, color: priorityBadge.color }}>{item.priority}</span>
      </div>
      <div className="text-sm font-medium text-gray-900 mb-2 leading-snug">{item.title}</div>
      {item.projectName && <div className="text-xs text-blue-600 font-medium mb-1 truncate">{item.projectName}</div>}
      {item.assignee && <div className="text-xs text-gray-500 mb-1">👤 {item.assignee}</div>}
      {!isQuickTask && !isBug && (item.estimate > 0 || item.storyPoints > 0) && (
        <div className="flex gap-3 text-xs text-gray-500 mb-1">
          {item.estimate > 0 && <span>⏱ {item.estimate}h</span>}
          {item.storyPoints > 0 && <span>◆ {item.storyPoints} pts</span>}
        </div>
      )}
      {item.progress > 0 && (
        <div className="mt-1.5 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${item.progress}%` }} />
        </div>
      )}
      {onStatusChange && moveOptions.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <select
            value=""
            onClick={e => e.stopPropagation()}
            onChange={e => { e.stopPropagation(); if (e.target.value) onStatusChange(item.id, e.target.value); }}
            className="w-full text-xs border border-gray-200 rounded px-2 py-1 text-gray-500 bg-white"
          >
            <option value="">Move to…</option>
            {moveOptions.map(s => <option key={s} value={s}>{statusLabels[s] || s}</option>)}
          </select>
        </div>
      )}
    </div>
  );
};

// ---- DevOps Capacity Cards ----
const DevOpsCapacityCards = ({ capacity }) => (
  <div className="mb-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="text-sm text-gray-600 mb-2">Total Capacity</div>
        <div className="text-4xl font-bold text-blue-700">{capacity.total}h</div>
      </div>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="text-sm text-gray-600 mb-2">Committed</div>
        <div className="text-4xl font-bold text-yellow-700">{capacity.committed}h</div>
        <div className="text-sm text-gray-600 mt-1">{capacity.utilization}% utilized</div>
      </div>
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="text-sm text-gray-600 mb-2">Completed</div>
        <div className="text-4xl font-bold text-green-700">{capacity.completed}h</div>
      </div>
    </div>
  </div>
);

// ---- DevOps Team Workload ----
const DevOpsTeamWorkload = ({ workload }) => (
  <div className="bg-white rounded-lg shadow p-6 mb-6">
    <h3 className="text-lg font-semibold mb-4">Team Workload</h3>
    <div className="space-y-4">
      {workload.map((member, i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="w-36 text-sm text-gray-700">{member.name}</div>
          <div className="flex-1 h-8 bg-gray-200 rounded-lg overflow-hidden">
            <div
              className={`h-full flex items-center justify-center text-white text-sm font-semibold ${member.percentage > 100 ? 'bg-red-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min(member.percentage, 100)}%` }}
            >
              {member.percentage}%
            </div>
          </div>
          <div className="w-24 text-sm text-gray-600 text-right">{member.assigned}h / {member.capacity}h</div>
        </div>
      ))}
    </div>
  </div>
);

// ---- DevOps Pipeline Board (shared for Sprint & Kanban) ----
const DevOpsPipelineBoard = ({ pipeline, title, onTaskClick, onStatusChange }) => {
  const columnLabels = { backlog: 'Backlog', ready: 'Ready', in_progress: 'In Progress', review: 'Review', done: 'Done' };

  // Normalize all keys to snake_case and merge done/completed
  const normalizedPipeline = {};
  Object.entries(pipeline).forEach(([key, items]) => {
    let normalized = key;
    if (key === 'inProgress') normalized = 'in_progress';
    if (key === 'completed')  normalized = 'done';
    if (!normalizedPipeline[normalized]) normalizedPipeline[normalized] = [];
    normalizedPipeline[normalized] = [...normalizedPipeline[normalized], ...items];
  });

  const columnOrder = ['backlog', 'ready', 'in_progress', 'review', 'done'];
  const uniqueColumns = columnOrder.filter(k => normalizedPipeline[k] !== undefined);

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {uniqueColumns.map(status => {
          const items = normalizedPipeline[status] || [];
          const totalPoints = items.reduce((sum, item) => sum + (item.storyPoints || 0), 0);
          return (
            <div key={status} className="bg-white rounded-lg shadow border border-gray-200 min-h-96">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="font-semibold text-gray-900">{columnLabels[status] || status}</div>
                <div className="text-sm text-gray-600">{items.length} items · {totalPoints} pts</div>
              </div>
              <div className="p-3 space-y-3">
                {items.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <div className="text-3xl mb-2">🔭</div>
                    <div className="text-sm">No items</div>
                  </div>
                ) : (
                  items.map((item, i) => (
                    <DevOpsTaskCard key={item.id || i} item={item} currentStatus={status} onClick={onTaskClick} onStatusChange={onStatusChange} />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---- DevOps Ops Group (CI/CD Pipelines) ----
const DevOpsOpsGroup = ({ pipelines }) => (
  <div className="mb-6">
    <h3 className="text-lg font-semibold mb-4">Ops Pipeline</h3>
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Pipeline</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Progress</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Records</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Duration</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {pipelines.map(p => {
            const statusStyle = getOpsStatusStyle(p.status);
            return (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{p.name}</div>
                  <div className="text-xs text-gray-500">{p.id}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-semibold">{p.type}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: statusStyle.bg, color: statusStyle.color }}>{statusStyle.label}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${p.status === 'failed' ? 'bg-red-500' : 'bg-blue-600'}`} style={{ width: `${p.progress}%` }} />
                    </div>
                    <span className="text-xs text-gray-500">{p.progress}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{formatRecords(p.recordsProcessed)}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{formatDuration(p.durationSeconds)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {pipelines.length === 0 && (
        <div className="p-8 text-center text-gray-400">No pipelines found.</div>
      )}
    </div>
  </div>
);

// ---- Add DevOps Task Modal ----
const AddDevOpsTaskModal = ({ onClose, onSubmit, teamMembers = [] }) => {
  const [form, setForm] = useState({ title: '', type: 'task', priority: 'P2', assignee: '', estimatedMinutes: '', actualMinutes: '', storyPoints: '', status: 'backlog', description: '' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="task">Task</option>
                <option value="BUG">Bug</option>
                <option value="ADHOC">Ad-hoc</option>
                <option value="PROJECT">Project</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="P1">P1 - Critical</option>
                <option value="P2">P2 - High</option>
                <option value="P3">P3 - Normal</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
              <select value={form.assignee} onChange={e => setForm({...form, assignee: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">Unassigned</option>
                {teamMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="backlog">Backlog</option>
                <option value="ready">Ready</option>
                <option value="in_progress">In Progress</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Est. Time (min)</label>
              <input type="number" min="0" placeholder="0" value={form.estimatedMinutes} onChange={e => setForm({...form, estimatedMinutes: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Actual Time (min)</label>
              <input type="number" min="0" placeholder="0" value={form.actualMinutes} onChange={e => setForm({...form, actualMinutes: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Story Points</label>
              <input type="number" min="0" value={form.storyPoints} onChange={e => setForm({...form, storyPoints: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Create Task</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Task Detail Panel ----
// ---- Shared task comments component (used by both DevOps and QuickTrack) ----
const TaskComments = ({ taskRef, taskSource, userName }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const apiBase = taskSource === 'devops'
    ? `/api/devops/tasks/${taskRef}`
    : taskSource === 'bug'
      ? `/api/quality/bugs/${taskRef}`
      : `/api/quicktrack/tasks/${taskRef}`;

  const load = async () => {
    try {
      const res = await fetch(`${apiBase}/comments`);
      const data = await res.json();
      setComments(Array.isArray(data) ? data : []);
    } catch { setComments([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [taskRef]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await fetch(`${apiBase}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: text.trim(), userName: userName || 'User' })
      });
      setText('');
      load();
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  const handleDelete = async (commentId) => {
    const deleteUrl = taskSource === 'devops'
      ? `/api/devops/tasks/comments/${commentId}`
      : taskSource === 'bug'
        ? `/api/quality/bugs/comments/${commentId}`
        : `/api/quicktrack/tasks/comments/${commentId}`;
    try {
      await fetch(deleteUrl, { method: 'DELETE' });
      setComments(c => c.filter(x => x.id !== commentId));
    } catch { /* ignore */ }
  };

  const fmtTime = (ts) => {
    const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <span>💬</span> Comments {comments.length > 0 && <span className="text-xs font-normal text-gray-400">({comments.length})</span>}
      </h4>
      {loading ? (
        <div className="text-xs text-gray-400 py-2">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="text-xs text-gray-400 italic mb-3">No comments yet. Be the first to comment.</div>
      ) : (
        <div className="space-y-3 mb-4">
          {comments.map(c => (
            <div key={c.id} className="flex gap-2.5">
              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 flex-shrink-0 mt-0.5">
                {(c.user_name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-semibold text-gray-800">{c.user_name}</span>
                  <span className="text-[10px] text-gray-400 flex-shrink-0">{fmtTime(c.created_at)}</span>
                </div>
                <p className="text-sm text-gray-700 mt-0.5 leading-relaxed">{c.content}</p>
              </div>
              <button onClick={() => handleDelete(c.id)} className="text-gray-300 hover:text-red-400 flex-shrink-0 mt-0.5" title="Delete comment">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text" value={text} onChange={e => setText(e.target.value)}
          placeholder="Write a comment…"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
        />
        <button type="submit" disabled={submitting || !text.trim()}
          className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
          Post
        </button>
      </form>
    </div>
  );
};

const TaskDetailPanel = ({ task, onClose, onUpdate, user, teamMembers = [] }) => {
  const [editStatus, setEditStatus] = useState(task.status);
  const [editAssignee, setEditAssignee] = useState(task.assignee || '');
  const [editPriority, setEditPriority] = useState(task.priority);
  const [editActualHours, setEditActualHours] = useState(task.actualHours || task.estimateHours || task.estimate || 0);
  const [editEstimatedMinutes, setEditEstimatedMinutes] = useState(task.estimatedMinutes || task.estimated_minutes || 0);
  const [editActualMinutes, setEditActualMinutes] = useState(task.actualMinutes || task.actual_minutes || 0);
  const [editDescription, setEditDescription] = useState(task.description || '');
  const [saving, setSaving] = useState(false);

  const isCompleting = (editStatus === 'done' || editStatus === 'completed') && task.status !== 'done' && task.status !== 'completed';

  const handleSave = async () => {
    setSaving(true);
    const changes = {};
    if (editStatus !== task.status) changes.status = editStatus;
    if (editAssignee !== (task.assignee || '')) changes.assignee = editAssignee;
    if (editPriority !== task.priority) changes.priority = editPriority;
    if (isCompleting && editActualHours > 0) changes.actualHours = parseFloat(editActualHours);
    if (editEstimatedMinutes !== (task.estimatedMinutes || task.estimated_minutes || 0)) changes.estimatedMinutes = parseInt(editEstimatedMinutes) || 0;
    if (editActualMinutes !== (task.actualMinutes || task.actual_minutes || 0)) changes.actualMinutes = parseInt(editActualMinutes) || 0;
    if (editDescription !== (task.description || '')) changes.description = editDescription;
    if (Object.keys(changes).length > 0) {
      await onUpdate(task.id, changes);
    }
    setSaving(false);
    onClose();
  };

  const typeBadge = getTypeBadge(task.type);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-gray-500">{task.id}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: typeBadge.bg, color: typeBadge.color }}>{task.type}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">{task.title}</h2>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
            <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)}
              rows={3} placeholder="Add a description…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select value={editStatus} onChange={e => setEditStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="backlog">Backlog</option>
                <option value="ready">Ready</option>
                <option value="in_progress">In Progress</option>
                <option value="review">Review</option>
                <option value="done">Done</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
              <select value={editPriority} onChange={e => setEditPriority(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="P1">P1 - Critical</option>
                <option value="P2">P2 - High</option>
                <option value="P3">P3 - Normal</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Assignee</label>
            <select value={editAssignee} onChange={e => setEditAssignee(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Unassigned</option>
              {teamMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
            </select>
          </div>

          {isCompleting && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <label className="block text-xs font-semibold text-blue-700 mb-1">Actual Hours Spent</label>
              <p className="text-[10px] text-blue-500 mb-2">These hours will be automatically logged to your TimeBoard.</p>
              <input type="number" min="0" step="0.5" value={editActualHours}
                onChange={e => setEditActualHours(e.target.value)}
                className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Est. Time (min)</label>
              <input type="number" min="0" placeholder="0" value={editEstimatedMinutes}
                onChange={e => setEditEstimatedMinutes(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Actual Time (min)</label>
              <input type="number" min="0" placeholder="0" value={editActualMinutes}
                onChange={e => setEditActualMinutes(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-500">Story Points</div>
              <div className="text-lg font-semibold text-gray-900">{task.storyPoints || 0}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-500">Progress</div>
              <div className="text-lg font-semibold text-gray-900">{task.progress || 0}%</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-500">Variance</div>
              <div className={`text-lg font-semibold ${(editActualMinutes - editEstimatedMinutes) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {editEstimatedMinutes > 0 ? `${editActualMinutes - editEstimatedMinutes > 0 ? '+' : ''}${editActualMinutes - editEstimatedMinutes}m` : '-'}
              </div>
            </div>
          </div>

          <TaskComments taskRef={task.id} taskSource="devops" userName={user?.user_name || user?.name} />

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Bug Detail Panel ----
const BugDetailPanel = ({ bug, onClose, onUpdate, onDelete, readOnly = false, user }) => {
  const [form, setForm] = useState({
    title: bug.title || '',
    status: bug.status || 'open',
    severity: bug.severity || 'medium',
    environment: bug.environment || 'DEV',
    module: bug.module || '',
    assignedTo: bug.assignedTo || '',
    stepsToReproduce: bug.stepsToReproduce || '',
    expectedResult: bug.expectedResult || '',
    actualResult: bug.actualResult || '',
  });
  const [saving, setSaving] = useState(false);

  const severityColors = { critical: { bg: '#fee2e2', color: '#dc2626' }, high: { bg: '#fed7aa', color: '#ea580c' }, medium: { bg: '#fef9c3', color: '#ca8a04' }, low: { bg: '#dcfce7', color: '#16a34a' } };
  const bugStatusColors = { open: { bg: '#fee2e2', color: '#dc2626' }, in_progress: { bg: '#dbeafe', color: '#2563eb' }, resolved: { bg: '#dcfce7', color: '#16a34a' }, closed: { bg: '#f3f4f6', color: '#6b7280' } };
  const sc = severityColors[form.severity] || severityColors.medium;
  const bc = bugStatusColors[form.status] || bugStatusColors.open;

  const handleSave = async () => {
    setSaving(true);
    const changes = {};
    if (form.title !== (bug.title || '')) changes.title = form.title;
    if (form.status !== (bug.status || 'open')) changes.status = form.status;
    if (form.severity !== (bug.severity || 'medium')) changes.severity = form.severity;
    if (form.environment !== (bug.environment || '')) changes.environment = form.environment;
    if (form.module !== (bug.module || '')) changes.module = form.module;
    if (form.assignedTo !== (bug.assignedTo || '')) changes.assignedTo = form.assignedTo;
    if (form.stepsToReproduce !== (bug.stepsToReproduce || '')) changes.stepsToReproduce = form.stepsToReproduce;
    if (form.expectedResult !== (bug.expectedResult || '')) changes.expectedResult = form.expectedResult;
    if (form.actualResult !== (bug.actualResult || '')) changes.actualResult = form.actualResult;
    if (Object.keys(changes).length > 0 && onUpdate) await onUpdate(bug.id, changes);
    setSaving(false);
    onClose();
  };

  const iCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400 focus:border-teal-400';
  const rCls = 'text-sm text-gray-800 bg-gray-50 rounded-lg px-3 py-2 min-h-[36px]';

  const Field = ({ label, children }) => (
    <div><label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>{children}</div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-mono text-gray-500">{bug.bugCode}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: sc.bg, color: sc.color }}>{form.severity}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: bc.bg, color: bc.color }}>{form.status}</span>
            {readOnly && <span className="text-xs text-gray-400 italic">· view only</span>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-4"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          <Field label="Title">
            {readOnly
              ? <p className={rCls}>{form.title}</p>
              : <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className={iCls} />}
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Status">
              {readOnly
                ? <p className={rCls + ' capitalize'}>{form.status.replace('_', ' ')}</p>
                : <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className={iCls}>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>}
            </Field>
            <Field label="Severity">
              {readOnly
                ? <p className={rCls + ' capitalize'}>{form.severity}</p>
                : <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })} className={iCls}>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>}
            </Field>
            <Field label="Environment">
              {readOnly
                ? <p className={rCls}>{form.environment || '—'}</p>
                : <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })} className={iCls}>
                    <option>DEV</option><option>UAT</option><option>Staging</option><option>Production</option>
                  </select>}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Module">
              {readOnly
                ? <p className={rCls}>{form.module || '—'}</p>
                : <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })} className={iCls}>
                    <option value="">None</option>
                    <option>Authentication</option><option>Settlement Calculator</option>
                    <option>Payment Processing</option><option>Reporting</option>
                    <option>User Management</option><option>UI</option>
                  </select>}
            </Field>
            <Field label="Assigned To">
              {readOnly
                ? <p className={rCls}>{form.assignedTo || '—'}</p>
                : <input value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })} placeholder="Developer name" className={iCls} />}
            </Field>
          </div>

          <Field label="Steps to Reproduce">
            {readOnly
              ? <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap min-h-[48px]">{form.stepsToReproduce || '—'}</p>
              : <textarea value={form.stepsToReproduce} onChange={e => setForm({ ...form, stepsToReproduce: e.target.value })} rows={3} className={iCls + ' resize-none'} />}
          </Field>

          <Field label="Expected Result">
            {readOnly
              ? <p className="text-sm text-gray-700 bg-green-50 rounded-lg p-3 min-h-[40px]">{form.expectedResult || '—'}</p>
              : <textarea value={form.expectedResult} onChange={e => setForm({ ...form, expectedResult: e.target.value })} rows={2} className={iCls + ' resize-none'} />}
          </Field>

          <Field label="Actual Result">
            {readOnly
              ? <p className="text-sm text-gray-700 bg-red-50 rounded-lg p-3 min-h-[40px]">{form.actualResult || '—'}</p>
              : <textarea value={form.actualResult} onChange={e => setForm({ ...form, actualResult: e.target.value })} rows={2} className={iCls + ' resize-none'} />}
          </Field>

          <TaskComments taskRef={bug.id} taskSource="bug" userName={user?.user_name || user?.name} />

          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            {!readOnly && onDelete
              ? <button onClick={() => { if (confirm('Delete this bug?')) onDelete(bug.id); }} className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">Delete Bug</button>
              : <div />}
            <div className="flex gap-3">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Close</button>
              {!readOnly && (
                <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- DevOps Module (Main Container) ----
const EodSettingsModal = ({ onClose }) => {
  const [settings, setSettings] = React.useState({
    eod_enabled: 'false', eod_slack_channel: '#daily-summary',
    pipedream_outbound_url: '', pipedream_api_key: ''
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    ModulesModel.getAppSettings().then(s => { setSettings(s); setLoading(false); });
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    const res = await ModulesModel.saveAppSettings(settings);
    setSaving(false);
    if (res?.success) setSaved(true);
    else setError(res?.error || 'Failed to save settings');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-gray-900">⚙️ EOD Settings</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading settings…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="text-sm font-medium text-gray-800">EOD Summary Enabled</div>
                <div className="text-xs text-gray-500">Allow sending EOD reports to Slack</div>
              </div>
              <button
                onClick={() => setSettings(s => ({ ...s, eod_enabled: s.eod_enabled === 'true' ? 'false' : 'true' }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.eod_enabled === 'true' ? 'bg-blue-600' : 'bg-gray-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.eod_enabled === 'true' ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slack Channel</label>
              <input type="text" value={settings.eod_slack_channel || ''} onChange={e => setSettings(s => ({ ...s, eod_slack_channel: e.target.value }))}
                placeholder="#daily-summary"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipedream Outbound URL</label>
              <input type="text" value={settings.pipedream_outbound_url || ''} onChange={e => setSettings(s => ({ ...s, pipedream_outbound_url: e.target.value }))}
                placeholder="https://eolg9zd6imjrhao.m.pipedream.net"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipedream API Key <span className="text-gray-400 font-normal">(leave blank to keep existing)</span></label>
              <input type="password" value={settings.pipedream_api_key || ''} onChange={e => setSettings(s => ({ ...s, pipedream_api_key: e.target.value }))}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            {saved && <p className="text-green-600 text-xs font-medium">✓ Settings saved</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SendEodModal = ({ module, onClose }) => {
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const handleSend = async () => {
    setSending(true); setError('');
    const res = await ModulesModel.sendEodSummary(module);
    setSending(false);
    if (res?.success) setResult(res);
    else setError(res?.error || 'Failed to send EOD summary');
  };

  const moduleLabel = module === 'devops' ? 'DevOps' : 'DataOps';
  const moduleColor = module === 'devops' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">📤 {moduleLabel} EOD Summary</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {result ? (
          <div className="text-center py-4">
            <div className="text-4xl mb-3">✅</div>
            <p className="font-medium text-gray-800 mb-3">EOD summary sent to Slack!</p>
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-left space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Members included</span><span className="font-medium">{result.usersIncluded}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Completed today</span><span className="font-medium text-green-600">{result.totals?.total_completed}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">In progress</span><span className="font-medium text-blue-600">{result.totals?.total_in_progress}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Pending</span><span className="font-medium text-gray-600">{result.totals?.total_pending}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Slack channel</span><span className="font-medium">{result.slackChannel}</span></div>
            </div>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-5">
              This will send an End of Day summary for all <strong>{moduleLabel}</strong> team members to Slack via Pipedream,
              showing tasks completed today, in progress, and pending.
            </p>
            {error && <p className="text-red-500 text-sm mb-3 bg-red-50 p-3 rounded-lg">{error}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSend} disabled={sending} className={`px-4 py-2 text-white text-sm rounded-lg disabled:opacity-50 ${moduleColor}`}>
                {sending ? 'Sending…' : 'Send EOD Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DevOpsModule = ({ data, viewMode, group, onViewModeChange, onGroupChange, onAddTask, onTaskClick, onStatusChange, onSendEod, onEodSettings }) => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">DevOps Pipeline</h1>
          <p className="text-gray-600 mt-1">Developer pipeline & operations tracking</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onEodSettings} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">⚙️ EOD Settings</button>
          <button onClick={onSendEod} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">📤 Send EOD</button>
          <button onClick={onAddTask} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Task</button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex gap-2 mb-4">
        {['sprint', 'kanban'].map(mode => (
          <button key={mode} onClick={() => onViewModeChange(mode)}
            className={`px-4 py-2 text-sm rounded-lg font-medium ${viewMode === mode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {mode === 'sprint' ? 'Sprint Board' : 'Kanban Board'}
          </button>
        ))}
      </div>

      {/* Group Toggle */}
      <div className="flex gap-2 mb-6">
        {[{ key: 'all', label: 'All' }, { key: 'dev', label: 'Dev Pipeline' }, { key: 'ops', label: 'Ops Pipeline' }].map(g => (
          <button key={g.key} onClick={() => onGroupChange(g.key)}
            className={`px-3 py-1.5 text-xs rounded-full font-medium ${group === g.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {g.label}
          </button>
        ))}
      </div>

      {/* Sprint Header (only in sprint view) */}
      {viewMode === 'sprint' && data.sprint && (group === 'all' || group === 'dev') && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{data.sprint.name}</h2>
              <p className="text-sm text-gray-600">{data.sprint.goal}</p>
              <div className="text-xs text-gray-500 mt-1">📅 {data.sprint.startDate} → {data.sprint.endDate}</div>
            </div>
          </div>
        </div>
      )}

      {/* Dev Pipeline content */}
      {(group === 'all' || group === 'dev') && (
        <>
          {viewMode === 'sprint' && (
            <>
              <DevOpsCapacityCards capacity={data.capacity} />
              <DevOpsTeamWorkload workload={data.workload} />
            </>
          )}
          <DevOpsPipelineBoard
            pipeline={viewMode === 'kanban' && data.kanbanPipeline ? data.kanbanPipeline : data.pipeline}
            title={viewMode === 'sprint' ? 'Sprint Pipeline' : 'Kanban Board'}
            onTaskClick={onTaskClick}
            onStatusChange={onStatusChange}
          />
        </>
      )}

      {/* Ops Pipeline content */}
      {(group === 'all' || group === 'ops') && data.ops && (
        <DevOpsOpsGroup pipelines={data.ops.pipelines} />
      )}
    </div>
  );
};

// Shared style helpers for PlanTrack
const getStatusStyle = (status) => {
  const styles = {
    active: { color: '#0366d6', bg: '#d4e4ff', label: 'Active' },
    planning: { color: '#6f42c1', bg: '#f3e8ff', label: 'Planning' },
    completed: { color: '#28a745', bg: '#dcffe4', label: 'Completed' },
    on_hold: { color: '#e36209', bg: '#fff5cc', label: 'On Hold' },
    backlog: { color: '#586069', bg: '#f6f8fa', label: 'Backlog' },
    in_progress: { color: '#0366d6', bg: '#d4e4ff', label: 'In Progress' },
    ready: { color: '#17a2b8', bg: '#d4f4ff', label: 'Ready' },
    review: { color: '#6f42c1', bg: '#f3e8ff', label: 'Review' },
    blocked: { color: '#dc3545', bg: '#ffe5e9', label: 'Blocked' }
  };
  return styles[status] || { color: '#586069', bg: '#f6f8fa', label: status };
};

const getPriorityStyle = (priority) => {
  const styles = {
    P1: { color: '#dc3545', bg: '#ffe5e9' },
    P2: { color: '#e36209', bg: '#fff5cc' },
    P3: { color: '#28a745', bg: '#dcffe4' }
  };
  return styles[priority] || { color: '#586069', bg: '#f6f8fa' };
};

const getMilestoneIcon = (status) => {
  if (status === 'completed') return { symbol: '✓', color: '#28a745', bg: '#dcffe4' };
  if (status === 'in_progress') return { symbol: '●', color: '#0366d6', bg: '#d4e4ff' };
  if (status === 'overdue') return { symbol: '!', color: '#dc3545', bg: '#ffe5e9' };
  return { symbol: '○', color: '#959da5', bg: '#f6f8fa' };
};

// ---- Milestone Engine Utilities ----

const getHealthBadgeStyle = (hs) => {
  const s = {
    'completed': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Completed' },
    'on-track': { bg: 'bg-green-100', text: 'text-green-700', label: 'On Track' },
    'at-risk': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'At Risk' },
    'critical': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Critical' },
    'overdue': { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' }
  };
  return s[hs] || s['on-track'];
};

const formatRiskScore = (score) => {
  if (score >= 80) return { level: 'Critical', color: 'text-red-600' };
  if (score >= 60) return { level: 'High', color: 'text-orange-600' };
  if (score >= 40) return { level: 'Medium', color: 'text-yellow-600' };
  if (score >= 20) return { level: 'Low', color: 'text-lime-600' };
  return { level: 'Minimal', color: 'text-green-600' };
};

// ---- Milestone Engine Modals ----

const AddMilestoneModal = ({ onClose, onSubmit, projectId, projects }) => {
  const [form, setForm] = useState({ projectId: projectId || '', title: '', description: '', dueDate: '', ownerId: '', isCriticalPath: false });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title || !form.projectId || !form.dueDate) return; onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Create Milestone</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Project *</label>
            <select value={form.projectId} onChange={e => setForm({...form, projectId: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required disabled={!!projectId}>
              <option value="">Select project...</option>
              {(projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Title *</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="e.g., Requirements Complete" required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" rows={2} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date *</label>
            <input type="date" value={form.dueDate} onChange={e => setForm({...form, dueDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.isCriticalPath} onChange={e => setForm({...form, isCriticalPath: e.target.checked})}
              className="w-4 h-4 text-blue-600 rounded" /> Mark as Critical Path
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">Create Milestone</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditMilestoneModal = ({ milestone, allMilestones, onClose, onSubmit, onDelete }) => {
  const [form, setForm] = useState({
    title: milestone.title || '', description: milestone.description || '', dueDate: milestone.due_date || '',
    status: milestone.status || 'pending', completionPercentage: milestone.completion_percentage || 0,
    ownerId: milestone.owner_id || '', isCriticalPath: !!milestone.is_critical_path
  });
  const [deps, setDeps] = useState(milestone.dependencies || []);
  const [showAddDep, setShowAddDep] = useState(false);
  const [newDep, setNewDep] = useState({ predecessorId: '', type: 'finish-to-start' });

  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  const handleAddDep = async () => {
    if (!newDep.predecessorId) return;
    await ModulesModel.createMilestoneDependency({ predecessorId: newDep.predecessorId, successorId: milestone.milestone_id, dependencyType: newDep.type });
    const updated = await ModulesModel.getMilestoneDetail(milestone.milestone_id);
    if (updated) setDeps(updated.dependencies || []);
    setShowAddDep(false); setNewDep({ predecessorId: '', type: 'finish-to-start' });
  };
  const handleRemoveDep = async (predId) => {
    await ModulesModel.deleteMilestoneDependency(predId, milestone.milestone_id);
    setDeps(deps.filter(d => d.predecessor_milestone_id !== predId));
  };

  // Filter milestones: same project, not self
  const depOptions = (allMilestones || []).filter(m => m.project_id === milestone.project_id && m.milestone_id !== milestone.milestone_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold text-gray-900">Edit Milestone</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" rows={2} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date</label>
              <input type="date" value={form.dueDate} onChange={e => setForm({...form, dueDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" required />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Completion: {form.completionPercentage}%</label>
              <input type="range" min="0" max="100" value={form.completionPercentage}
                onChange={e => setForm({...form, completionPercentage: parseInt(e.target.value)})} className="w-full mt-2" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.isCriticalPath} onChange={e => setForm({...form, isCriticalPath: e.target.checked})}
              className="w-4 h-4 text-blue-600 rounded" /> Critical Path Milestone
          </label>

          {/* Dependencies section */}
          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-bold text-gray-900">Dependencies (predecessors)</h3>
              <button type="button" onClick={() => setShowAddDep(!showAddDep)}
                className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">+ Add</button>
            </div>
            {showAddDep && (
              <div className="bg-gray-50 p-3 rounded-lg mb-3 flex gap-2 items-end">
                <div className="flex-1">
                  <select value={newDep.predecessorId} onChange={e => setNewDep({...newDep, predecessorId: e.target.value})}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                    <option value="">Select milestone...</option>
                    {depOptions.map(m => <option key={m.milestone_id} value={m.milestone_id}>{m.title}</option>)}
                  </select>
                </div>
                <select value={newDep.type} onChange={e => setNewDep({...newDep, type: e.target.value})}
                  className="w-40 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500">
                  <option value="finish-to-start">Finish-to-Start</option>
                  <option value="start-to-start">Start-to-Start</option>
                  <option value="finish-to-finish">Finish-to-Finish</option>
                </select>
                <button type="button" onClick={handleAddDep} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Add</button>
              </div>
            )}
            {deps.length === 0 ? <p className="text-sm text-gray-400 text-center py-2">No dependencies</p> : (
              <div className="space-y-1">
                {deps.filter(d => d.successor_milestone_id === milestone.milestone_id).map(d => (
                  <div key={d.id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded text-sm">
                    <span><span className="font-medium">{d.predecessor_title}</span> <span className="text-gray-400 text-xs mx-1">{d.dependency_type}</span></span>
                    <button type="button" onClick={() => handleRemoveDep(d.predecessor_milestone_id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Linked tasks */}
          {milestone.linkedTasks && milestone.linkedTasks.length > 0 && (
            <div className="border-t pt-4">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Linked Tasks ({milestone.linkedTasks.length})</h3>
              <div className="space-y-1">
                {milestone.linkedTasks.map(t => (
                  <div key={t.task_id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded text-sm">
                    <span>{t.title}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${t.status === 'completed' || t.status === 'done' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{t.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-4 border-t">
            <button type="button" onClick={() => { if (confirm('Delete this milestone?')) onDelete(milestone.milestone_id); }}
              className="px-4 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 font-medium">Delete</button>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">Save Changes</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const ManageTemplatesModal = ({ onClose, onChanged }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', projectType: 'general', description: '' });
  const [newItem, setNewItem] = useState({ title: '', offsetDays: 0, durationDays: 7, isCritical: false });
  const [showNewItem, setShowNewItem] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTemplates = async () => {
    setLoading(true);
    const data = await ModulesModel.getMilestoneTemplates();
    setTemplates(data || []);
    setLoading(false);
  };

  useEffect(() => { loadTemplates(); }, []);

  useEffect(() => {
    if (selectedId) {
      ModulesModel.getTemplateItems(selectedId).then(data => setItems(data || []));
    } else {
      setItems([]);
    }
  }, [selectedId]);

  const selected = templates.find(t => t.id === selectedId);

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if (!newTemplate.name.trim()) return;
    setSaving(true);
    await ModulesModel.createMilestoneTemplate(newTemplate);
    setNewTemplate({ name: '', projectType: 'general', description: '' });
    setShowNewTemplate(false);
    setSaving(false);
    await loadTemplates();
    onChanged();
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm('Delete this template and all its milestones?')) return;
    await ModulesModel.deleteMilestoneTemplate(id);
    if (selectedId === id) setSelectedId(null);
    await loadTemplates();
    onChanged();
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.title.trim() || !selectedId) return;
    setSaving(true);
    await ModulesModel.createTemplateItem(selectedId, { ...newItem, sortOrder: items.length });
    setNewItem({ title: '', offsetDays: 0, durationDays: 7, isCritical: false });
    setShowNewItem(false);
    setSaving(false);
    const updated = await ModulesModel.getTemplateItems(selectedId);
    setItems(updated || []);
  };

  const handleDeleteItem = async (itemId) => {
    await ModulesModel.deleteTemplateItem(selectedId, itemId);
    const updated = await ModulesModel.getTemplateItems(selectedId);
    setItems(updated || []);
  };

  const projectTypeOptions = ['general', 'software', 'marketing', 'operations', 'research', 'infrastructure'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Manage Milestone Templates</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Template list */}
          <div className="w-64 border-r flex flex-col bg-gray-50">
            <div className="p-3 border-b">
              <button onClick={() => setShowNewTemplate(true)} className="w-full px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Template</button>
            </div>
            {showNewTemplate && (
              <form onSubmit={handleCreateTemplate} className="p-3 border-b bg-white space-y-2">
                <input required placeholder="Template name" value={newTemplate.name} onChange={e => setNewTemplate({...newTemplate, name: e.target.value})}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                <select value={newTemplate.projectType} onChange={e => setNewTemplate({...newTemplate, projectType: e.target.value})}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm">
                  {projectTypeOptions.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
                <input placeholder="Description (optional)" value={newTemplate.description} onChange={e => setNewTemplate({...newTemplate, description: e.target.value})}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                <div className="flex gap-2">
                  <button type="submit" disabled={saving} className="flex-1 px-2 py-1.5 bg-purple-600 text-white text-xs rounded hover:bg-purple-700">Save</button>
                  <button type="button" onClick={() => setShowNewTemplate(false)} className="flex-1 px-2 py-1.5 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
                </div>
              </form>
            )}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <p className="text-xs text-gray-400 p-3">Loading...</p>
              ) : templates.length === 0 ? (
                <p className="text-xs text-gray-400 p-3">No templates yet.</p>
              ) : templates.map(t => (
                <div key={t.id} onClick={() => setSelectedId(t.id)}
                  className={`px-3 py-2.5 cursor-pointer border-b border-gray-100 hover:bg-purple-50 flex justify-between items-start ${selectedId === t.id ? 'bg-purple-100 border-l-4 border-l-purple-500' : ''}`}>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.template_name}</p>
                    <p className="text-xs text-gray-500">{t.project_type} · {t.item_count} milestones</p>
                  </div>
                  <button onClick={e => { e.stopPropagation(); handleDeleteTemplate(t.id); }} className="text-red-400 hover:text-red-600 text-xs ml-1 mt-0.5">✕</button>
                </div>
              ))}
            </div>
          </div>

          {/* Template items */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Select a template to view and edit its milestones</div>
            ) : (
              <>
                <div className="px-5 py-3 border-b flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-gray-900">{selected.template_name}</h3>
                    {selected.description && <p className="text-xs text-gray-500 mt-0.5">{selected.description}</p>}
                  </div>
                  <button onClick={() => setShowNewItem(true)} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ Add Milestone</button>
                </div>

                {showNewItem && (
                  <form onSubmit={handleAddItem} className="px-5 py-3 bg-blue-50 border-b grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <input required placeholder="Milestone title" value={newItem.title} onChange={e => setNewItem({...newItem, title: e.target.value})}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Start offset (days from project start)</label>
                      <input type="number" min="0" value={newItem.offsetDays} onChange={e => setNewItem({...newItem, offsetDays: parseInt(e.target.value) || 0})}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Duration (days)</label>
                      <input type="number" min="1" value={newItem.durationDays} onChange={e => setNewItem({...newItem, durationDays: parseInt(e.target.value) || 1})}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                    </div>
                    <div className="col-span-2 flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={newItem.isCritical} onChange={e => setNewItem({...newItem, isCritical: e.target.checked})} />
                        Mark as critical path
                      </label>
                      <div className="flex gap-2">
                        <button type="submit" disabled={saving} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Add</button>
                        <button type="button" onClick={() => setShowNewItem(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                      </div>
                    </div>
                  </form>
                )}

                <div className="flex-1 overflow-y-auto">
                  {items.length === 0 ? (
                    <div className="p-6 text-center text-gray-400 text-sm">No milestones in this template yet. Add one above.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">#</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Milestone</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Offset</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Duration</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Critical</th>
                          <th className="px-4 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {items.map((item, i) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 text-gray-400 text-xs">{i + 1}</td>
                            <td className="px-4 py-2.5 font-medium text-gray-900">{item.title}</td>
                            <td className="px-4 py-2.5 text-gray-600">Day {item.offset_from_start}</td>
                            <td className="px-4 py-2.5 text-gray-600">{item.default_duration_days}d</td>
                            <td className="px-4 py-2.5">
                              {item.is_critical ? <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">Critical</span> : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <button onClick={() => handleDeleteItem(item.id)} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const GenerateFromTemplateModal = ({ projects, templates, onClose, onGenerate }) => {
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [templateItems, setTemplateItems] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (selectedTemplate) {
      ModulesModel.getTemplateItems(parseInt(selectedTemplate)).then(items => setTemplateItems(items || []));
    } else { setTemplateItems([]); }
  }, [selectedTemplate]);

  const handleGenerate = async () => {
    if (!selectedProject || !selectedTemplate || !startDate) return;
    setGenerating(true);
    await onGenerate({ projectId: selectedProject, templateId: parseInt(selectedTemplate), startDate });
    setGenerating(false);
  };

  const tmpl = templates.find(t => t.id === parseInt(selectedTemplate));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Generate from Template</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Project *</label>
              <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="">Select project...</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Template *</label>
              <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="">Select template...</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.template_name} ({t.item_count} milestones)</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Project Start Date *</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            <p className="text-xs text-gray-500 mt-1">Milestone due dates are calculated from this date</p>
          </div>
          {tmpl && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-1">{tmpl.template_name}</h3>
              <p className="text-sm text-blue-800">{tmpl.description}</p>
              <p className="text-xs text-blue-600 mt-1">Type: {tmpl.project_type}</p>
            </div>
          )}
          {templateItems.length > 0 && (
            <div className="border rounded-lg">
              <div className="bg-gray-50 px-4 py-2 border-b"><h3 className="font-semibold text-gray-900 text-sm">Preview: {templateItems.length} Milestones</h3></div>
              <div className="p-4 max-h-52 overflow-y-auto space-y-2">
                {templateItems.map((item, i) => (
                  <div key={item.id} className="flex items-start gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0">{i+1}</div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{item.title}{item.is_critical ? <span className="ml-2 text-red-600 font-semibold text-xs">CRITICAL</span> : ''}</div>
                      <div className="text-xs text-gray-500">Day {item.offset_from_start} + {item.default_duration_days}d</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button onClick={handleGenerate} disabled={generating || !selectedProject || !selectedTemplate || !startDate}
              className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50">
              {generating ? 'Generating...' : `Generate ${templateItems.length} Milestones`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Milestone Engine View ----

const MilestoneEngineView = ({ onBack, projects }) => {
  const [milestones, setMilestones] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [execSummary, setExecSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showManageTemplates, setShowManageTemplates] = useState(false);
  const [editingMs, setEditingMs] = useState(null);
  const [filters, setFilters] = useState({ project: '', status: '', health: '' });

  const loadData = async () => {
    setLoading(true);
    const [ms, tmpl, summary] = await Promise.all([
      ModulesModel.getMilestones(), ModulesModel.getMilestoneTemplates(), ModulesModel.getExecutiveSummary()
    ]);
    setMilestones(ms || []); setTemplates(tmpl || []); setExecSummary(summary);
    setLoading(false);
  };
  useEffect(() => { loadData(); }, []);

  const handleCreate = async (data) => { await ModulesModel.createMilestone(data); setShowCreate(false); loadData(); };
  const handleUpdate = async (msId, data) => { await ModulesModel.updateMilestone(msId, data); setEditingMs(null); loadData(); };
  const handleDelete = async (msId) => { await ModulesModel.deleteMilestone(msId); setEditingMs(null); loadData(); };
  const handleComplete = async (msId) => {
    const r = await ModulesModel.completeMilestone(msId);
    if (!r.success) alert(r.error || 'Failed'); else loadData();
  };
  const handleGenerate = async (data) => {
    const r = await ModulesModel.generateMilestonesFromTemplate(data);
    if (r.success) { alert(`Generated ${r.createdCount} milestones!`); setShowGenerate(false); loadData(); }
    else alert(r.error || 'Failed');
  };

  const filtered = milestones.filter(m => {
    if (filters.project && m.project_id !== filters.project) return false;
    if (filters.status && m.status !== filters.status) return false;
    if (filters.health && m.health_status !== filters.health) return false;
    return true;
  });

  const s = execSummary?.summary || {};

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading Milestone Engine...</div></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium">
        <ChevronRight size={16} className="rotate-180" /> Back to PlanTrack
      </button>

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Milestone Engine</h1>
          <p className="text-gray-600 mt-1">Template-driven milestones, risk scoring, and critical path analysis</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowManageTemplates(true)} className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 font-medium">Manage Templates</button>
          <button onClick={() => setShowGenerate(true)} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">Generate from Template</button>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Milestone</button>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { label: 'Total', value: s.total_milestones || 0, sub: `${s.completed || 0} completed`, color: 'text-gray-900' },
          { label: 'Overdue', value: s.overdue || 0, sub: 'Requires attention', color: 'text-red-600' },
          { label: 'Critical', value: s.critical || 0, sub: 'High priority', color: 'text-orange-600' },
          { label: 'At Risk', value: s.at_risk || 0, sub: 'Monitor closely', color: 'text-yellow-600' },
          { label: 'Critical Path', value: s.on_critical_path || 0, sub: 'Essential', color: 'text-purple-600' }
        ].map(c => (
          <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <div className="text-xs text-gray-500 uppercase font-semibold mb-1">{c.label}</div>
            <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
            <div className="text-xs text-gray-500 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Delayed + Upcoming */}
      {(execSummary?.delayedMilestones?.length > 0 || execSummary?.upcomingMilestones?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {execSummary?.delayedMilestones?.length > 0 && (
            <div className="bg-white rounded-lg shadow border border-red-200 p-4">
              <h3 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2"><AlertTriangle size={14} /> Delayed Milestones</h3>
              <div className="space-y-2">
                {execSummary.delayedMilestones.map(m => {
                  const hs = getHealthBadgeStyle(m.health_status);
                  return (
                    <div key={m.milestone_id} className="flex justify-between items-center text-sm">
                      <div><span className="font-medium text-gray-900">{m.title}</span><span className="text-gray-500 ml-2 text-xs">{m.project_name}</span></div>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${hs.bg} ${hs.text}`}>{hs.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {execSummary?.upcomingMilestones?.length > 0 && (
            <div className="bg-white rounded-lg shadow border border-blue-200 p-4">
              <h3 className="text-sm font-bold text-blue-700 mb-3">Upcoming Milestones</h3>
              <div className="space-y-2">
                {execSummary.upcomingMilestones.map(m => (
                  <div key={m.milestone_id} className="flex justify-between items-center text-sm">
                    <div><span className="font-medium text-gray-900">{m.title}</span><span className="text-gray-500 ml-2 text-xs">{m.project_name}</span></div>
                    <span className="text-xs text-gray-500">{m.due_date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Project</label>
            <select value={filters.project} onChange={e => setFilters({...filters, project: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">All Projects</option>
              {(projects || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
            <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">All</option><option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Health</label>
            <select value={filters.health} onChange={e => setFilters({...filters, health: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">All</option><option value="on-track">On Track</option><option value="at-risk">At Risk</option><option value="critical">Critical</option><option value="overdue">Overdue</option><option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Milestone list */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
            <p className="text-gray-500 mb-4">No milestones match your filters.</p>
            <button onClick={() => setFilters({ project: '', status: '', health: '' })} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Clear Filters</button>
          </div>
        ) : filtered.map(ms => {
          const hs = getHealthBadgeStyle(ms.health_status);
          const rf = formatRiskScore(ms.risk_score || 0);
          return (
            <div key={ms.id || ms.milestone_id} className="bg-white rounded-lg shadow border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <h3 className="text-base font-semibold text-gray-900">{ms.title}</h3>
                    {ms.is_critical_path === 1 && <span className="px-2 py-0.5 rounded text-xs font-bold bg-purple-100 text-purple-700">CRITICAL PATH</span>}
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${hs.bg} ${hs.text}`}>{hs.label}</span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    <span className="font-medium">{ms.project_name}</span>
                    {ms.owner_name && <span className="ml-3 text-gray-400">Owner: {ms.owner_name}</span>}
                  </div>
                  <div className="text-xs text-gray-500 mb-2">
                    Due: {ms.due_date}{ms.status === 'completed' && ms.completed_at && <span className="ml-3 text-green-600">Completed: {ms.completed_at}</span>}
                  </div>
                  {ms.status !== 'completed' && (
                    <div className="flex items-center gap-6">
                      <div className="flex-1 max-w-xs">
                        <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Completion</span><span>{ms.completion_percentage || 0}%</span></div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${ms.completion_percentage || 0}%` }} /></div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">Risk</div>
                        <div className={`text-lg font-bold ${rf.color}`}>{ms.risk_score || 0}</div>
                        <div className="text-xs text-gray-500">{rf.level}</div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4 shrink-0">
                  {ms.status !== 'completed' && (
                    <button onClick={() => handleComplete(ms.milestone_id)} className="px-3 py-1.5 text-xs bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-medium">Complete</button>
                  )}
                  <button onClick={async () => { const d = await ModulesModel.getMilestoneDetail(ms.milestone_id); if (d) setEditingMs(d); }}
                    className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 font-medium">Edit</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && <AddMilestoneModal onClose={() => setShowCreate(false)} onSubmit={handleCreate} projects={projects || []} />}
      {showGenerate && <GenerateFromTemplateModal projects={projects || []} templates={templates} onClose={() => setShowGenerate(false)} onGenerate={handleGenerate} />}
      {showManageTemplates && <ManageTemplatesModal onClose={() => setShowManageTemplates(false)} onChanged={() => { loadData(); }} />}
      {editingMs && <EditMilestoneModal milestone={editingMs} allMilestones={milestones} onClose={() => setEditingMs(null)} onSubmit={(data) => handleUpdate(editingMs.milestone_id, data)} onDelete={handleDelete} />}
    </div>
  );
};

// ---- Modal Components ----

const AddProjectModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: '', description: '', priority: 'P2', owner: '', startDate: '', targetEndDate: '', budgetHours: '' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Project</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="P1">P1 - Critical</option>
                <option value="P2">P2 - High</option>
                <option value="P3">P3 - Normal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
              <input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target End Date</label>
              <input type="date" value={form.targetEndDate} onChange={e => setForm({...form, targetEndDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Budget Hours</label>
            <input type="number" value={form.budgetHours} onChange={e => setForm({...form, budgetHours: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Create Project</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditProjectModal = ({ project, sprints, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    name: project.name || '',
    description: project.description || '',
    status: project.status || 'planning',
    priority: project.priority || 'P2',
    owner: project.owner || '',
    startDate: project.startDate || '',
    targetEndDate: project.targetEndDate || '',
    budgetHours: project.budgetHours || '',
    progress: project.progress || 0,
    sprintId: project.sprintId || ''
  });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Edit Project</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="P1">P1 - Critical</option>
                <option value="P2">P2 - High</option>
                <option value="P3">P3 - Normal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Progress %</label>
              <input type="number" min="0" max="100" value={form.progress} onChange={e => setForm({...form, progress: parseInt(e.target.value) || 0})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
            <input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target End Date</label>
              <input type="date" value={form.targetEndDate} onChange={e => setForm({...form, targetEndDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Hours</label>
              <input type="number" value={form.budgetHours} onChange={e => setForm({...form, budgetHours: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint</label>
              <select value={form.sprintId} onChange={e => setForm({...form, sprintId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">No Sprint</option>
                {(sprints || []).map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.status === 'active' ? ' (Active)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddSprintModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: '', number: '', startDate: '', endDate: '', status: 'active', goal: '', capacityHours: '160' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Sprint</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Name *</label>
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Number</label>
              <input type="number" value={form.number} onChange={e => setForm({...form, number: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="active">Active</option>
              <option value="planned">Planned</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input required type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input required type="date" value={form.endDate} onChange={e => setForm({...form, endDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Goal</label>
            <input value={form.goal} onChange={e => setForm({...form, goal: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Capacity Hours</label>
            <input type="number" value={form.capacityHours} onChange={e => setForm({...form, capacityHours: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Create Sprint</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddTaskInline = ({ onSubmit, onCancel }) => {
  const [form, setForm] = useState({ title: '', priority: 'P2', assignee: '', type: 'task', estimateHours: '', storyPoints: '' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
      <input required placeholder="Task title" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="P1">P1</option><option value="P2">P2</option><option value="P3">P3</option>
        </select>
        <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="task">Project Task</option><option value="ADHOC">Ad hoc</option>
        </select>
        <input placeholder="Assignee" value={form.assignee} onChange={e => setForm({...form, assignee: e.target.value})}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <input type="number" placeholder="Hours" value={form.estimateHours} onChange={e => setForm({...form, estimateHours: e.target.value})}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      </div>
      <div className="flex gap-2">
        <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Add Task</button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
      </div>
    </form>
  );
};

// ---- Project Detail View ----

const ProjectDetailView = ({ detail, onBack, onRefresh, onEdit, onCreateMilestone, onEditMilestone, onCompleteMilestone, user }) => {
  const [activeTab, setActiveTab] = useState('tasks');
  const [commentText, setCommentText] = useState('');
  const [updateText, setUpdateText] = useState('');
  const [showAddTask, setShowAddTask] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({});
  const [selectedProjectTask, setSelectedProjectTask] = useState(null);
  const [selectedProjectBug, setSelectedProjectBug] = useState(null);

  const { project, tasks, comments, updates, milestones, bugs = [] } = detail;

  useEffect(() => {
    if (project?.id) {
      ModulesModel.getProjectOverview(project.id).then(data => { if (data) setOverview(data); });
    }
  }, [project?.id]);
  const statusStyle = getStatusStyle(project.status);
  const priorityStyle = getPriorityStyle(project.priority);
  const budgetPercent = project.budgetHours > 0 ? Math.round((project.spentHours / project.budgetHours) * 100) : 0;

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    await ModulesModel.addComment(project.id, commentText.trim());
    setCommentText('');
    setSubmitting(false);
    onRefresh();
  };

  const handleAddUpdate = async (e) => {
    e.preventDefault();
    if (!updateText.trim()) return;
    setSubmitting(true);
    await ModulesModel.addUpdate(project.id, updateText.trim());
    setUpdateText('');
    setSubmitting(false);
    onRefresh();
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Delete this task? This cannot be undone.')) return;
    await ModulesModel.deleteProjectTask(project.id, taskId);
    onRefresh();
  };

  const startEditTask = (task) => {
    setEditingTaskId(task.id);
    setEditTaskForm({ title: task.title, status: task.status, priority: task.priority, assignee: task.assignee || '', progress: task.progress || 0 });
  };

  const handleSaveTask = async () => {
    await ModulesModel.updateDevOpsTask(editingTaskId, editTaskForm);
    setEditingTaskId(null);
    onRefresh();
  };

  const handleProjectTaskUpdate = async (taskId, changes) => {
    await ModulesModel.updateDevOpsTask(taskId, changes);
    onRefresh();
  };

  const handleAddTask = async (taskData) => {
    setSubmitting(true);
    if (taskData.type === 'ADHOC') {
      const priorityMap = { P1: 'urgent', P2: 'high', P3: 'medium' };
      await ModulesModel.createQuickTask({
        title: taskData.title,
        priority: priorityMap[taskData.priority] || 'medium',
        assignedTo: taskData.assignee || null,
        projectId: project.id,
        category: 'general',
        status: 'open'
      });
    } else {
      await ModulesModel.createProjectTask(project.id, taskData);
    }
    setShowAddTask(false);
    setSubmitting(false);
    onRefresh();
  };

  const tabs = [
    { id: 'tasks', label: 'Tasks', count: tasks.length },
    { id: 'bugs', label: 'Bugs', count: bugs.length },
    { id: 'updates', label: 'Updates', count: updates.length },
    { id: 'comments', label: 'Comments', count: comments.length },
    { id: 'milestones', label: 'Milestones', count: milestones.length }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {selectedProjectTask && (
        <TaskDetailPanel
          task={selectedProjectTask}
          onClose={() => setSelectedProjectTask(null)}
          onUpdate={handleProjectTaskUpdate}
          user={user}
        />
      )}
      {selectedProjectBug && (
        <BugDetailPanel
          bug={selectedProjectBug}
          onClose={() => setSelectedProjectBug(null)}
          user={user}
          readOnly={true}
        />
      )}
      {/* Back button */}
      <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium">
        <ChevronRight size={16} className="rotate-180" /> Back to Projects
      </button>

      {/* Project Header */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-gray-500">{project.id}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: priorityStyle.bg, color: priorityStyle.color }}>{project.priority}</span>
            <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: statusStyle.bg, color: statusStyle.color }}>{statusStyle.label}</span>
            {project.sprintName && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">{project.sprintName}</span>}
          </div>
          <button onClick={onEdit} className="px-3 py-1.5 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 font-medium">Edit Project</button>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{project.name}</h1>
        <p className="text-gray-600 mb-4">{project.description}</p>
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-4">
          <span>👤 {project.owner}</span>
          <span>📅 {project.startDate} → {project.targetEndDate}</span>
          <span>💰 {project.spentHours}h / {project.budgetHours}h budget ({budgetPercent}%)</span>
        </div>
        <div>
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Progress</span>
            <span>{project.progress}%</span>
          </div>
          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${project.progress}%`, background: project.progress === 100 ? '#28a745' : '#0366d6' }} />
          </div>
        </div>
      </div>

      {/* Cross-Module Summary */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'DevOps Tasks', value: overview.tasks, icon: '🔧', color: '#0366d6' },
            { label: 'Quick Tasks', value: overview.quickTasks, icon: '⚡', color: '#667eea' },
            { label: 'Pipelines', value: overview.pipelines, icon: '📊', color: '#6f42c1' },
            { label: 'Time Logged', value: `${overview.timeLogged}h`, icon: '⏱️', color: '#17a2b8' },
            ...(overview.qa ? [
              { label: 'QA Test Cases', value: overview.qa.testCases, icon: '✓', color: '#20c997' },
              { label: 'Open Bugs', value: overview.qa.openBugs, icon: '🐛', color: overview.qa.openBugs > 0 ? '#dc3545' : '#28a745' }
            ] : [
              { label: 'QA Status', value: 'Not Enabled', icon: '✓', color: '#6b7280' },
              { label: '', value: '', icon: '', color: '' }
            ])
          ].filter(s => s.label).map(s => (
            <div key={s.label} className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span>{s.icon}</span>
                <span className="text-xs font-medium text-gray-500">{s.label}</span>
              </div>
              <div className="text-xl font-bold" style={{ color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}>
            {tab.label} <span className="ml-1 px-1.5 py-0.5 bg-gray-100 rounded-full text-xs">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Linked Tasks</h2>
            {!showAddTask && (
              <button onClick={() => setShowAddTask(true)} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ Add Task</button>
            )}
          </div>
          {showAddTask && <div className="mb-4"><AddTaskInline onSubmit={handleAddTask} onCancel={() => setShowAddTask(false)} /></div>}
          {tasks.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No tasks linked to this project yet.</div>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Assignee</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Progress</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tasks.map(task => {
                    const isEditing = editingTaskId === task.id;
                    const ts = getStatusStyle(task.status);
                    const tp = getPriorityStyle(task.priority);
                    if (isEditing) {
                      return (
                        <tr key={task.id} className="bg-blue-50">
                          <td className="px-4 py-2 text-xs font-mono text-gray-500">{task.id}</td>
                          <td className="px-4 py-2">
                            <input value={editTaskForm.title} onChange={e => setEditTaskForm({...editTaskForm, title: e.target.value})}
                              className="w-full border border-blue-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500" />
                          </td>
                          <td className="px-4 py-2">
                            <select value={editTaskForm.status} onChange={e => setEditTaskForm({...editTaskForm, status: e.target.value})}
                              className="border border-blue-300 rounded px-2 py-1 text-xs">
                              {['backlog','ready','in_progress','review','done'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <select value={editTaskForm.priority} onChange={e => setEditTaskForm({...editTaskForm, priority: e.target.value})}
                              className="border border-blue-300 rounded px-2 py-1 text-xs">
                              {['P1','P2','P3'].map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <input value={editTaskForm.assignee} onChange={e => setEditTaskForm({...editTaskForm, assignee: e.target.value})}
                              className="w-full border border-blue-300 rounded px-2 py-1 text-sm" placeholder="Assignee" />
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <input type="number" min="0" max="100" value={editTaskForm.progress} onChange={e => setEditTaskForm({...editTaskForm, progress: parseInt(e.target.value) || 0})}
                                className="w-16 border border-blue-300 rounded px-2 py-1 text-xs" />
                              <span className="text-xs text-gray-500">%</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            <button onClick={handleSaveTask} className="text-blue-600 hover:text-blue-800 text-xs font-medium mr-2">Save</button>
                            <button onClick={() => setEditingTaskId(null)} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={task.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">{task.id}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{task.title}</td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: ts.bg, color: ts.color }}>{ts.label}</span></td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: tp.bg, color: tp.color }}>{task.priority}</span></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{task.assignee}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-600 rounded-full" style={{ width: `${task.progress || 0}%` }} />
                            </div>
                            <span className="text-xs text-gray-500">{task.progress || 0}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button onClick={() => setSelectedProjectTask(task)} className="text-blue-500 hover:text-blue-700 text-xs font-medium mr-2">View</button>
                          <button onClick={() => startEditTask(task)} className="text-indigo-500 hover:text-indigo-700 text-xs font-medium mr-2">Edit</button>
                          <button onClick={() => handleDeleteTask(task.id)} className="text-red-400 hover:text-red-600 text-xs font-medium">Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Bugs Tab */}
      {activeTab === 'bugs' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">QA Bugs</h2>
            <span className="text-xs text-gray-500">Bugs are created in the Quality module</span>
          </div>
          {bugs.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No bugs reported for this project yet.</div>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Bug ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Severity</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Module</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Assigned To</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Reported</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bugs.map(bug => {
                    const severityColors = { critical: { bg: '#fee2e2', color: '#dc2626' }, high: { bg: '#fed7aa', color: '#ea580c' }, medium: { bg: '#fef9c3', color: '#ca8a04' }, low: { bg: '#dcfce7', color: '#16a34a' } };
                    const sc = severityColors[bug.severity] || severityColors.medium;
                    const bugStatusColors = { open: { bg: '#fee2e2', color: '#dc2626' }, 'in-progress': { bg: '#dbeafe', color: '#2563eb' }, resolved: { bg: '#dcfce7', color: '#16a34a' }, closed: { bg: '#f3f4f6', color: '#6b7280' } };
                    const bc = bugStatusColors[bug.status] || bugStatusColors.open;
                    return (
                      <tr key={bug.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">{bug.bugCode}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{bug.title}</td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: sc.bg, color: sc.color }}>{bug.severity}</span></td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: bc.bg, color: bc.color }}>{bug.status || 'open'}</span></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{bug.module || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{bug.assignedTo || '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-400">{bug.createdAt ? new Date(bug.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setSelectedProjectBug(bug)} className="text-blue-500 hover:text-blue-700 text-xs font-medium">View</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Updates Tab */}
      {activeTab === 'updates' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Updates</h2>
          <form onSubmit={handleAddUpdate} className="mb-6 flex gap-3">
            <input value={updateText} onChange={e => setUpdateText(e.target.value)} placeholder="Post a project update..."
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <button type="submit" disabled={submitting || !updateText.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">Post</button>
          </form>
          {updates.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No updates yet.</div>
          ) : (
            <div className="space-y-3">
              {updates.map(upd => (
                <div key={upd.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">{upd.userName.charAt(0)}</div>
                    <span className="font-medium text-sm text-gray-900">{upd.userName}</span>
                    {upd.updateType === 'status_change' && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-semibold">Status Change</span>}
                    {upd.updateType === 'progress' && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">Progress</span>}
                    <span className="text-xs text-gray-400 ml-auto">{formatDateTime(upd.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-700">{upd.content}</p>
                  {upd.oldValue && upd.newValue && (
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="px-2 py-0.5 bg-gray-100 rounded">{upd.oldValue}</span> → <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">{upd.newValue}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Comments Tab */}
      {activeTab === 'comments' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Comments</h2>
          <form onSubmit={handleAddComment} className="mb-6 flex gap-3">
            <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Write a comment..."
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <button type="submit" disabled={submitting || !commentText.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">Comment</button>
          </form>
          {comments.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No comments yet. Be the first to comment!</div>
          ) : (
            <div className="space-y-3">
              {comments.map(comment => (
                <div key={comment.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-bold">{comment.userName.charAt(0)}</div>
                    <span className="font-medium text-sm text-gray-900">{comment.userName}</span>
                    <span className="text-xs text-gray-400 ml-auto">{formatDateTime(comment.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-700">{comment.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Milestones Tab — Enhanced with Milestone Engine */}
      {activeTab === 'milestones' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Project Milestones</h2>
            <div className="flex gap-2">
              <button onClick={async () => { const r = await ModulesModel.calculateCriticalPath(project.id); if (r.success) onRefresh(); }}
                className="px-3 py-1.5 text-xs bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 font-medium">Calculate Critical Path</button>
              {onCreateMilestone && <button onClick={onCreateMilestone} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">+ Add Milestone</button>}
            </div>
          </div>
          {milestones.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
              <p className="text-gray-400 mb-3">No milestones defined for this project.</p>
              {onCreateMilestone && <button onClick={onCreateMilestone} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Create first milestone</button>}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <div className="space-y-3">
                {milestones.map((ms, i) => {
                  const icon = getMilestoneIcon(ms.status);
                  const hs = getHealthBadgeStyle(ms.health_status || (ms.status === 'completed' ? 'completed' : 'on-track'));
                  const rf = formatRiskScore(ms.risk_score || 0);
                  return (
                    <div key={ms.id} className="flex gap-4 pb-3 border-b last:border-b-0 last:pb-0">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${ms.is_critical_path ? 'ring-2 ring-purple-400' : ''}`} style={{ background: icon.bg, color: icon.color }}>{icon.symbol}</div>
                        {i < milestones.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 my-1" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <span className="font-semibold text-gray-900">{ms.title}</span>
                              {ms.is_critical_path === 1 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700">CRITICAL</span>}
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${hs.bg} ${hs.text}`}>{hs.label}</span>
                            </div>
                            {ms.description && <p className="text-sm text-gray-600 mb-1">{ms.description}</p>}
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span>{ms.status === 'completed' ? `Completed ${formatDate(ms.completedAt)}` : `Due ${formatDate(ms.dueDate)}`}</span>
                              {ms.status !== 'completed' && ms.risk_score > 0 && <span className={`font-semibold ${rf.color}`}>Risk: {ms.risk_score}</span>}
                              {ms.completion_percentage > 0 && ms.status !== 'completed' && <span>{ms.completion_percentage}% done</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-2 shrink-0">
                            {ms.status !== 'completed' && onCompleteMilestone && (
                              <button onClick={() => onCompleteMilestone(ms.milestone_id || ms.id)} className="text-green-600 hover:text-green-800 text-xs font-medium px-2 py-1">Complete</button>
                            )}
                            {onEditMilestone && <button onClick={() => onEditMilestone(ms.milestone_id || ms.id)} className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1">Edit</button>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---- PlanTrack List View ----

const PostUpdateReportModal = ({ onClose }) => {
  const [preparedBy, setPreparedBy] = React.useState('');
  const [role, setRole] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const handleSend = async () => {
    if (!preparedBy.trim()) { setError('Enter your name'); return; }
    setSending(true); setError('');
    const res = await ModulesModel.postProjectUpdateReport(preparedBy.trim(), role.trim());
    setSending(false);
    if (res?.success) setResult(res);
    else setError(res?.error || 'Failed to send report');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">📋 Post Project Update Report</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {result ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-2">✅</div>
            <p className="font-medium text-gray-800">Report sent to Pipedream!</p>
            <p className="text-sm text-gray-500 mt-1">{result.projectCount} active project(s) included</p>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">Sends a consolidated status report covering all active projects with their latest updates.</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prepared By</label>
            <input type="text" value={preparedBy} onChange={e => setPreparedBy(e.target.value)}
              placeholder="e.g. Gian Halili"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <label className="block text-sm font-medium text-gray-700 mb-1">Role <span className="text-gray-400 font-normal">(optional)</span></label>
            <input type="text" value={role} onChange={e => setRole(e.target.value)}
              placeholder="e.g. IT Project Manager"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            {error && <p className="text-red-500 text-sm mt-2 bg-red-50 p-2 rounded-lg">{error}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSend} disabled={sending}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {sending ? 'Sending…' : 'Send Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const EmailSummaryModal = ({ onClose, onSend }) => {
  const [email, setEmail] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleSend = async () => {
    if (!email || !email.includes('@')) { setError('Enter a valid email address'); return; }
    setSending(true); setError('');
    const res = await onSend(email);
    setSending(false);
    if (res?.success) setSent(true);
    else setError(res?.error || 'Failed to send. Check Cloudflare Pages logs for details.');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Email Status Report</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {sent ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-2">✅</div>
            <p className="text-gray-700 font-medium">Status report sent!</p>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">Send a project status summary email including stats, active projects, and upcoming milestones.</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="stakeholder@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {error && <p className="text-red-500 text-xs mt-1 mb-2">{error}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSend} disabled={sending} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {sending ? 'Sending…' : 'Send Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PlanTrackView = ({ data, onSelectProject, onShowAddProject, onShowSprints, onShowMilestones, onShowEmailSummary, onShowPostUpdateReport }) => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📋 PlanTrack</h1>
          <p className="text-gray-600 mt-1">Project planning, sprint management, and milestone tracking.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onShowPostUpdateReport} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">📋 Post Update</button>
          <button onClick={onShowEmailSummary} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">📧 Email Report</button>
          <button onClick={onShowAddProject} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Project</button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Projects', value: data.stats.total, color: '#667eea' },
          { label: 'Active', value: data.stats.active, color: '#0366d6' },
          { label: 'Completed', value: data.stats.completed, color: '#28a745' },
          { label: 'On Hold', value: data.stats.onHold, color: '#e36209' }
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-lg shadow p-5">
            <div className="text-sm text-gray-600 mb-1">{stat.label}</div>
            <div className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Projects */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Projects</h2>
          <div className="space-y-4">
            {data.projects.map(project => {
              const ss = getStatusStyle(project.status);
              const ps = getPriorityStyle(project.priority);
              const bp = project.budgetHours > 0 ? Math.round((project.spentHours / project.budgetHours) * 100) : 0;
              return (
                <div key={project.id} onClick={() => onSelectProject(project.id)} className="bg-white rounded-lg shadow border border-gray-200 p-5 hover:shadow-lg hover:border-blue-400 transition-all cursor-pointer">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-semibold text-gray-500">{project.id}</span>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: ps.bg, color: ps.color }}>{project.priority}</span>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: ss.bg, color: ss.color }}>{ss.label}</span>
                    {project.sprintName && <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700">{project.sprintName}</span>}
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg">{project.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{project.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-3 mb-3">
                    <span>👤 {project.owner}</span>
                    <span>📅 {project.startDate} → {project.targetEndDate}</span>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Progress</span><span>{project.progress}%</span></div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${project.progress}%`, background: project.progress === 100 ? '#28a745' : '#0366d6' }} />
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Budget: {project.spentHours}h / {project.budgetHours}h ({bp}%)</span>
                    <span className={bp > 90 ? 'text-red-600 font-semibold' : ''}>{project.budgetHours - project.spentHours}h remaining</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right sidebar */}
        <div className="space-y-6">
          {/* Active Sprint */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Active Sprint</h2>
              <button onClick={onShowSprints} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Manage Sprints</button>
            </div>
            {data.sprint ? (() => {
              const progress = data.sprint.capacityHours > 0 ? Math.round((data.sprint.completedHours / data.sprint.capacityHours) * 100) : 0;
              return (
                <div className="bg-white rounded-lg shadow border border-blue-400 ring-1 ring-blue-100 p-5">
                  <div className="font-semibold text-gray-900 text-lg mb-1">{data.sprint.name}</div>
                  {data.sprint.goal && <p className="text-sm text-gray-600 mb-3">{data.sprint.goal}</p>}
                  <div className="text-xs text-gray-500 mb-3">{data.sprint.startDate} → {data.sprint.endDate}</div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Completed</span><span>{data.sprint.completedHours}h / {data.sprint.capacityHours}h ({progress}%)</span></div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full" style={{ width: `${progress}%` }} /></div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Committed</span><span>{data.sprint.committedHours}h / {data.sprint.capacityHours}h</span></div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-yellow-500 rounded-full" style={{ width: `${Math.min(Math.round((data.sprint.committedHours / data.sprint.capacityHours) * 100), 100)}%` }} /></div>
                    </div>
                  </div>
                </div>
              );
            })() : (
              <div className="bg-white rounded-lg shadow border border-gray-200 p-6 text-center">
                <p className="text-gray-500 text-sm mb-3">No active sprint</p>
                <button onClick={onShowSprints} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Manage Sprints</button>
              </div>
            )}
          </div>

          {/* Milestones Timeline */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Milestones</h2>
              <button onClick={onShowMilestones} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Manage Milestones</button>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <div className="space-y-0">
                {data.milestones.map((ms, i) => {
                  const icon = getMilestoneIcon(ms.status);
                  const hs = getHealthBadgeStyle(ms.health_status || (ms.status === 'completed' ? 'completed' : 'on-track'));
                  return (
                    <div key={ms.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${ms.is_critical_path ? 'ring-2 ring-purple-400' : ''}`} style={{ background: icon.bg, color: icon.color }}>{icon.symbol}</div>
                        {i < data.milestones.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 my-1" />}
                      </div>
                      <div className="pb-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">{ms.title}</span>
                          {ms.is_critical_path === 1 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700">CP</span>}
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${hs.bg} ${hs.text}`}>{hs.label}</span>
                        </div>
                        <div className="text-xs text-gray-500">{ms.projectName}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{ms.status === 'completed' ? `Completed ${ms.completedAt}` : `Due ${ms.dueDate}`}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Sprints Management View ----

const SprintsManagementView = ({ sprints, onBack, onCreateSprint, onUpdateSprint }) => {
  const [showCreate, setShowCreate] = useState(false);

  const handleCreate = async (formData) => {
    await onCreateSprint(formData);
    setShowCreate(false);
  };

  const handleStatusChange = async (sprintId, newStatus) => {
    await onUpdateSprint(sprintId, { status: newStatus });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 text-sm font-medium">
        <ChevronRight size={16} className="rotate-180" /> Back to PlanTrack
      </button>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sprint Management</h1>
          <p className="text-gray-600 mt-1">Create, manage, and track all sprints.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ New Sprint</button>
      </div>

      {(sprints || []).length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 mb-4">No sprints created yet.</p>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ Create First Sprint</button>
        </div>
      ) : (
        <div className="space-y-4">
          {(sprints || []).map(sprint => {
            const progress = sprint.capacityHours > 0 ? Math.round((sprint.completedHours / sprint.capacityHours) * 100) : 0;
            const isActive = sprint.status === 'active';
            return (
              <div key={sprint.id} className={`bg-white rounded-lg shadow border p-5 ${isActive ? 'border-blue-400 ring-1 ring-blue-100' : 'border-gray-200'}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{sprint.name}</h3>
                      <span className="text-xs font-semibold text-gray-500">#{sprint.number}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${isActive ? 'bg-green-100 text-green-700' : sprint.status === 'completed' ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-700'}`}>
                        {sprint.status}
                      </span>
                    </div>
                    {sprint.goal && <p className="text-sm text-gray-600 mb-2">{sprint.goal}</p>}
                    <div className="text-sm text-gray-500 mb-3">{sprint.startDate} → {sprint.endDate}</div>
                    <div className="max-w-md">
                      <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Progress</span><span>{sprint.completedHours}h / {sprint.capacityHours}h ({progress}%)</span></div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${progress}%`, background: isActive ? '#22c55e' : sprint.status === 'completed' ? '#9ca3af' : '#eab308' }} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <select
                      value={sprint.status}
                      onChange={(e) => handleStatusChange(sprint.id, e.target.value)}
                      className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="planned">Planned</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <AddSprintModal onClose={() => setShowCreate(false)} onSubmit={handleCreate} />}
    </div>
  );
};

// ============================================================================
// TASK MANAGEMENT VIEW (Manager/Admin cross-module view)
// ============================================================================

const SOURCE_META = {
  sprint:     { label: 'Sprint Board', bg: '#dbeafe', color: '#1e40af' },
  quicktrack: { label: 'QuickTrack',   bg: '#ede9fe', color: '#5b21b6' },
};

const SPRINT_STATUSES  = ['backlog', 'ready', 'in_progress', 'review', 'completed', 'blocked', 'cancelled'];
const QUICK_STATUSES   = ['open', 'in_progress', 'done', 'blocked'];
const ALL_STATUSES     = [...new Set([...SPRINT_STATUSES, ...QUICK_STATUSES])];
const PRIORITIES       = ['P1', 'P2', 'P3', 'P4', 'high', 'medium', 'low'];

const TaskManagementView = ({ data, filters, onFilterChange, onBulkUpdate, onRefresh }) => {
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkStatus, setBulkStatus]   = useState('');
  const [applying, setApplying]       = useState(false);

  const tasks     = data?.tasks     || [];
  const summary   = data?.summary   || {};
  const assignees = data?.assignees || [];
  const projects  = data?.projects  || [];

  const taskKey = (t) => `${t.source}:${t.id}`;

  const toggleOne = (t) => setSelected(prev => {
    const s = new Set(prev);
    s.has(taskKey(t)) ? s.delete(taskKey(t)) : s.add(taskKey(t));
    return s;
  });
  const toggleAll = () => setSelected(
    selected.size === tasks.length ? new Set() : new Set(tasks.map(taskKey))
  );

  const handleApplyBulk = async () => {
    if (!bulkAssignee && !bulkStatus) return;
    const selectedTasks = [...selected].map(k => {
      const idx = k.indexOf(':');
      return { source: k.slice(0, idx), id: k.slice(idx + 1) };
    });
    const updates = {};
    if (bulkAssignee) updates.assignee = bulkAssignee === '__unassigned__' ? '' : bulkAssignee;
    if (bulkStatus)   updates.status   = bulkStatus;
    setApplying(true);
    await onBulkUpdate(selectedTasks, updates);
    setSelected(new Set()); setBulkAssignee(''); setBulkStatus('');
    setApplying(false);
  };

  const isOverdue = (t) => t.due_date && t.due_date < new Date().toISOString().slice(0, 10)
    && !['completed', 'done', 'cancelled'].includes(t.status);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Task Management</h1>
        <p className="text-gray-600 mt-1">Unified view across Sprint Board and QuickTrack — for managers and admins.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        {[
          { label: 'Total Tasks',   value: summary.total       || 0, color: 'blue'   },
          { label: 'Active',        value: summary.active      || 0, color: 'green',  sub: 'In progress' },
          { label: 'Overdue',       value: summary.overdue     || 0, color: 'red',    sub: 'Past due date' },
          { label: 'Unassigned',    value: summary.unassigned  || 0, color: 'orange', sub: 'No assignee' },
          { label: 'Sprint Board',  value: summary.sprintTasks || 0, color: 'blue',   sub: 'DevOps / PlanTrack' },
          { label: 'QuickTrack',    value: summary.quickTasks  || 0, color: 'purple', sub: 'Ad-hoc tasks' },
        ].map(c => <ReportCard key={c.label} {...c} />)}
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Source toggle */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Source</div>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm flex-wrap">
              {[['', 'All'], ['sprint', 'Sprint'], ['quicktrack', 'QuickTrack']].map(([v, l]) => (
                <button key={v} onClick={() => onFilterChange({ source: v })}
                  className={`px-3 py-1.5 font-medium ${filters.source === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {/* Search */}
          <div className="flex-1 min-w-[160px]">
            <div className="text-xs font-medium text-gray-500 mb-1">Search</div>
            <input value={filters.search || ''} onChange={e => onFilterChange({ search: e.target.value })}
              placeholder="Search tasks…"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
          </div>
          {/* Status */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Status</div>
            <select value={filters.status || ''} onChange={e => onFilterChange({ status: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">All Statuses</option>
              {ALL_STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </div>
          {/* Priority */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Priority</div>
            <select value={filters.priority || ''} onChange={e => onFilterChange({ priority: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">All Priorities</option>
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {/* Assignee */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Assignee</div>
            <select value={filters.assignee || ''} onChange={e => onFilterChange({ assignee: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">All Assignees</option>
              {assignees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          {/* Project */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Project</div>
            <select value={filters.project || ''} onChange={e => onFilterChange({ project: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">All Projects</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
            </select>
          </div>
          {/* Clear */}
          <button onClick={() => onFilterChange({ source: '', status: '', priority: '', assignee: '', project: '', search: '' })}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            Clear
          </button>
          <button onClick={onRefresh} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4">
          <span className="text-sm font-semibold text-blue-800">{selected.size} selected</span>
          <div className="flex-1" />
          <div className="flex items-center gap-2 flex-wrap">
            <select value={bulkAssignee} onChange={e => setBulkAssignee(e.target.value)}
              className="border border-blue-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Reassign to…</option>
              <option value="__unassigned__">— Unassigned —</option>
              {assignees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
              className="border border-blue-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Change status…</option>
              {ALL_STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
            <button onClick={handleApplyBulk} disabled={applying || (!bulkAssignee && !bulkStatus)}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
              {applying ? 'Applying…' : 'Apply'}
            </button>
            <button onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg bg-white">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Task Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 w-10">
                <input type="checkbox" checked={tasks.length > 0 && selected.size === tasks.length}
                  onChange={toggleAll} className="rounded" />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Source</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 min-w-[240px]">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Assignee</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Project</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Due Date</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Est. Hours</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tasks.map(t => {
              const key  = taskKey(t);
              const src  = SOURCE_META[t.source] || SOURCE_META.sprint;
              const over = isOverdue(t);
              return (
                <tr key={key} className={`hover:bg-gray-50 ${selected.has(key) ? 'bg-blue-50' : ''}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.has(key)} onChange={() => toggleOne(t)} className="rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap"
                      style={{ background: src.bg, color: src.color }}>{src.label}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">{t.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 max-w-xs truncate">{t.title}</div>
                    {t.sprint_name && <div className="text-xs text-gray-400">{t.sprint_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 capitalize">{t.type?.replace('_', ' ')}</td>
                  <td className="px-4 py-3">
                    <StatusBadge value={t.status} map={statusColors} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge value={t.priority} map={statusColors} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {t.assignee_name
                      ? <div className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{t.assignee_name.charAt(0)}</div>
                          <span className="truncate max-w-[100px]">{t.assignee_name}</span>
                        </div>
                      : <span className="text-orange-500 text-xs font-medium">Unassigned</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 truncate max-w-[120px]">
                    {t.project_name || t.project_id || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    {t.due_date
                      ? <span className={over ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                          {over ? '⚠ ' : ''}{t.due_date}
                        </span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">
                    {t.estimate_hours > 0 ? `${t.estimate_hours}h` : '—'}
                  </td>
                </tr>
              );
            })}
            {tasks.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-12 text-center text-gray-400">
                No tasks match the current filters.
              </td></tr>
            )}
          </tbody>
        </table>
        {tasks.length > 0 && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
            Showing {tasks.length} task{tasks.length !== 1 ? 's' : ''}
            {selected.size > 0 && ` · ${selected.size} selected`}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// REPORTS VIEW
// ============================================================================

const downloadCSV = (filename, headers, rows) => {
  const escape = v => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const ReportCard = ({ label, value, sub, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    pink: 'bg-pink-50 border-pink-200 text-pink-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };
  const cls = colors[color] || colors.blue;
  return (
    <div className={`rounded-lg border p-5 ${cls.split(' ').slice(0,2).join(' ')}`}>
      <div className="text-sm text-gray-600 mb-1">{label}</div>
      <div className={`text-3xl font-bold ${cls.split(' ')[2]}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
};

const StatusBadge = ({ value, map }) => {
  const cfg = map[value] || { bg: '#e5e7eb', color: '#374151' };
  return <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: cfg.bg, color: cfg.color }}>{value}</span>;
};

const statusColors = {
  active: { bg: '#dcffe4', color: '#166534' }, completed: { bg: '#dbeafe', color: '#1e40af' },
  'on-hold': { bg: '#fef9c3', color: '#854d0e' }, cancelled: { bg: '#fee2e2', color: '#991b1b' },
  planning: { bg: '#ede9fe', color: '#5b21b6' }, backlog: { bg: '#f3f4f6', color: '#374151' },
  in_progress: { bg: '#dbeafe', color: '#1e40af' }, review: { bg: '#fef9c3', color: '#854d0e' },
  blocked: { bg: '#fee2e2', color: '#991b1b' }, open: { bg: '#fee2e2', color: '#991b1b' },
  approved: { bg: '#dcffe4', color: '#166534' }, pending: { bg: '#fef9c3', color: '#854d0e' },
  rejected: { bg: '#fee2e2', color: '#991b1b' }, draft: { bg: '#f3f4f6', color: '#374151' },
  submitted: { bg: '#ede9fe', color: '#5b21b6' }, 'in-progress': { bg: '#dbeafe', color: '#1e40af' },
  pass: { bg: '#dcffe4', color: '#166534' }, fail: { bg: '#fee2e2', color: '#991b1b' },
  resolved: { bg: '#dcffe4', color: '#166534' }, closed: { bg: '#f3f4f6', color: '#374151' },
  critical: { bg: '#fee2e2', color: '#991b1b' }, high: { bg: '#fed7aa', color: '#9a3412' },
  medium: { bg: '#fef9c3', color: '#854d0e' }, low: { bg: '#dcffe4', color: '#166534' },
  P1: { bg: '#fee2e2', color: '#991b1b' }, P2: { bg: '#fed7aa', color: '#9a3412' },
  P3: { bg: '#fef9c3', color: '#854d0e' }, P4: { bg: '#dcffe4', color: '#166534' },
};

const ExportBtn = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 font-medium">
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
    Export CSV
  </button>
);

const ReportsView = ({ data }) => {
  const [tab, setTab] = useState('overview');
  const d = data || {};
  const s = d.summary || {};

  const tabs = [
    { id: 'overview',   label: 'Overview' },
    { id: 'projects',   label: 'Projects' },
    { id: 'sprints',    label: 'Sprints & Velocity' },
    { id: 'tasks',      label: 'Tasks' },
    { id: 'time',       label: 'Time & Leave' },
    { id: 'quality',    label: 'Quality' },
  ];

  // ---- CSV exports ----
  const exportProjects = () => {
    downloadCSV('projects_report.csv',
      ['Project ID','Name','Status','Priority','Health','Progress %','Budget Hours','Spent Hours','Tasks','Completed Tasks','Estimated Hours','Milestones','Start Date','End Date'],
      (d.projects || []).map(p => [p.project_id, p.name, p.status, p.priority, p.health_status, p.progress, p.budget_hours, p.spent_hours, p.task_count, p.completed_tasks, p.estimated_hours, p.milestone_count, p.start_date, p.end_date])
    );
  };
  const exportSprints = () => {
    downloadCSV('sprints_report.csv',
      ['Sprint','Name','Status','Start Date','End Date','Points Planned','Points Completed','Velocity','Capacity Hours','Committed Hours','Completed Hours'],
      (d.sprints || []).map(sp => [sp.sprint_number, sp.name, sp.status, sp.start_date, sp.end_date, sp.story_points_planned, sp.story_points_completed, sp.velocity, sp.capacity_hours, sp.committed_hours, sp.completed_hours])
    );
  };
  const exportTasks = () => {
    downloadCSV('tasks_by_assignee.csv',
      ['Assignee','Total Tasks','Completed','Estimated Hours','Actual Hours'],
      (d.tasks?.byAssignee || []).map(t => [t.assignee, t.total, t.completed, t.estimated_hours, t.actual_hours])
    );
  };
  const exportTime = () => {
    downloadCSV('timesheet_report.csv',
      ['User','Total Hours','Billable Hours','Leave Hours','Weeks Logged'],
      (d.timesheets?.byUser || []).map(t => [t.user_name, t.total_hours, t.billable_hours, t.leave_hours, t.weeks_logged])
    );
  };
  const exportLeave = () => {
    downloadCSV('leave_report.csv',
      ['User','Approved Days','Pending Days','Rejected Days'],
      (d.leaves?.byUser || []).map(l => [l.user_name, l.approved_days, l.pending_days, l.rejected_days])
    );
  };
  const exportQuality = () => {
    downloadCSV('bugs_report.csv',
      ['Bug ID','Title','Severity','Status','Environment','Assignee','Created'],
      (d.quality?.recentBugs || []).map(b => [b.bug_id, b.title, b.severity, b.status, b.environment, b.assignee, b.created_at])
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
        <p className="text-gray-600 mt-1">Generate, view, and export comprehensive system reports.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-6 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <ReportCard label="Total Projects"    value={s.totalProjects}    color="blue"   sub="All projects" />
            <ReportCard label="Active Sprints"    value={s.activeSprints}    color="green"  sub="Currently running" />
            <ReportCard label="Open Bugs"         value={s.openBugs}         color="red"    sub="Requiring attention" />
            <ReportCard label="Pending Leave"     value={s.pendingLeave}     color="orange" sub="Awaiting approval" />
            <ReportCard label="Hours This Month"  value={`${s.hoursThisMonth}h`} color="purple" sub="Last 30 days" />
            <ReportCard label="Active Users"      value={s.activeUsers}      color="gray"   sub="In the system" />
            <ReportCard label="Open Quick Tasks"  value={s.openQuickTasks}   color="pink"   sub="Unresolved" />
          </div>

          {/* Task status + Quick tasks side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Tasks by Status</h3>
              <div className="space-y-2">
                {(d.tasks?.byStatus || []).map(row => {
                  const total = (d.tasks?.byStatus || []).reduce((s, r) => s + r.count, 0) || 1;
                  const pct = Math.round((row.count / total) * 100);
                  return (
                    <div key={row.status} className="flex items-center gap-3">
                      <div className="w-24 text-xs text-right capitalize text-gray-600">{row.status?.replace('_', ' ')}</div>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: statusColors[row.status]?.color || '#6b7280' }} />
                      </div>
                      <div className="w-12 text-xs text-gray-700 font-semibold">{row.count} <span className="text-gray-400">({pct}%)</span></div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Bugs by Severity</h3>
              <div className="space-y-2">
                {(d.quality?.bugsBySeverity || []).map(row => {
                  const total = (d.quality?.bugsBySeverity || []).reduce((s, r) => s + r.count, 0) || 1;
                  const pct = Math.round((row.count / total) * 100);
                  return (
                    <div key={row.severity} className="flex items-center gap-3">
                      <div className="w-16 text-xs text-right capitalize text-gray-600">{row.severity}</div>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: statusColors[row.severity]?.color || '#6b7280' }} />
                      </div>
                      <div className="w-12 text-xs text-gray-700 font-semibold">{row.count} <span className="text-gray-400">({pct}%)</span></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Weekly hours trend */}
          {(d.timesheets?.weekly || []).length > 0 && (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Weekly Hours (Last 4 Weeks)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-200">
                    <th className="pb-2 text-left text-xs font-semibold text-gray-600">Week Starting</th>
                    <th className="pb-2 text-right text-xs font-semibold text-gray-600">Total Hours</th>
                    <th className="pb-2 text-right text-xs font-semibold text-gray-600">Billable Hours</th>
                    <th className="pb-2 text-right text-xs font-semibold text-gray-600">Billable %</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {d.timesheets.weekly.map(w => {
                      const pct = w.total_hours > 0 ? Math.round((w.billable_hours / w.total_hours) * 100) : 0;
                      return (
                        <tr key={w.week_start_date} className="hover:bg-gray-50">
                          <td className="py-2 text-gray-800">{w.week_start_date}</td>
                          <td className="py-2 text-right font-semibold text-blue-700">{w.total_hours}h</td>
                          <td className="py-2 text-right text-green-700">{w.billable_hours}h</td>
                          <td className="py-2 text-right text-gray-600">{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PROJECTS ── */}
      {tab === 'projects' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Project Status Report</h2>
            <ExportBtn onClick={exportProjects} />
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Project','Status','Priority','Health','Progress','Budget','Spent','Tasks','Milestones'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.projects || []).map(p => (
                  <tr key={p.project_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{p.name}</div>
                      <div className="text-xs text-gray-400">{p.project_id}</div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge value={p.status} map={statusColors} /></td>
                    <td className="px-4 py-3"><StatusBadge value={p.priority} map={statusColors} /></td>
                    <td className="px-4 py-3"><StatusBadge value={p.health_status || 'on-track'} map={statusColors} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-blue-500" style={{ width: `${p.progress || 0}%` }} />
                        </div>
                        <span className="text-xs text-gray-600">{p.progress || 0}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{p.budget_hours || 0}h</td>
                    <td className="px-4 py-3">
                      <span className={p.spent_hours > p.budget_hours && p.budget_hours > 0 ? 'text-red-600 font-semibold' : 'text-gray-700'}>{p.spent_hours || 0}h</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{p.completed_tasks || 0}/{p.task_count || 0}</td>
                    <td className="px-4 py-3 text-gray-700">{p.milestone_count || 0}</td>
                  </tr>
                ))}
                {(!d.projects || d.projects.length === 0) && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No project data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── SPRINTS ── */}
      {tab === 'sprints' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Sprint Velocity Report</h2>
            <ExportBtn onClick={exportSprints} />
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Sprint','Status','Dates','Story Points','Velocity','Capacity','Committed','Completed'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.sprints || []).map(sp => {
                  const completion = sp.story_points_planned > 0 ? Math.round((sp.story_points_completed / sp.story_points_planned) * 100) : 0;
                  return (
                    <tr key={sp.sprint_number} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">Sprint {sp.sprint_number}</div>
                        <div className="text-xs text-gray-400">{sp.name}</div>
                      </td>
                      <td className="px-4 py-3"><StatusBadge value={sp.status} map={statusColors} /></td>
                      <td className="px-4 py-3 text-xs text-gray-600">{sp.start_date} → {sp.end_date}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-semibold text-gray-900">{sp.story_points_completed}/{sp.story_points_planned}</div>
                        <div className="text-xs text-gray-500">{completion}% done</div>
                      </td>
                      <td className="px-4 py-3 text-blue-700 font-bold">{sp.velocity || 0}</td>
                      <td className="px-4 py-3 text-gray-700">{sp.capacity_hours}h</td>
                      <td className="px-4 py-3 text-gray-700">{sp.committed_hours}h</td>
                      <td className="px-4 py-3 text-green-700 font-semibold">{sp.completed_hours}h</td>
                    </tr>
                  );
                })}
                {(!d.sprints || d.sprints.length === 0) && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No sprint data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TASKS ── */}
      {tab === 'tasks' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Task Analysis Report</h2>
            <ExportBtn onClick={exportTasks} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* By Status */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Tasks by Status</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Status</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.tasks?.byStatus || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.tasks?.byStatus || []).map(row => (
                      <tr key={row.status} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.status} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            {/* By Priority */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Tasks by Priority</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Priority</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.tasks?.byPriority || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.tasks?.byPriority || []).map(row => (
                      <tr key={row.priority} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.priority} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
          {/* By Assignee */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Tasks by Assignee</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Assignee','Total','Completed','Completion %','Estimated Hours','Actual Hours'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.tasks?.byAssignee || []).map(row => {
                  const pct = row.total > 0 ? Math.round((row.completed / row.total) * 100) : 0;
                  return (
                    <tr key={row.assignee} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">{(row.assignee || '?').charAt(0)}</div>
                          <span className="font-medium text-gray-900">{row.assignee}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{row.total}</td>
                      <td className="px-4 py-3 text-green-700">{row.completed}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct >= 75 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444' }} />
                          </div>
                          <span className="text-xs text-gray-600">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-purple-700">{row.estimated_hours || 0}h</td>
                      <td className="px-4 py-3 text-blue-700">{row.actual_hours || 0}h</td>
                    </tr>
                  );
                })}
                {(!d.tasks?.byAssignee || d.tasks.byAssignee.length === 0) && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No task data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TIME & LEAVE ── */}
      {tab === 'time' && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Timesheet by User */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-semibold text-gray-900">Hours by Team Member (Last 4 Weeks)</h2>
                <ExportBtn onClick={exportTime} />
              </div>
              <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Member','Total','Billable','Leave','Weeks'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(d.timesheets?.byUser || []).map(row => (
                      <tr key={row.user_name} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{row.user_name}</td>
                        <td className="px-4 py-3 font-semibold text-blue-700">{row.total_hours}h</td>
                        <td className="px-4 py-3 text-green-700">{row.billable_hours}h</td>
                        <td className="px-4 py-3 text-pink-600">{row.leave_hours > 0 ? `${row.leave_hours}h` : '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{row.weeks_logged}w</td>
                      </tr>
                    ))}
                    {(!d.timesheets?.byUser || d.timesheets.byUser.length === 0) && (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No timesheet data.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Hours by Project */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Hours by Project</h2>
              <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Project','Hours','Billable','Billable %'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(d.timesheets?.byProject || []).map(row => {
                      const pct = row.hours > 0 ? Math.round((row.billable_hours / row.hours) * 100) : 0;
                      return (
                        <tr key={row.project_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{row.project_id}</td>
                          <td className="px-4 py-3 font-semibold text-blue-700">{row.hours}h</td>
                          <td className="px-4 py-3 text-green-700">{row.billable_hours}h</td>
                          <td className="px-4 py-3 text-gray-600">{pct}%</td>
                        </tr>
                      );
                    })}
                    {(!d.timesheets?.byProject || d.timesheets.byProject.length === 0) && (
                      <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No project hours data.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Leave Report */}
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Leave Report</h2>
            <ExportBtn onClick={exportLeave} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Leave by User */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600">By Team Member</div>
              <table className="w-full text-sm">
                <thead><tr>
                  {['Member','Approved Days','Pending','Rejected'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(d.leaves?.byUser || []).map(row => (
                    <tr key={row.user_name} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{row.user_name}</td>
                      <td className="px-4 py-3 text-green-700 font-semibold">{row.approved_days}d</td>
                      <td className="px-4 py-3 text-yellow-700">{row.pending_days > 0 ? `${row.pending_days}d` : '—'}</td>
                      <td className="px-4 py-3 text-red-600">{row.rejected_days > 0 ? `${row.rejected_days}d` : '—'}</td>
                    </tr>
                  ))}
                  {(!d.leaves?.byUser || d.leaves.byUser.length === 0) && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No leave data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Leave Balances */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600">Leave Balances</div>
              <table className="w-full text-sm">
                <thead><tr>
                  {['Member','Vacation','Sick','Personal'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(d.leaves?.balances || []).map(row => (
                    <tr key={row.user_name} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{row.user_name}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-blue-700">{row.vacation_remaining}</span>
                        <span className="text-gray-400 text-xs"> /{row.vacation_total}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-red-600">{row.sick_remaining}</span>
                        <span className="text-gray-400 text-xs"> /{row.sick_total}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-semibold text-purple-700">{row.personal_remaining}</span>
                        <span className="text-gray-400 text-xs"> /{row.personal_total}</span>
                      </td>
                    </tr>
                  ))}
                  {(!d.leaves?.balances || d.leaves.balances.length === 0) && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No balance data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── QUALITY ── */}
      {tab === 'quality' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Quality & Bug Report</h2>
            <ExportBtn onClick={exportQuality} />
          </div>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {(() => {
              const bugs = d.quality?.bugsBySeverity || [];
              const totalBugs = bugs.reduce((s, r) => s + r.count, 0);
              const critical = bugs.find(r => r.severity === 'critical')?.count || 0;
              const high = bugs.find(r => r.severity === 'high')?.count || 0;
              const tc = d.quality?.testCases || [];
              const totalTC = tc.reduce((s, r) => s + r.count, 0);
              const passed = tc.find(r => r.status === 'pass' || r.status === 'passed')?.count || 0;
              const coverage = totalTC > 0 ? Math.round((passed / totalTC) * 100) : 0;
              return [
                <ReportCard key="total" label="Total Bugs"    value={totalBugs} color="red"    sub="All bugs" />,
                <ReportCard key="crit"  label="Critical Bugs" value={critical}  color="red"    sub="Needs immediate action" />,
                <ReportCard key="high"  label="High Severity" value={high}      color="orange" sub="High priority" />,
                <ReportCard key="cov"   label="Test Pass Rate" value={`${coverage}%`} color="green" sub={`${passed}/${totalTC} test cases`} />,
              ];
            })()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Bugs by severity */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Bugs by Severity</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Severity</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.quality?.bugsBySeverity || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.quality?.bugsBySeverity || []).map(row => (
                      <tr key={row.severity} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.severity} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            {/* Test Cases */}
            <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Test Case Results</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-600">Status</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">Count</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-600">%</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const total = (d.quality?.testCases || []).reduce((s, r) => s + r.count, 0) || 1;
                    return (d.quality?.testCases || []).map(row => (
                      <tr key={row.status} className="hover:bg-gray-50">
                        <td className="py-2"><StatusBadge value={row.status} map={statusColors} /></td>
                        <td className="py-2 text-right font-semibold text-gray-900">{row.count}</td>
                        <td className="py-2 text-right text-gray-500">{Math.round((row.count/total)*100)}%</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Bugs table */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Bug List (by Severity)</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Bug ID','Title','Severity','Status','Environment','Assignee','Reported'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(d.quality?.recentBugs || []).map(b => (
                  <tr key={b.bug_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{b.bug_id}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">{b.title}</td>
                    <td className="px-4 py-3"><StatusBadge value={b.severity} map={statusColors} /></td>
                    <td className="px-4 py-3"><StatusBadge value={b.status} map={statusColors} /></td>
                    <td className="px-4 py-3 text-gray-600">{b.environment || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{b.assignee || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{b.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
                {(!d.quality?.recentBugs || d.quality.recentBugs.length === 0) && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No bug data available.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- TimeBoard Components ----

const getWeekDates = (weekStart) => {
  const dates = [];
  const start = new Date(weekStart + 'T00:00:00');
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
};

const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const categoryLabels = { project: 'Project Work', adhoc: 'Ad-hoc', meeting: 'Meetings', admin: 'Admin', training: 'Training', leave: 'Leave' };
const categoryColors = { project: '#0366d6', adhoc: '#6f42c1', meeting: '#e36209', admin: '#586069', training: '#fd7e14', leave: '#e83e8c' };

const TimesheetGrid = ({ entries, weekDates, leaveDays, onAddEntry }) => {
  const categories = ['project', 'adhoc', 'meeting', 'admin', 'training', 'leave'];

  // Build a grid: category -> date -> hours
  const grid = {};
  categories.forEach(cat => { grid[cat] = {}; weekDates.forEach(d => { grid[cat][d] = 0; }); });

  entries.forEach(e => {
    if (grid[e.category] && grid[e.category][e.entryDate] !== undefined) {
      grid[e.category][e.entryDate] += e.hours;
    }
  });

  // Add leave days from approved requests
  if (leaveDays) {
    leaveDays.forEach(leave => {
      const start = new Date(leave.startDate + 'T00:00:00');
      const end = new Date(leave.endDate + 'T00:00:00');
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        if (grid.leave[dateStr] !== undefined) {
          grid.leave[dateStr] = 8;
        }
      }
    });
  }

  // Daily totals
  const dailyTotals = {};
  weekDates.forEach(d => {
    dailyTotals[d] = categories.reduce((sum, cat) => sum + (grid[cat][d] || 0), 0);
  });

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto mb-6">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 w-36">Category</th>
            {weekDates.map((d, i) => (
              <th key={d} className="px-3 py-3 text-center text-xs font-semibold text-gray-600 min-w-[70px]">
                <div>{dayLabels[i]}</div>
                <div className="text-gray-400 font-normal">{d.slice(5)}</div>
              </th>
            ))}
            <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {categories.map(cat => {
            const catTotal = weekDates.reduce((sum, d) => sum + (grid[cat][d] || 0), 0);
            const isLeave = cat === 'leave';
            return (
              <tr key={cat} className={isLeave ? 'bg-pink-50' : 'hover:bg-gray-50'}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: categoryColors[cat] }} />
                    <span className="text-sm font-medium text-gray-700">{categoryLabels[cat]}</span>
                  </div>
                </td>
                {weekDates.map(d => {
                  const hours = grid[cat][d] || 0;
                  return (
                    <td key={d} className="px-3 py-3 text-center">
                      {hours > 0 ? (
                        <span className={`text-sm font-semibold ${isLeave ? 'text-pink-600' : 'text-gray-900'}`}>{hours}</span>
                      ) : (
                        !isLeave ? (
                          <button onClick={() => onAddEntry(d, cat)} className="text-xs text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded w-8 h-8 flex items-center justify-center mx-auto">+</button>
                        ) : <span className="text-gray-300">-</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-center text-sm font-bold text-gray-900">{catTotal > 0 ? catTotal : '-'}</td>
              </tr>
            );
          })}
          {/* Daily totals row */}
          <tr className="bg-gray-50 font-semibold">
            <td className="px-4 py-3 text-sm text-gray-700">Daily Total</td>
            {weekDates.map(d => {
              const total = dailyTotals[d];
              return (
                <td key={d} className="px-3 py-3 text-center">
                  <span className={`text-sm font-bold ${total === 0 ? 'text-gray-300' : total >= 8 ? 'text-green-600' : total > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
                    {total > 0 ? total : '-'}
                  </span>
                </td>
              );
            })}
            <td className="px-3 py-3 text-center text-sm font-bold text-blue-700">
              {Object.values(dailyTotals).reduce((a, b) => a + b, 0)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const TimeEntryModal = ({ date, category, onClose, onSubmit }) => {
  const [form, setForm] = useState({ hours: '', category: category || 'project', projectId: '', description: '', isBillable: true });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit({ ...form, entryDate: date, hours: parseFloat(form.hours) || 0 }); };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Add Time Entry — {date}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hours *</label>
              <input required type="number" step="0.5" min="0.5" max="24" value={form.hours} onChange={e => setForm({...form, hours: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="project">Project Work</option>
                <option value="adhoc">Ad-hoc</option>
                <option value="meeting">Meeting</option>
                <option value="admin">Admin</option>
                <option value="training">Training</option>
              </select>
            </div>
          </div>
          {form.category === 'project' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={form.projectId} onChange={e => setForm({...form, projectId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select project...</option>
                <option value="PROJ-001">Settlement Vault API Modernization</option>
                <option value="PROJ-002">Negotiator Worklist Dashboard</option>
                <option value="PROJ-003">Customer Portal Redesign</option>
                <option value="PROJ-004">Data Warehouse Migration</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="billable" checked={form.isBillable} onChange={e => setForm({...form, isBillable: e.target.checked})} className="rounded" />
            <label htmlFor="billable" className="text-sm text-gray-700">Billable</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Add Entry</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TimeBoardModule = ({ data, weekStart, onWeekChange, onAddEntry, onDeleteEntry, onSubmitTimesheet, onApproveTimesheet }) => {
  const weekDates = getWeekDates(weekStart);
  const weekEnd = weekDates[6];

  const formatWeekLabel = (start, end) => {
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const totalHours = data?.myTimesheet?.totalHours || 0;
  const billableHours = data?.myTimesheet?.billableHours || 0;
  const leaveHours = data?.myTimesheet?.leaveHours || 0;        // calculated from leave_requests
  const accountedHours = data?.myTimesheet?.accountedHours || totalHours;
  const availableHours = data?.myTimesheet?.availableHours ?? (40 - leaveHours);
  const estimatedHours = data?.myTimesheet?.estimatedHours || 0;
  const billablePercent = totalHours > 0 ? Math.round((billableHours / totalHours) * 100) : 0;
  const timesheetStatus = data?.myTimesheet?.status || 'draft';
  const projectComparison = data?.projectComparison || [];

  // Count leave days this week from approved requests
  const leaveDaysThisWeek = (data?.leaveDays || []).filter(l => l.userId === 2);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">TimeBoard</h1>
          <p className="text-gray-600 mt-1">Time tracking & timesheet management</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Timesheet status */}
          {timesheetStatus === 'draft' && data?.myTimesheet && (
            <button onClick={() => onSubmitTimesheet(data.myTimesheet.id)} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 font-medium">Submit for Approval</button>
          )}
          {timesheetStatus === 'submitted' && (
            <span className="px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg text-sm font-semibold">Awaiting Approval</span>
          )}
          {timesheetStatus === 'approved' && (
            <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-semibold">Approved</span>
          )}
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => onWeekChange(-1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
          <ChevronRight size={20} className="rotate-180" />
        </button>
        <div className="text-lg font-semibold text-gray-900">{formatWeekLabel(weekStart, weekEnd)}</div>
        <button onClick={() => onWeekChange(1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-5">
          <div className="text-sm text-gray-600 mb-1">Estimated</div>
          <div className="text-3xl font-bold text-purple-700">{estimatedHours}h</div>
          <div className="text-xs text-gray-500 mt-1">Active task estimates</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
          <div className="text-sm text-gray-600 mb-1">Actual Logged</div>
          <div className="text-3xl font-bold text-blue-700">{totalHours}h</div>
          <div className="text-xs text-gray-500 mt-1">{billablePercent}% billable</div>
        </div>
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-5">
          <div className="text-sm text-gray-600 mb-1">Leave Hours</div>
          <div className="text-3xl font-bold text-pink-700">{leaveHours}h</div>
          <div className="text-xs text-gray-500 mt-1">From approved leave</div>
        </div>
        <div className={`rounded-lg p-5 border ${accountedHours >= 40 ? 'bg-green-50 border-green-200' : accountedHours >= 32 ? 'bg-yellow-50 border-yellow-200' : 'bg-orange-50 border-orange-200'}`}>
          <div className="text-sm text-gray-600 mb-1">Accounted</div>
          <div className={`text-3xl font-bold ${accountedHours >= 40 ? 'text-green-700' : accountedHours >= 32 ? 'text-yellow-700' : 'text-orange-700'}`}>{accountedHours}h</div>
          <div className="text-xs text-gray-500 mt-1">Logged + leave / {availableHours}h avail</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
          <div className="text-sm text-gray-600 mb-1">Status</div>
          <div className={`text-xl font-bold capitalize ${timesheetStatus === 'approved' ? 'text-green-700' : timesheetStatus === 'submitted' ? 'text-yellow-700' : 'text-gray-700'}`}>{timesheetStatus}</div>
          <div className="text-xs text-gray-500 mt-1">{billableHours}h billable</div>
        </div>
      </div>

      {/* Timesheet Grid */}
      <h2 className="text-xl font-semibold text-gray-900 mb-4">My Timesheet</h2>
      <TimesheetGrid
        entries={data?.entries || []}
        weekDates={weekDates}
        leaveDays={leaveDaysThisWeek}
        onAddEntry={onAddEntry}
        onDeleteEntry={onDeleteEntry}
      />

      {/* Estimate vs Actual by Project */}
      {projectComparison.length > 0 && (
        <>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Estimate vs Actual — by Project</h2>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Project</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Estimated</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actual (This Week)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Variance</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {projectComparison.map(p => {
                  const est = p.estimated_hours || 0;
                  const act = p.actual_hours || 0;
                  const variance = act - est;
                  const pct = est > 0 ? Math.min(100, Math.round((act / est) * 100)) : null;
                  return (
                    <tr key={p.project_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{p.project_name}</div>
                        <div className="text-xs text-gray-400">{p.project_id}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-purple-700">{est}h</td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-blue-700">{act}h</td>
                      <td className="px-4 py-3 text-right text-sm font-semibold">
                        {est > 0 ? (
                          <span className={variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-gray-500'}>
                            {variance > 0 ? '+' : ''}{variance.toFixed(1)}h
                          </span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct !== null ? (
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct > 100 ? '#dc2626' : pct > 75 ? '#f59e0b' : '#10b981' }} />
                            </div>
                            <span className="text-xs text-gray-600">{pct}%</span>
                          </div>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Team Timesheets */}
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Team Overview</h2>
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Team Member</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Estimated</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actual Logged</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Leave</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Accounted</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Billable</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(data?.teamTimesheets || []).map(ts => {
              const ss = getStatusStyle(ts.status === 'draft' ? 'backlog' : ts.status === 'submitted' ? 'review' : ts.status === 'approved' ? 'completed' : 'blocked');
              const tsEst = ts.estimatedHours || 0;
              const tsAct = ts.totalHours || 0;
              const tsLeave = ts.leaveHours || 0;
              const tsAccounted = ts.accountedHours ?? (tsAct + tsLeave);
              const tsVariance = tsAct - tsEst;
              return (
                <tr key={ts.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">{ts.userName.charAt(0)}</div>
                      <span className="text-sm font-medium text-gray-900">{ts.userName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-purple-700">{tsEst}h</td>
                  <td className="px-4 py-3 text-right">
                    <div className="text-sm font-semibold text-blue-700">{tsAct}h</div>
                    {tsEst > 0 && (
                      <div className={`text-xs font-medium ${tsVariance > 0 ? 'text-red-500' : tsVariance < 0 ? 'text-green-500' : 'text-gray-400'}`}>
                        {tsVariance > 0 ? '+' : ''}{tsVariance.toFixed(1)}h
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-pink-600">{tsLeave > 0 ? `${tsLeave}h` : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className={`text-sm font-semibold ${tsAccounted >= 40 ? 'text-green-700' : tsAccounted >= 32 ? 'text-yellow-700' : 'text-orange-700'}`}>{tsAccounted}h</div>
                    <div className="text-xs text-gray-400">{tsLeave > 0 ? `${tsAct}w + ${tsLeave}l` : 'of 40h'}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">{ts.billableHours}h</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{ts.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    {ts.status === 'submitted' && (
                      <button onClick={() => onApproveTimesheet(ts.id)} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">Approve</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(!data?.teamTimesheets || data.teamTimesheets.length === 0) && (
          <div className="p-8 text-center text-gray-400">No timesheets for this week.</div>
        )}
      </div>
    </div>
  );
};

// ---- LeaveBoard Components ----

const getLeaveTypeBadge = (type) => {
  const badges = {
    vacation: { color: '#0366d6', bg: '#d4e4ff', label: 'Vacation', icon: '🏖️' },
    sick: { color: '#dc3545', bg: '#ffe5e9', label: 'Sick', icon: '🤒' },
    personal: { color: '#6f42c1', bg: '#f3e8ff', label: 'Personal', icon: '👤' },
    wfh: { color: '#28a745', bg: '#dcffe4', label: 'WFH', icon: '🏠' }
  };
  return badges[type] || { color: '#586069', bg: '#f6f8fa', label: type, icon: '📅' };
};

const getLeaveStatusBadge = (status) => {
  const badges = {
    pending: { color: '#e36209', bg: '#fff5cc', label: 'Pending' },
    approved: { color: '#28a745', bg: '#dcffe4', label: 'Approved' },
    rejected: { color: '#dc3545', bg: '#ffe5e9', label: 'Rejected' },
    cancelled: { color: '#586069', bg: '#f6f8fa', label: 'Cancelled' }
  };
  return badges[status] || { color: '#586069', bg: '#f6f8fa', label: status };
};

const LeaveBalanceCards = ({ balance }) => {
  const cards = [
    { label: 'Vacation', total: balance.vacation_total, used: balance.vacation_used, remaining: balance.vacation_remaining, color: '#0366d6', icon: '🏖️' },
    { label: 'Sick Leave', total: balance.sick_total, used: balance.sick_used, remaining: balance.sick_remaining, color: '#dc3545', icon: '🤒' },
    { label: 'Personal', total: balance.personal_total, used: balance.personal_used, remaining: balance.personal_remaining, color: '#6f42c1', icon: '👤' }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {cards.map(card => {
        const usedPercent = card.total > 0 ? Math.round((card.used / card.total) * 100) : 0;
        return (
          <div key={card.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-gray-600">{card.icon} {card.label}</div>
              <div className="text-2xl font-bold" style={{ color: card.color }}>{card.remaining}</div>
            </div>
            <div className="text-xs text-gray-500 mb-2">{card.used} used of {card.total} days</div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${usedPercent}%`, background: card.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RequestLeaveModal = ({ onClose, onSubmit, balance }) => {
  const [form, setForm] = useState({ leaveType: 'vacation', startDate: '', endDate: '', reason: '' });

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    let days = 0;
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) days++;
    }
    return days;
  };

  const totalDays = calculateDays(form.startDate, form.endDate);
  const typeKey = form.leaveType === 'sick' ? 'sick' : form.leaveType === 'personal' ? 'personal' : 'vacation';
  const remaining = balance?.[`${typeKey}_remaining`] || 0;
  const insufficient = totalDays > remaining;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (insufficient) return;
    onSubmit({ ...form, totalDays });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Request Leave</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
            <select value={form.leaveType} onChange={e => setForm({...form, leaveType: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="vacation">Vacation ({balance?.vacation_remaining || 0} days remaining)</option>
              <option value="sick">Sick Leave ({balance?.sick_remaining || 0} days remaining)</option>
              <option value="personal">Personal ({balance?.personal_remaining || 0} days remaining)</option>
              <option value="wfh">Work from Home</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input required type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input required type="date" value={form.endDate} onChange={e => setForm({...form, endDate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          </div>
          {totalDays > 0 && (
            <div className={`px-3 py-2 rounded-lg text-sm ${insufficient ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
              {totalDays} business day{totalDays > 1 ? 's' : ''} {insufficient ? '— Insufficient balance!' : 'requested'}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" disabled={insufficient || totalDays === 0} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">Submit Request</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TeamAbsenceCalendar = ({ upcomingLeave }) => {
  // Build a simple month view
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const monthName = new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const calDayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Map leave days
  const leaveMap = {};
  (upcomingLeave || []).forEach(leave => {
    const s = new Date(leave.startDate + 'T00:00:00');
    const e = new Date(leave.endDate + 'T00:00:00');
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      if (!leaveMap[dateStr]) leaveMap[dateStr] = [];
      leaveMap[dateStr].push(leave);
    }
  });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{monthName}</h3>
      <div className="grid grid-cols-7 gap-1">
        {calDayLabels.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-gray-500 py-1">{d}</div>
        ))}
        {cells.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} />;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const leaveForDay = leaveMap[dateStr] || [];
          const isToday = day === now.getDate();
          const isWeekend = new Date(year, month, day).getDay() === 0 || new Date(year, month, day).getDay() === 6;

          return (
            <div key={day} className={`text-center py-1.5 rounded text-xs relative ${isToday ? 'bg-blue-600 text-white font-bold' : isWeekend ? 'text-gray-300' : 'text-gray-700'} ${leaveForDay.length > 0 && !isToday ? 'bg-pink-100' : ''}`}
              title={leaveForDay.map(l => `${l.userName} (${l.leaveType})`).join(', ')}>
              {day}
              {leaveForDay.length > 0 && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {leaveForDay.slice(0, 3).map((l, j) => (
                    <div key={j} className="w-1 h-1 rounded-full" style={{ background: getLeaveTypeBadge(l.leaveType).color }} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-gray-200">
        {upcomingLeave && [...new Set(upcomingLeave.map(l => l.userName))].map(name => {
          const leaves = upcomingLeave.filter(l => l.userName === name);
          return (
            <div key={name} className="text-xs text-gray-600">
              <span className="font-medium">{name}</span>: {leaves.map(l => {
                const badge = getLeaveTypeBadge(l.leaveType);
                return <span key={l.startDate} className="ml-1 px-1.5 py-0.5 rounded" style={{ background: badge.bg, color: badge.color }}>{badge.label} {l.startDate.slice(5)}</span>;
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LeaveBoardModule = ({ data, onRequestLeave, onApproveRequest, onRejectRequest }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">LeaveBoard</h1>
          <p className="text-gray-600 mt-1">Leave management & absence calendar</p>
        </div>
        <button onClick={onRequestLeave} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 font-medium">+ Request Leave</button>
      </div>

      {/* Leave Balances */}
      <h2 className="text-xl font-semibold text-gray-900 mb-4">My Leave Balance</h2>
      <LeaveBalanceCards balance={data?.myBalance || {}} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Leave Requests */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Leave Requests</h2>
          <div className="space-y-3">
            {(data?.requests || []).map(req => {
              const typeBadge = getLeaveTypeBadge(req.leaveType);
              const statusBadge = getLeaveStatusBadge(req.status);
              return (
                <div key={req.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">{req.userName.charAt(0)}</div>
                      <span className="font-medium text-sm text-gray-900">{req.userName}</span>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: typeBadge.bg, color: typeBadge.color }}>{typeBadge.icon} {typeBadge.label}</span>
                    </div>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: statusBadge.bg, color: statusBadge.color }}>{statusBadge.label}</span>
                  </div>
                  <div className="text-sm text-gray-700 mb-2">
                    📅 {formatDate(req.startDate)} — {formatDate(req.endDate)} ({req.totalDays} day{req.totalDays > 1 ? 's' : ''})
                  </div>
                  {req.reason && <p className="text-sm text-gray-500 mb-2">{req.reason}</p>}
                  {req.status === 'pending' && (
                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                      <button onClick={() => onApproveRequest(req.id)} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">Approve</button>
                      <button onClick={() => onRejectRequest(req.id)} className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200">Reject</button>
                    </div>
                  )}
                  {req.status === 'approved' && req.approvedBy && (
                    <div className="text-xs text-gray-400">Approved by {req.approvedBy}</div>
                  )}
                </div>
              );
            })}
            {(!data?.requests || data.requests.length === 0) && (
              <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No leave requests found.</div>
            )}
          </div>

          {/* Team Balances */}
          <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-4">Team Balances</h2>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Team Member</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Vacation</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Sick</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Personal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(data?.teamBalances || []).map(tb => (
                  <tr key={tb.userName} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{tb.userName}</td>
                    <td className="px-4 py-3 text-center text-sm text-blue-700 font-semibold">{tb.vacationRemaining}</td>
                    <td className="px-4 py-3 text-center text-sm text-red-600 font-semibold">{tb.sickRemaining}</td>
                    <td className="px-4 py-3 text-center text-sm text-purple-700 font-semibold">{tb.personalRemaining}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Calendar */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Team Calendar</h2>
          <TeamAbsenceCalendar upcomingLeave={data?.upcomingLeave} />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DATAOPS MODULE VIEWS
// ============================================================================

const getDataOpsPipelineStatusBadge = (status) => {
  const badges = {
    running: { bg: '#d4e4ff', color: '#0366d6', label: 'Running' },
    idle: { bg: '#f6f8fa', color: '#586069', label: 'Idle' },
    queued: { bg: '#f3e8ff', color: '#6f42c1', label: 'Queued' },
    completed: { bg: '#dcffe4', color: '#28a745', label: 'Completed' },
    failed: { bg: '#ffe5e9', color: '#dc3545', label: 'Failed' }
  };
  return badges[status] || badges.idle;
};

const formatPipelineDuration = (seconds) => {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const formatDataOpsDateTime = (dateStr) => {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const DataOpsMetricsCards = ({ metrics }) => {
  const cards = [
    { label: 'Active Pipelines', value: metrics.activePipelines, icon: '🔧', color: '#6f42c1' },
    { label: 'Data Processed', value: metrics.dataProcessed, icon: '💾', color: '#17a2b8' },
    { label: 'Reports Generated', value: metrics.reportsGenerated, icon: '📊', color: '#28a745' },
    { label: 'Success Rate', value: metrics.successRate, icon: '✓', color: '#20c997' }
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map(card => (
        <div key={card.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{card.label}</span>
            <span className="text-2xl">{card.icon}</span>
          </div>
          <div className="text-2xl font-bold" style={{ color: card.color }}>{card.value}</div>
        </div>
      ))}
    </div>
  );
};

const DataOpsActivePipelines = ({ pipelines, onTrigger }) => (
  <div className="bg-white rounded-lg shadow border border-gray-200">
    <div className="px-5 py-4 border-b border-gray-200">
      <h2 className="text-lg font-semibold text-gray-900">Active Pipelines</h2>
    </div>
    <div className="p-5 space-y-3">
      {(!pipelines || pipelines.length === 0) ? (
        <div className="text-center text-gray-400 py-8">No active pipelines</div>
      ) : pipelines.map((p, i) => {
        const badge = getDataOpsPipelineStatusBadge(p.status);
        return (
          <div key={i} className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-gray-900">{p.name}</span>
                <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{p.type}</span>
                {onTrigger && p.status !== 'running' && (
                  <button onClick={() => onTrigger(p.pipelineId)} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200">▶</button>
                )}
              </div>
            </div>
            {p.progress !== undefined && (
              <>
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span><span>{p.progress}%</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-full transition-all" style={{ width: `${p.progress}%`, background: badge.color }} />
                </div>
              </>
            )}
            <div className="text-xs text-gray-500">{p.records?.toLocaleString()} records</div>
          </div>
        );
      })}
    </div>
  </div>
);

const DataOpsPowerBIReports = ({ reports }) => (
  <div className="bg-white rounded-lg shadow border border-gray-200">
    <div className="px-5 py-4 border-b border-gray-200">
      <h2 className="text-lg font-semibold text-gray-900">Power BI Reports</h2>
    </div>
    <div className="p-5 space-y-3">
      {(!reports || reports.length === 0) ? (
        <div className="text-center text-gray-400 py-8">No reports available</div>
      ) : reports.map((r, i) => (
        <div key={i} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{r.icon}</span>
            <div>
              <div className="font-medium text-sm text-gray-900">{r.title}</div>
              <div className="text-xs text-gray-500">Updated {r.lastUpdated}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">👁 {r.views}</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${r.status === 'Published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const DataOpsPipelinesView = ({ pipelines, onAdd, onPipelineClick, onTrigger }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-lg font-semibold text-gray-900">All Pipelines</h2>
      <button onClick={onAdd} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Pipeline</button>
    </div>
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Pipeline</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Last Run</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Duration</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Records</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Progress</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {(pipelines || []).map(p => {
            const badge = getDataOpsPipelineStatusBadge(p.status);
            return (
              <tr key={p.pipelineId} className="hover:bg-gray-50 cursor-pointer" onClick={() => onPipelineClick(p.pipelineId)}>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{p.pipelineName}</div>
                  <div className="text-xs font-mono text-gray-500">{p.pipelineId}</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{p.pipelineType}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">{formatDataOpsDateTime(p.lastRun)}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{formatPipelineDuration(p.durationSeconds)}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{p.recordsProcessed?.toLocaleString() || 0}</td>
                <td className="px-4 py-3">
                  {p.progress != null ? (
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${p.progress}%`, background: badge.color }} />
                      </div>
                      <span className="text-xs text-gray-500">{p.progress}%</span>
                    </div>
                  ) : '-'}
                </td>
                <td className="px-4 py-3">
                  <button onClick={(e) => { e.stopPropagation(); onTrigger(p.pipelineId); }}
                    disabled={p.status === 'running'}
                    className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed">
                    ▶ Run
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {(!pipelines || pipelines.length === 0) && (
        <div className="p-8 text-center text-gray-400">No pipelines found. Create your first pipeline to get started.</div>
      )}
    </div>
  </div>
);

const DataOpsReportsView = ({ reports, onAdd, onReportClick, onToggleFavorite }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-lg font-semibold text-gray-900">Power BI Reports</h2>
      <button onClick={onAdd} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Report</button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {(reports || []).map(r => {
        const published = r.status === 'Published';
        return (
          <div key={r.reportId} className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => onReportClick(r.reportId)}>
            <div className="flex items-start justify-between mb-3">
              <span className="text-3xl">{r.reportIcon}</span>
              <button onClick={(e) => { e.stopPropagation(); onToggleFavorite(r.reportId, !r.isFavorite); }} className="text-xl">{r.isFavorite ? '⭐' : '☆'}</button>
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">{r.reportName}</h3>
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
              {r.category && <span className="text-xs text-gray-500">{r.category}</span>}
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>👁 {r.viewCount} views</span>
              <span>{formatDataOpsDateTime(r.lastUpdated)}</span>
            </div>
          </div>
        );
      })}
    </div>
    {(!reports || reports.length === 0) && (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center text-gray-400">No reports found.</div>
    )}
  </div>
);

const DataOpsModule = ({ data, viewMode, onViewModeChange, onAddPipeline, onAddReport, onPipelineClick, onReportClick, onTriggerPipeline, onToggleFavorite, onTaskClick, onStatusChange, onAddTask, onSendEod, onEodSettings }) => (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">DataOps</h1>
        <p className="text-gray-600 mt-1">Data pipeline orchestration & Power BI integration</p>
      </div>
      <div className="flex gap-2">
        <button onClick={onEodSettings} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">⚙️ EOD Settings</button>
        <button onClick={onSendEod} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">📤 Send EOD</button>
        {viewMode === 'sprint' && onAddTask && (
          <button onClick={onAddTask} className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 font-medium">+ New Task</button>
        )}
      </div>
    </div>
    <div className="flex gap-2 mb-6">
      {['dashboard', 'sprint', 'pipelines', 'reports'].map(mode => (
        <button key={mode} onClick={() => onViewModeChange(mode)}
          className={`px-4 py-2 text-sm rounded-lg font-medium ${viewMode === mode ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
          {mode === 'dashboard' ? '📊 Dashboard' : mode === 'sprint' ? '🗂️ Sprint Board' : mode === 'pipelines' ? '🔧 Pipelines' : '📈 Reports'}
        </button>
      ))}
    </div>
    {viewMode === 'dashboard' && data && (
      <>
        <DataOpsMetricsCards metrics={data.metrics} />
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <DataOpsActivePipelines pipelines={data.activePipelines} onTrigger={onTriggerPipeline} />
          <DataOpsPowerBIReports reports={data.powerbiReports} />
        </div>
      </>
    )}
    {viewMode === 'sprint' && (
      <>
        {data?.sprint ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{data.sprint.name}</h2>
                <p className="text-sm text-gray-600">{data.sprint.goal}</p>
                <div className="text-xs text-gray-500 mt-1">📅 {data.sprint.startDate} → {data.sprint.endDate}</div>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold uppercase">{data.sprint.status}</span>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-sm text-yellow-800">No active sprint found.</div>
        )}
        {data?.sprintBoard && (
          <DevOpsPipelineBoard pipeline={data.sprintBoard} title="DataOps Sprint Board" onTaskClick={onTaskClick} onStatusChange={onStatusChange} />
        )}
      </>
    )}
    {viewMode === 'pipelines' && (
      <DataOpsPipelinesView pipelines={data?.allPipelines} onAdd={onAddPipeline} onPipelineClick={onPipelineClick} onTrigger={onTriggerPipeline} />
    )}
    {viewMode === 'reports' && (
      <DataOpsReportsView reports={data?.allReports} onAdd={onAddReport} onReportClick={onReportClick} onToggleFavorite={onToggleFavorite} />
    )}
  </div>
);

// ============================================================================
// DATAOPS MODALS
// ============================================================================

const AddPipelineModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ pipelineName: '', pipelineType: 'ETL', description: '', scheduleCron: '', maxRetries: 3 });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Data Pipeline</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Name *</label>
            <input required value={form.pipelineName} onChange={e => setForm({...form, pipelineName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., Daily Sales ETL" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Type</label>
            <select value={form.pipelineType} onChange={e => setForm({...form, pipelineType: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
              <option value="ETL">ETL</option><option value="Report">Report</option><option value="Dashboard">Dashboard</option><option value="Power BI">Power BI</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Describe what this pipeline does..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Schedule (Cron Expression)</label>
            <input value={form.scheduleCron} onChange={e => setForm({...form, scheduleCron: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., 0 2 * * * (2 AM daily)" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Retries</label>
            <input type="number" value={form.maxRetries} onChange={e => setForm({...form, maxRetries: parseInt(e.target.value)})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" min="0" max="10" />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">Create Pipeline</button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PipelineDetailModal = ({ pipeline, onClose, onUpdate, onDelete, onTrigger }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    pipelineName: pipeline.pipelineName || '', pipelineType: pipeline.pipelineType || 'ETL',
    description: pipeline.description || '', scheduleCron: pipeline.scheduleCron || '', maxRetries: pipeline.maxRetries || 3
  });
  const handleUpdate = (e) => { e.preventDefault(); onUpdate(pipeline.pipelineId, form); setEditing(false); };
  const handleDelete = () => { if (confirm(`Delete pipeline "${pipeline.pipelineName}"?`)) onDelete(pipeline.pipelineId); };
  const badge = getDataOpsPipelineStatusBadge(pipeline.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">{pipeline.pipelineName}</h2>
            <p className="text-xs font-mono text-gray-500 mt-1">{pipeline.pipelineId}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        {!editing ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded text-sm font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
              <span className="text-sm text-gray-600">{pipeline.pipelineType}</span>
            </div>
            {pipeline.description && <p className="text-sm text-gray-600 mb-4">{pipeline.description}</p>}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Last Run</h3><p className="text-sm text-gray-900">{pipeline.lastRun ? new Date(pipeline.lastRun).toLocaleString() : 'Never'}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Duration</h3><p className="text-sm text-gray-900">{formatPipelineDuration(pipeline.durationSeconds)}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Records Processed</h3><p className="text-sm text-gray-900">{pipeline.recordsProcessed?.toLocaleString() || 0}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Schedule</h3><p className="text-sm text-gray-900 font-mono">{pipeline.scheduleCron || 'Manual'}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Data Processed</h3><p className="text-sm text-gray-900">{pipeline.dataProcessedGb ? `${pipeline.dataProcessedGb} GB` : '-'}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Retries</h3><p className="text-sm text-gray-900">{pipeline.retryCount || 0} / {pipeline.maxRetries || 3}</p></div>
            </div>
            {pipeline.progress != null && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2"><span>Progress</span><span>{pipeline.progress}%</span></div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pipeline.progress}%`, background: badge.color }} />
                </div>
              </div>
            )}
            {pipeline.errorMessage && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="text-xs font-semibold text-red-700 mb-1">Error</h3>
                <p className="text-sm text-red-600">{pipeline.errorMessage}</p>
              </div>
            )}
            <div className="flex gap-2 pt-4 border-t border-gray-200">
              <button onClick={() => onTrigger(pipeline.pipelineId)} disabled={pipeline.status === 'running'}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">▶ Trigger Run</button>
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Edit</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200">Delete</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Name</label>
              <input required value={form.pipelineName} onChange={e => setForm({...form, pipelineName: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.pipelineType} onChange={e => setForm({...form, pipelineType: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500">
                <option value="ETL">ETL</option><option value="Report">Report</option><option value="Dashboard">Dashboard</option><option value="Power BI">Power BI</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Schedule (Cron)</label>
              <input value={form.scheduleCron} onChange={e => setForm({...form, scheduleCron: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save Changes</button>
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const AddReportModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ reportName: '', reportIcon: '📊', workspaceId: '', datasetId: '', description: '', category: 'General', refreshSchedule: '' });
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(form); };
  const iconOptions = ['📊', '💰', '👥', '📈', '⚙️', '📢', '🎯', '💡', '📉', '🔍'];
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Power BI Report</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Name *</label>
            <input required value={form.reportName} onChange={e => setForm({...form, reportName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., Sales Performance Dashboard" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
            <div className="flex gap-2 flex-wrap">
              {iconOptions.map(icon => (
                <button key={icon} type="button" onClick={() => setForm({...form, reportIcon: icon})}
                  className={`w-10 h-10 text-xl rounded border-2 hover:border-purple-500 ${form.reportIcon === icon ? 'border-purple-600 bg-purple-50' : 'border-gray-200'}`}>{icon}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Workspace ID</label>
              <input value={form.workspaceId} onChange={e => setForm({...form, workspaceId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Optional" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dataset ID</label>
              <input value={form.datasetId} onChange={e => setForm({...form, datasetId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Optional" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
              <option value="General">General</option><option value="Executive">Executive</option><option value="Sales">Sales</option>
              <option value="Finance">Finance</option><option value="Operations">Operations</option><option value="Marketing">Marketing</option><option value="Analytics">Analytics</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Describe what this report shows..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Schedule</label>
            <input value={form.refreshSchedule} onChange={e => setForm({...form, refreshSchedule: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., Daily at 6 AM" />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">Create Report</button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReportDetailModal = ({ report, onClose, onUpdate, onDelete, onToggleFavorite }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    reportName: report.reportName || '', reportIcon: report.reportIcon || '📊',
    description: report.description || '', category: report.category || 'General',
    status: report.status || 'draft', refreshSchedule: report.refreshSchedule || ''
  });
  const handleUpdate = (e) => { e.preventDefault(); onUpdate(report.reportId, form); setEditing(false); };
  const handleDelete = () => { if (confirm(`Delete report "${report.reportName}"?`)) onDelete(report.reportId); };
  const published = report.status === 'Published';
  const iconOptions = ['📊', '💰', '👥', '📈', '⚙️', '📢', '🎯', '💡', '📉', '🔍'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{report.reportIcon}</span>
            <div>
              <h2 className="text-xl font-semibold">{report.reportName}</h2>
              <p className="text-xs font-mono text-gray-500 mt-1">{report.reportId}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        {!editing ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded text-sm font-semibold ${published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{report.status}</span>
              {report.category && <span className="text-sm text-gray-600">{report.category}</span>}
              <button onClick={() => onToggleFavorite(report.reportId, !report.isFavorite)} className="text-2xl ml-auto">{report.isFavorite ? '⭐' : '☆'}</button>
            </div>
            {report.description && <p className="text-sm text-gray-600 mb-4">{report.description}</p>}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">View Count</h3><p className="text-sm text-gray-900">{report.viewCount?.toLocaleString() || 0}</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Last Updated</h3><p className="text-sm text-gray-900">{report.lastUpdated ? new Date(report.lastUpdated).toLocaleString() : 'Never'}</p></div>
              {report.workspaceId && <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Workspace ID</h3><p className="text-sm font-mono text-gray-900">{report.workspaceId}</p></div>}
              {report.datasetId && <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Dataset ID</h3><p className="text-sm font-mono text-gray-900">{report.datasetId}</p></div>}
              {report.refreshSchedule && <div className="col-span-2"><h3 className="text-xs font-semibold text-gray-500 mb-1">Refresh Schedule</h3><p className="text-sm text-gray-900">{report.refreshSchedule}</p></div>}
            </div>
            <div className="flex gap-2 pt-4 border-t border-gray-200">
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Edit</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200">Delete</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Report Name</label>
              <input required value={form.reportName} onChange={e => setForm({...form, reportName: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
              <div className="flex gap-2 flex-wrap">
                {iconOptions.map(icon => (
                  <button key={icon} type="button" onClick={() => setForm({...form, reportIcon: icon})}
                    className={`w-10 h-10 text-xl rounded border-2 hover:border-purple-500 ${form.reportIcon === icon ? 'border-purple-600 bg-purple-50' : 'border-gray-200'}`}>{icon}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500">
                <option value="General">General</option><option value="Executive">Executive</option><option value="Sales">Sales</option>
                <option value="Finance">Finance</option><option value="Operations">Operations</option><option value="Marketing">Marketing</option><option value="Analytics">Analytics</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500">
                <option value="draft">Draft</option><option value="Published">Published</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save Changes</button>
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// LEARNTRACK MODULE VIEWS
// ============================================================================

const getDifficultyBadge = (d) => {
  const badges = { beginner: { bg: '#dcffe4', color: '#28a745', label: 'Beginner' }, intermediate: { bg: '#fff3cd', color: '#856404', label: 'Intermediate' }, advanced: { bg: '#ffe5e9', color: '#dc3545', label: 'Advanced' } };
  return badges[d] || badges.beginner;
};

const getGroupLabel = (g) => {
  const labels = { all: 'All Users', admin: 'Admins', developer: 'Developers' };
  return labels[g] || g;
};

const getEnrollmentStatusBadge = (s) => {
  const badges = {
    assigned: { bg: '#f6f8fa', color: '#586069', label: 'Assigned' },
    in_progress: { bg: '#d4e4ff', color: '#0366d6', label: 'In Progress' },
    completed: { bg: '#dcffe4', color: '#28a745', label: 'Completed' },
    dropped: { bg: '#ffe5e9', color: '#dc3545', label: 'Dropped' }
  };
  return badges[s] || badges.assigned;
};

const LearnTrackMetrics = ({ data }) => {
  const totalCourses = data?.courses?.length || 0;
  const myEnrolled = data?.myEnrollments?.length || 0;
  const myCompleted = (data?.myEnrollments || []).filter(e => e.status === 'completed').length;
  const myBadges = data?.myBadges?.length || 0;
  const cards = [
    { label: 'Total Courses', value: totalCourses, icon: '📚', color: '#fd7e14' },
    { label: 'My Enrollments', value: myEnrolled, icon: '📝', color: '#0366d6' },
    { label: 'Completed', value: myCompleted, icon: '✅', color: '#28a745' },
    { label: 'Badges Earned', value: myBadges, icon: '🏅', color: '#6f42c1' }
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{c.label}</span>
            <span className="text-2xl">{c.icon}</span>
          </div>
          <div className="text-2xl font-bold" style={{ color: c.color }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
};

const SkillsOverview = ({ skills, courses }) => (
  <div className="mb-6">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">Skills</h2>
    <div className="flex flex-wrap gap-3">
      {(skills || []).map(s => {
        const count = (courses || []).filter(c => c.skillName === s.skillName).length;
        const categoryColors = { technical: '#0366d6', soft: '#fd7e14', leadership: '#6f42c1', compliance: '#dc3545' };
        return (
          <div key={s.id} className="bg-white rounded-lg shadow border border-gray-200 px-4 py-3 flex items-center gap-3">
            <div className="w-2 h-8 rounded-full" style={{ background: categoryColors[s.category] || '#586069' }} />
            <div>
              <div className="text-sm font-semibold text-gray-900">{s.skillName}</div>
              <div className="text-xs text-gray-500">{count} course{count !== 1 ? 's' : ''} · {s.category}</div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const TeamProgressTable = ({ teamProgress }) => (
  <div>
    <h2 className="text-lg font-semibold text-gray-900 mb-3">Team Progress</h2>
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Team Member</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Enrolled</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">In Progress</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Completed</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Badges</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {(teamProgress || []).map(tp => (
            <tr key={tp.userId} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{tp.userName}</td>
              <td className="px-4 py-3 text-center text-sm text-gray-600">{tp.totalEnrollments}</td>
              <td className="px-4 py-3 text-center text-sm text-blue-600 font-semibold">{tp.inProgressCourses}</td>
              <td className="px-4 py-3 text-center text-sm text-green-600 font-semibold">{tp.completedCourses}</td>
              <td className="px-4 py-3 text-center text-sm text-purple-600 font-semibold">{tp.badgeCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const CourseCatalog = ({ courses, skills, onAdd, onCourseClick, onEnroll, filterSkill, filterGroup, onFilterSkill, onFilterGroup }) => (
  <div>
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
      <h2 className="text-lg font-semibold text-gray-900">Course Catalog</h2>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={filterSkill} onChange={e => onFilterSkill(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="">All Skills</option>
          {(skills || []).map(s => <option key={s.id} value={s.skillName}>{s.skillName}</option>)}
        </select>
        <select value={filterGroup} onChange={e => onFilterGroup(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="">All Groups</option>
          <option value="all">All Users</option>
          <option value="developer">Developers</option>
          <option value="admin">Admins</option>
        </select>
        <button onClick={onAdd} className="px-4 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 font-medium">+ New Course</button>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {(courses || []).filter(c => (!filterSkill || c.skillName === filterSkill) && (!filterGroup || c.targetGroup === filterGroup)).map(c => {
        const diff = getDifficultyBadge(c.difficulty);
        return (
          <div key={c.id} className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => onCourseClick(c.id)}>
            <div className="flex items-start justify-between mb-3">
              <span className="text-3xl">{c.badgeIcon || '📚'}</span>
              <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: diff.bg, color: diff.color }}>{diff.label}</span>
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">{c.courseName}</h3>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {c.skillName && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">{c.skillName}</span>}
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{getGroupLabel(c.targetGroup)}</span>
            </div>
            <div className="text-xs text-gray-500 mb-3">
              {c.durationHours}h · {c.instructor || 'TBD'}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">{c.enrollmentCount} enrolled · {c.completedCount} completed</span>
              <button onClick={(e) => { e.stopPropagation(); onEnroll({ courseId: c.id, userId: 2, userName: 'John Developer' }); }}
                className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200 font-medium">Enroll</button>
            </div>
          </div>
        );
      })}
    </div>
    {(!courses || courses.length === 0) && <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center text-gray-400">No courses found.</div>}
  </div>
);

const MyEnrollments = ({ enrollments, onUpdateProgress }) => (
  <div className="mb-6">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">My Courses</h2>
    <div className="space-y-3">
      {(enrollments || []).map(e => {
        const badge = getEnrollmentStatusBadge(e.status);
        return (
          <div key={e.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{e.badgeIcon || '📚'}</span>
                <div>
                  <div className="font-medium text-sm text-gray-900">{e.courseName}</div>
                  <div className="text-xs text-gray-500">{e.skillName} · {e.difficulty} · {e.durationHours}h</div>
                </div>
              </div>
              <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span><span>{e.progress}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${e.progress}%`, background: e.status === 'completed' ? '#28a745' : '#0366d6' }} />
                </div>
              </div>
              {e.score != null && <span className="text-sm font-bold text-purple-600">Score: {e.score}%</span>}
              {e.status === 'assigned' && (
                <button onClick={() => onUpdateProgress(e.id, { status: 'in_progress' })} className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">Start</button>
              )}
              {e.status === 'in_progress' && (
                <button onClick={() => onUpdateProgress(e.id, { status: 'completed' })} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">Complete</button>
              )}
            </div>
          </div>
        );
      })}
      {(!enrollments || enrollments.length === 0) && <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No enrollments yet. Browse the course catalog to get started.</div>}
    </div>
  </div>
);

const MyBadges = ({ badges }) => (
  <div>
    <h2 className="text-lg font-semibold text-gray-900 mb-3">My Badges</h2>
    {(!badges || badges.length === 0) ? (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-400">No badges earned yet. Complete courses to earn badges!</div>
    ) : (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {badges.map(b => (
          <div key={b.id} className="bg-white rounded-lg shadow border border-gray-200 p-4 text-center">
            <div className="text-4xl mb-2">{b.badgeIcon}</div>
            <div className="text-sm font-semibold text-gray-900">{b.badgeName}</div>
            <div className="text-xs text-gray-500 mt-1">{b.description}</div>
            <div className="text-xs text-gray-400 mt-2">{b.awardedAt ? new Date(b.awardedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const LearnTrackModule = ({ data, viewMode, onViewModeChange, onAddCourse, onCourseClick, onEnroll, onUpdateProgress, filterSkill, filterGroup, onFilterSkill, onFilterGroup }) => (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">LearnTrack</h1>
        <p className="text-gray-600 mt-1">Learning management, courses & skill development</p>
      </div>
    </div>
    <div className="flex gap-2 mb-6">
      {['dashboard', 'courses', 'mylearning'].map(mode => (
        <button key={mode} onClick={() => onViewModeChange(mode)}
          className={`px-4 py-2 text-sm rounded-lg font-medium ${viewMode === mode ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
          {mode === 'dashboard' ? '📊 Dashboard' : mode === 'courses' ? '📚 Courses' : '🎓 My Learning'}
        </button>
      ))}
    </div>
    {viewMode === 'dashboard' && (
      <>
        <LearnTrackMetrics data={data} />
        <SkillsOverview skills={data?.skills} courses={data?.courses} />
        <TeamProgressTable teamProgress={data?.teamProgress} />
      </>
    )}
    {viewMode === 'courses' && (
      <CourseCatalog courses={data?.courses} skills={data?.skills} onAdd={onAddCourse} onCourseClick={onCourseClick} onEnroll={onEnroll}
        filterSkill={filterSkill} filterGroup={filterGroup} onFilterSkill={onFilterSkill} onFilterGroup={onFilterGroup} />
    )}
    {viewMode === 'mylearning' && (
      <>
        <MyEnrollments enrollments={data?.myEnrollments} onUpdateProgress={onUpdateProgress} />
        <MyBadges badges={data?.myBadges} />
      </>
    )}
  </div>
);

// ============================================================================
// LEARNTRACK MODALS
// ============================================================================

const AddCourseModal = ({ onClose, onSubmit, skills }) => {
  const [form, setForm] = useState({
    courseName: '', description: '', skillId: '', difficulty: 'beginner',
    durationHours: 1, instructor: '', targetGroup: 'all', badgeName: '', badgeIcon: '🏅'
  });
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...form, skillId: form.skillId ? parseInt(form.skillId) : null });
  };
  const badgeIcons = ['🏅', '⚛️', '🟨', '🐍', '🚀', '👑', '🛡️', '⭐', '🎯', '💎', '🔥', '🌟'];
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Course</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Course Name *</label>
            <input required value={form.courseName} onChange={e => setForm({...form, courseName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="e.g., React Advanced Patterns" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Skill</label>
              <select value={form.skillId} onChange={e => setForm({...form, skillId: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="">Select skill...</option>
                {(skills || []).map(s => <option key={s.id} value={s.id}>{s.skillName}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
              <select value={form.difficulty} onChange={e => setForm({...form, difficulty: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (hours)</label>
              <input type="number" min="0.5" step="0.5" value={form.durationHours} onChange={e => setForm({...form, durationHours: parseFloat(e.target.value)})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Group</label>
              <select value={form.targetGroup} onChange={e => setForm({...form, targetGroup: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="all">All Users</option><option value="developer">Developers</option><option value="admin">Admins</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructor</label>
            <input value={form.instructor} onChange={e => setForm({...form, instructor: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Instructor name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Course description..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Badge Name (awarded on completion)</label>
            <input value={form.badgeName} onChange={e => setForm({...form, badgeName: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="e.g., React Pro" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Badge Icon</label>
            <div className="flex gap-2 flex-wrap">
              {badgeIcons.map(icon => (
                <button key={icon} type="button" onClick={() => setForm({...form, badgeIcon: icon})}
                  className={`w-10 h-10 text-xl rounded border-2 hover:border-orange-500 ${form.badgeIcon === icon ? 'border-orange-600 bg-orange-50' : 'border-gray-200'}`}>{icon}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium">Create Course</button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CourseDetailModal = ({ course, onClose, onUpdate, onDelete, onEnroll }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    courseName: course.courseName || '', description: course.description || '',
    difficulty: course.difficulty || 'beginner', durationHours: course.durationHours || 1,
    instructor: course.instructor || '', targetGroup: course.targetGroup || 'all',
    status: course.status || 'published'
  });
  const handleUpdate = (e) => { e.preventDefault(); onUpdate(course.id, form); setEditing(false); };
  const handleDelete = () => { if (confirm(`Delete course "${course.courseName}"?`)) onDelete(course.id); };
  const diff = getDifficultyBadge(course.difficulty);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{course.badgeIcon || '📚'}</span>
            <div>
              <h2 className="text-xl font-semibold">{course.courseName}</h2>
              {course.skillName && <span className="text-xs text-blue-600">{course.skillName}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        {!editing ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: diff.bg, color: diff.color }}>{diff.label}</span>
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{getGroupLabel(course.targetGroup)}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${course.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{course.status}</span>
            </div>
            {course.description && <p className="text-sm text-gray-600 mb-4">{course.description}</p>}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Duration</h3><p className="text-sm text-gray-900">{course.durationHours} hours</p></div>
              <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Instructor</h3><p className="text-sm text-gray-900">{course.instructor || 'TBD'}</p></div>
              {course.badgeName && <div><h3 className="text-xs font-semibold text-gray-500 mb-1">Badge</h3><p className="text-sm text-gray-900">{course.badgeIcon} {course.badgeName}</p></div>}
            </div>

            {/* Enrolled Users */}
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Enrolled Users ({(course.enrolledUsers || []).length})</h3>
            <div className="bg-gray-50 rounded-lg border border-gray-200 mb-4">
              {(course.enrolledUsers || []).length === 0 ? (
                <div className="p-4 text-center text-gray-400 text-sm">No enrollments yet</div>
              ) : (
                <table className="w-full">
                  <thead><tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">User</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Progress</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Score</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-200">
                    {(course.enrolledUsers || []).map(eu => {
                      const sb = getEnrollmentStatusBadge(eu.status);
                      return (
                        <tr key={eu.id}>
                          <td className="px-3 py-2 text-sm text-gray-900">{eu.userName}</td>
                          <td className="px-3 py-2"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: sb.bg, color: sb.color }}>{sb.label}</span></td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${eu.progress}%` }} /></div>
                              <span className="text-xs text-gray-500">{eu.progress}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">{eu.score != null ? `${eu.score}%` : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex gap-2 mb-4">
              <button onClick={() => onEnroll({ courseId: course.id, userId: 1, userName: 'Jim Manager' })} className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200">+ Enroll Jim</button>
              <button onClick={() => onEnroll({ courseId: course.id, userId: 2, userName: 'John Developer' })} className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200">+ Enroll John</button>
              <button onClick={() => onEnroll({ courseId: course.id, userId: 3, userName: 'Sarah Frontend' })} className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200">+ Enroll Sarah</button>
              <button onClick={() => onEnroll({ courseId: course.id, userId: 4, userName: 'Mike Fullstack' })} className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200">+ Enroll Mike</button>
            </div>
            <div className="flex gap-2 pt-4 border-t border-gray-200">
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Edit</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-100 text-red-700 text-sm rounded-lg hover:bg-red-200">Delete</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Name</label>
              <input required value={form.courseName} onChange={e => setForm({...form, courseName: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                <select value={form.difficulty} onChange={e => setForm({...form, difficulty: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                  <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Group</label>
                <select value={form.targetGroup} onChange={e => setForm({...form, targetGroup: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                  <option value="all">All Users</option><option value="developer">Developers</option><option value="admin">Admins</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" />
            </div>
            <div className="flex gap-2 pt-4">
              <button type="submit" className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">Save Changes</button>
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// QMS (QUALITY MANAGEMENT SYSTEM) VIEWS
// ============================================================================

const qmsSeverityStyle = (s) => {
  const m = { critical: { bg: '#fef2f2', color: '#dc2626' }, high: { bg: '#fff7ed', color: '#ea580c' }, medium: { bg: '#fffbeb', color: '#d97706' }, low: { bg: '#eff6ff', color: '#2563eb' } };
  return m[s] || m.medium;
};
const qmsStatusStyle = (s) => {
  const m = { pending: { bg: '#fefce8', color: '#ca8a04' }, approved: { bg: '#f0fdf4', color: '#16a34a' }, rejected: { bg: '#fef2f2', color: '#dc2626' }, pass: { bg: '#f0fdf4', color: '#16a34a' }, fail: { bg: '#fef2f2', color: '#dc2626' }, open: { bg: '#eff6ff', color: '#2563eb' }, in_progress: { bg: '#fefce8', color: '#ca8a04' }, resolved: { bg: '#f0fdf4', color: '#16a34a' }, closed: { bg: '#f3f4f6', color: '#6b7280' } };
  return m[s] || m.pending;
};
const qmsStageLabels = { requirements: 'Review Requirements', testplan: 'Create Test Plan', testcases: 'Prepare Test Cases', testing: 'Execute Testing', uat: 'UAT Sign-off', verification: 'Production Verification' };

const QmsWorkflowSteps = ({ currentStage, onStageChange }) => {
  const stages = ['requirements', 'testplan', 'testcases', 'testing', 'uat', 'verification'];
  const icons = ['📋', '📝', '✅', '🧪', '✍️', '🚀'];
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
      <h2 className="text-sm font-semibold text-gray-700 mb-4">QA Workflow Stages</h2>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {stages.map((s, i) => (
          <button key={s} onClick={() => onStageChange(s)}
            className={`flex-1 min-w-[130px] px-3 py-3 rounded-lg text-center text-xs font-semibold transition-all ${currentStage === s ? 'bg-teal-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <span className="text-lg block mb-1">{icons[i]}</span>
            {qmsStageLabels[s]}
            {i < stages.length - 1 && <span className="hidden sm:inline text-gray-300 ml-1">→</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

const QmsMetrics = ({ metrics, stage }) => {
  let cards = [];
  if (stage === 'requirements') {
    cards = [
      { label: 'Total Requirements', value: metrics?.requirements?.total || 0, icon: '📋', color: '#20c997' },
      { label: 'Approved', value: metrics?.requirements?.approved || 0, icon: '✅', color: '#16a34a' },
      { label: 'Pending Review', value: metrics?.requirements?.pending || 0, icon: '⏳', color: '#d97706' }
    ];
  } else if (stage === 'testing' || stage === 'testcases') {
    cards = [
      { label: 'Total Test Cases', value: metrics?.testCases?.total || 0, icon: '🧪', color: '#20c997' },
      { label: 'Passed', value: metrics?.testCases?.passed || 0, icon: '✅', color: '#16a34a' },
      { label: 'Failed', value: metrics?.testCases?.failed || 0, icon: '❌', color: '#dc2626' }
    ];
  } else if (stage === 'uat' || stage === 'verification') {
    cards = [
      { label: 'Pass Rate', value: `${metrics?.passRate || 0}%`, icon: '📊', color: '#16a34a' },
      { label: 'Open Defects', value: metrics?.bugs?.open || 0, icon: '🐛', color: '#ea580c' },
      { label: 'Resolved', value: metrics?.bugs?.resolved || 0, icon: '✅', color: '#20c997' }
    ];
  } else {
    cards = [
      { label: 'Requirements', value: metrics?.requirements?.total || 0, icon: '📋', color: '#20c997' },
      { label: 'Test Cases', value: metrics?.testCases?.total || 0, icon: '🧪', color: '#2563eb' },
      { label: 'Bugs', value: metrics?.bugs?.total || 0, icon: '🐛', color: '#ea580c' }
    ];
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{c.label}</span>
            <span className="text-2xl">{c.icon}</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: c.color }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
};

const QmsRequirementsStage = ({ requirements, onApprove, onReject, onAdd, onNext }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-bold text-gray-900">Requirements</h3>
      <button onClick={onAdd} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2">
        <span className="text-lg leading-none">+</span> Add Requirement
      </button>
    </div>
    {(requirements || []).map(r => {
      const ss = qmsStatusStyle(r.status);
      const ps = qmsSeverityStyle(r.priority);
      return (
        <div key={r.id} className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-3" style={{ borderLeftWidth: '4px', borderLeftColor: ps.color }}>
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-gray-900">{r.reqCode}: {r.title}</h4>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{r.status}</span>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            <span className="font-medium">Type:</span> {r.reqType} |{' '}
            <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{r.priority}</span>
          </p>
          {r.description && <p className="text-sm text-gray-500 mb-3">{r.description}</p>}
          {r.status === 'pending' && (
            <div className="flex gap-2">
              <button onClick={() => onApprove(r.id)} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700">✓ Approve</button>
              <button onClick={() => onReject(r.id)} className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-300">✗ Reject</button>
            </div>
          )}
        </div>
      );
    })}
    <button onClick={onNext} className="mt-4 px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Proceed to Test Plan →</button>
  </div>
);

const QmsTestPlanStage = ({ project, onUpdate, onNext, onBack }) => {
  const [env, setEnv] = useState(project?.environment || 'DEV');
  const [targetDate, setTargetDate] = useState(project?.targetDate || '');
  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Test Plan</h3>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
          <input type="text" value={project?.projectName || ''} readOnly className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Test Environment</label>
          <select value={env} onChange={e => setEnv(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
            <option value="DEV">DEV Environment</option>
            <option value="UAT">UAT Environment</option>
            <option value="Staging">Staging Environment</option>
            <option value="Production">Production</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target Completion</label>
          <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
        </div>
      </div>
      <div className="flex gap-3 mt-4">
        <button onClick={() => { onUpdate(project?.projectId, { environment: env, targetDate }); onNext(); }} className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Save & Proceed to Test Cases →</button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
      </div>
    </div>
  );
};

const QmsTestCasesStage = ({ testCases, onAdd, onNext, onBack }) => (
  <div>
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-bold text-gray-900">Test Cases</h3>
      <button onClick={onAdd} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2">
        <span className="text-lg leading-none">+</span> Add Test Case
      </button>
    </div>
    {(testCases || []).map(tc => {
      const ss = qmsStatusStyle(tc.status);
      const ps = qmsSeverityStyle(tc.priority);
      return (
        <div key={tc.id} className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-3">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-gray-900">{tc.tcCode}: {tc.title}</h4>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{tc.status}</span>
          </div>
          <p className="text-sm text-gray-600">
            <span className="font-medium">Module:</span> {tc.module || '-'} |{' '}
            <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{tc.priority}</span>
          </p>
          {tc.steps && tc.steps.length > 0 && (
            <div className="mt-2 text-xs text-gray-500">{tc.steps.length} test step(s)</div>
          )}
        </div>
      );
    })}
    <div className="flex gap-3 mt-4">
      <button onClick={onNext} className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Start Testing →</button>
      <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
    </div>
  </div>
);

const QmsTestingStage = ({ testCases, bugs, metrics, onPass, onFail, onReportBug, onEditBug, onNext, onBack }) => {
  const total = metrics?.testCases?.total || 0;
  const executed = (metrics?.testCases?.passed || 0) + (metrics?.testCases?.failed || 0);
  const progress = total > 0 ? Math.round((executed / total) * 100) : 0;
  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Test Execution</h3>
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Test Progress</span>
          <span>{progress}% Complete</span>
        </div>
        <div className="w-full h-5 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(testCases || []).map(tc => {
              const ss = qmsStatusStyle(tc.status);
              return (
                <tr key={tc.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{tc.tcCode}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{tc.title}</td>
                  <td className="px-4 py-3">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ background: ss.bg, color: ss.color }}>{tc.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {tc.status === 'pending' && (
                        <>
                          <button onClick={() => onPass(tc.id)} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200">Pass</button>
                          <button onClick={() => onFail(tc.id)} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium hover:bg-red-200">Fail</button>
                        </>
                      )}
                      {tc.status === 'fail' && (
                        <button onClick={() => onReportBug(tc)} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200">Report Bug</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {bugs && bugs.filter(b => b.status === 'open' || b.status === 'in_progress').length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Open Bugs</h4>
          {bugs.filter(b => b.status === 'open' || b.status === 'in_progress').map(b => {
            const bs = qmsSeverityStyle(b.severity);
            return (
              <div key={b.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-2" style={{ borderLeftWidth: '4px', borderLeftColor: bs.color }}>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-sm text-gray-900">{b.bugCode}: {b.title}</span>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: bs.bg, color: bs.color }}>{b.severity}</span>
                    <button onClick={() => onEditBug(b)} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300">Edit</button>
                  </div>
                </div>
                {b.assignedTo && <p className="text-xs text-gray-500 mt-1">Assigned: {b.assignedTo}</p>}
              </div>
            );
          })}
        </div>
      )}
      <div className="flex gap-3">
        <button onClick={onNext} className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Proceed to UAT →</button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
        <button onClick={() => onReportBug(null)} className="ml-auto px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200">🐛 Report Bug</button>
      </div>
    </div>
  );
};

const QmsUatStage = ({ metrics, bugs, project, onUpdateProject, onNext, onBack }) => {
  const noBlockers = !(bugs || []).some(b => b.severity === 'critical' && b.status === 'open');
  const passRate = metrics?.passRate || 0;
  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">UAT Sign-off</h3>
      <div className={`p-5 rounded-lg mb-6 border-l-4 ${noBlockers && passRate >= 80 ? 'bg-green-50 border-green-500' : 'bg-yellow-50 border-yellow-500'}`}>
        <h4 className={`font-semibold mb-1 ${noBlockers && passRate >= 80 ? 'text-green-800' : 'text-yellow-800'}`}>
          {noBlockers && passRate >= 80 ? '✓ Ready for UAT Sign-off' : '⚠️ Not Ready — Issues Remain'}
        </h4>
        <p className={`text-sm ${noBlockers && passRate >= 80 ? 'text-green-700' : 'text-yellow-700'}`}>
          {noBlockers && passRate >= 80
            ? 'All critical test cases passed. No blocker issues identified.'
            : `Pass rate: ${passRate}%. Open critical bugs: ${(bugs || []).filter(b => b.severity === 'critical' && b.status === 'open').length}`}
        </p>
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-3 mb-6">
        <div className="flex justify-between text-sm"><span className="text-gray-600">QA Lead</span><span className="font-medium text-gray-900">{project?.qaLead || '-'}</span></div>
        <div className="flex justify-between text-sm"><span className="text-gray-600">Total Test Cases</span><span className="font-medium text-gray-900">{metrics?.testCases?.total || 0}</span></div>
        <div className="flex justify-between text-sm"><span className="text-gray-600">Pass Rate</span><span className="font-medium text-green-600">{passRate}%</span></div>
        <div className="flex justify-between text-sm"><span className="text-gray-600">Open Defects</span><span className="font-medium text-orange-600">{metrics?.bugs?.open || 0}</span></div>
      </div>
      <div className="flex gap-3">
        <button onClick={() => { onUpdateProject(project?.projectId, { status: 'production', currentStage: 'verification' }); onNext(); }}
          className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700" disabled={!noBlockers}>
          Submit Sign-off & Deploy →
        </button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
      </div>
    </div>
  );
};

const QmsVerificationStage = ({ project, onUpdateProject, onBack }) => {
  const [checks, setChecks] = useState({});
  const items = [
    { key: 'accessible', label: 'Verify application is accessible', desc: 'URL: https://app.company.com' },
    { key: 'login', label: 'Test user login functionality', desc: 'Verify authentication works with production credentials' },
    { key: 'database', label: 'Verify database connections', desc: 'Ensure all database operations are functioning correctly' },
    { key: 'workflows', label: 'Test critical business workflows', desc: 'Settlement calculations, payment processing, reporting' },
    { key: 'health', label: 'Monitor system health', desc: 'Check server logs, performance metrics, error rates' },
    { key: 'integrations', label: 'Verify third-party integrations', desc: 'CRM, API gateways, email services, etc.' }
  ];
  const allChecked = items.every(i => checks[i.key]);
  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Production Verification</h3>
      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-5 rounded-lg mb-6">
        <h4 className="font-semibold text-yellow-800 mb-1">⚠️ Production Deployment In Progress</h4>
        <p className="text-sm text-yellow-700">Please verify all critical functionality below.</p>
      </div>
      <div className="space-y-3 mb-6">
        {items.map(item => (
          <div key={item.key} className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3 cursor-pointer hover:bg-gray-100"
            onClick={() => setChecks(prev => ({ ...prev, [item.key]: !prev[item.key] }))}>
            <input type="checkbox" checked={!!checks[item.key]} onChange={() => {}} className="mt-1 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
            <div>
              <h4 className="font-semibold text-sm text-gray-900">{item.label}</h4>
              <p className="text-xs text-gray-500">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-3">
        <button onClick={() => onUpdateProject(project?.projectId, { status: 'completed' })}
          className={`px-5 py-2.5 rounded-lg text-sm font-medium ${allChecked ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} disabled={!allChecked}>
          ✓ Confirm Verification Complete
        </button>
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
      </div>
    </div>
  );
};

const AddRequirementModal = ({ onClose, onSubmit, projectId }) => {
  const [form, setForm] = useState({ title: '', description: '', reqType: 'functional', priority: 'medium' });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSubmit({ ...form, projectId }); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Add Requirement</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400 focus:border-teal-400" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.reqType} onChange={e => setForm({ ...form, reqType: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="functional">Functional</option><option value="security">Security</option><option value="performance">Performance</option><option value="usability">Usability</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Add Requirement</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddTestCaseModal = ({ onClose, onSubmit, projectId }) => {
  const [form, setForm] = useState({ title: '', module: 'Authentication', priority: 'high', prerequisites: '', steps: [{ action: '', expected: '' }] });
  const addStep = () => setForm({ ...form, steps: [...form.steps, { action: '', expected: '' }] });
  const updateStep = (i, field, val) => { const s = [...form.steps]; s[i][field] = val; setForm({ ...form, steps: s }); };
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSubmit({ ...form, projectId }); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">Add Test Case</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
              <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option>Authentication</option><option>Settlement Calculator</option><option>Payment Processing</option><option>Reporting</option><option>Security</option><option>Performance</option><option>UI</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prerequisites</label>
            <textarea value={form.prerequisites} onChange={e => setForm({ ...form, prerequisites: e.target.value })} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Test Steps</label>
            {form.steps.map((step, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 mb-2">
                <h5 className="text-xs font-semibold text-gray-600 mb-2">Step {i + 1}</h5>
                <input type="text" placeholder="Action" value={step.action} onChange={e => updateStep(i, 'action', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm mb-2 focus:ring-2 focus:ring-teal-400" />
                <input type="text" placeholder="Expected Result" value={step.expected} onChange={e => updateStep(i, 'expected', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-teal-400" />
              </div>
            ))}
            <button type="button" onClick={addStep} className="text-xs text-teal-600 font-medium hover:text-teal-700">+ Add Step</button>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Add Test Case</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReportBugModal = ({ onClose, onSubmit, projectId, testCases, prefillTc }) => {
  const [form, setForm] = useState({ title: '', testCaseId: prefillTc?.id || '', severity: 'medium', module: prefillTc?.module || 'Authentication', stepsToReproduce: '', expectedResult: '', actualResult: '', environment: 'UAT' });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSubmit({ ...form, projectId, testCaseId: form.testCaseId || null }); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">🐛 Report Bug</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bug Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Test Case</label>
              <select value={form.testCaseId} onChange={e => setForm({ ...form, testCaseId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="">None</option>
                {(testCases || []).map(tc => <option key={tc.id} value={tc.id}>{tc.tcCode} - {tc.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
              <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option>Authentication</option><option>Settlement Calculator</option><option>Payment Processing</option><option>Reporting</option><option>User Management</option><option>UI</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
              <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option>DEV</option><option>UAT</option><option>Staging</option><option>Production</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Steps to Reproduce</label>
            <textarea value={form.stepsToReproduce} onChange={e => setForm({ ...form, stepsToReproduce: e.target.value })} rows={3} placeholder="1. Navigate to...&#10;2. Click on...&#10;3. Observe..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Result</label>
            <textarea value={form.expectedResult} onChange={e => setForm({ ...form, expectedResult: e.target.value })} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Actual Result</label>
            <textarea value={form.actualResult} onChange={e => setForm({ ...form, actualResult: e.target.value })} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Submit Bug Report</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditBugModal = ({ bug, onClose, onSave, onDelete }) => {
  const [form, setForm] = useState({
    title: bug.title || '', severity: bug.severity || 'medium', status: bug.status || 'open',
    module: bug.module || '', assignedTo: bug.assignedTo || '', environment: bug.environment || 'DEV',
    stepsToReproduce: bug.stepsToReproduce || '', expectedResult: bug.expectedResult || '', actualResult: bug.actualResult || ''
  });
  const handleSubmit = (e) => { e.preventDefault(); onSave(bug.id, form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">Edit Bug — {bug.bugCode}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" required />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="open">Open</option><option value="in_progress">In Progress</option><option value="resolved">Resolved</option><option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
              <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option>DEV</option><option>UAT</option><option>Staging</option><option>Production</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
              <select value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400">
                <option value="">None</option><option>Authentication</option><option>Settlement Calculator</option><option>Payment Processing</option><option>Reporting</option><option>User Management</option><option>UI</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
              <input type="text" value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })} placeholder="Developer name"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Steps to Reproduce</label>
            <textarea value={form.stepsToReproduce} onChange={e => setForm({ ...form, stepsToReproduce: e.target.value })} rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Result</label>
            <textarea value={form.expectedResult} onChange={e => setForm({ ...form, expectedResult: e.target.value })} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Actual Result</label>
            <textarea value={form.actualResult} onChange={e => setForm({ ...form, actualResult: e.target.value })} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-400" />
          </div>
          <div className="flex justify-between items-center pt-2">
            <button type="button" onClick={() => { if (confirm('Delete this bug?')) onDelete(bug.id); }}
              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium">Delete Bug</button>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">Save Changes</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const QmsSidebar = ({ data, projects, currentProjectId, onProjectChange, onReportBug, onEditBug }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Project</h3>
      <select value={currentProjectId || ''} onChange={e => onProjectChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:ring-2 focus:ring-teal-400">
        {(projects || []).map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
      </select>
      {data?.project && (
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">QA Lead</span><span className="font-medium">{data.project.qaLead || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Environment</span><span className="font-medium">{data.project.environment || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Stage</span><span className="font-medium capitalize">{data.project.currentStage || '-'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Status</span><span className="font-medium capitalize">{data.project.status || '-'}</span></div>
        </div>
      )}
    </div>
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Bugs</h3>
      {(data?.bugs || []).slice(0, 3).map(b => {
        const bs = qmsSeverityStyle(b.severity);
        return (
          <div key={b.id} className="bg-gray-50 rounded-lg p-3 mb-2 cursor-pointer hover:bg-gray-100" onClick={() => onEditBug(b)} style={{ borderLeftWidth: '3px', borderLeftColor: bs.color }}>
            <div className="flex justify-between items-center">
              <div className="font-semibold text-xs text-gray-900">{b.bugCode}</div>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize" style={{ background: bs.bg, color: bs.color }}>{b.severity}</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{b.title}</div>
            <div className="text-[10px] text-gray-400 mt-1 capitalize">{b.status}</div>
          </div>
        );
      })}
      <button onClick={() => onReportBug(null)} className="w-full mt-2 px-3 py-2 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200">+ Report New Bug</button>
    </div>
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Stats</h3>
      <div className="space-y-2">
        {[
          { label: 'Test Cases', value: data?.metrics?.testCases?.total || 0 },
          { label: 'Bugs Reported', value: data?.metrics?.bugs?.total || 0 },
          { label: 'Bugs Resolved', value: data?.metrics?.bugs?.resolved || 0 },
          { label: 'Pass Rate', value: `${data?.metrics?.passRate || 0}%`, color: '#20c997' }
        ].map(s => (
          <div key={s.label} className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">{s.label}</div>
            <div className="text-lg font-bold" style={{ color: s.color || '#1f2937' }}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SendQaEodModal = ({ onClose }) => {
  const [userName, setUserName] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const handleSend = async () => {
    if (!userName.trim()) { setError('Enter the QA Lead name'); return; }
    setSending(true); setError('');
    const res = await ModulesModel.sendQualityEodSummary(userName.trim());
    setSending(false);
    if (res?.success) setResult(res);
    else setError(res?.error || 'Failed to send QA EOD summary');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">📤 QA EOD Summary</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {result ? (
          <div className="text-center py-4">
            <div className="text-4xl mb-3">✅</div>
            <p className="font-medium text-gray-800 mb-3">QA EOD summary sent to Slack!</p>
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-left space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Bugs reported + TC executed</span><span className="font-medium text-green-600">{result.totals?.total_completed}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Open bugs assigned</span><span className="font-medium text-blue-600">{result.totals?.total_in_progress}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Pending (TC + requirements)</span><span className="font-medium text-gray-600">{result.totals?.total_pending}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Slack channel</span><span className="font-medium">{result.slackChannel}</span></div>
            </div>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">Close</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Sends today's QA activity — bugs reported, test cases executed, requirements created,
              open bugs assigned, and pending test cases.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">QA Lead Name</label>
            <input
              type="text" value={userName} onChange={e => setUserName(e.target.value)}
              placeholder="e.g. Maria Santos"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <p className="text-xs text-gray-400 mt-1">Must match the name used in bug reports and test case execution records.</p>
            {error && <p className="text-red-500 text-sm mt-2 bg-red-50 p-2 rounded-lg">{error}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSend} disabled={sending} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50">
                {sending ? 'Sending…' : 'Send QA EOD'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const QmsModule = ({ data, stage, onStageChange, onApproveReq, onRejectReq, onAddReq, onUpdateProject, onAddTestCase, onPassTC, onFailTC, onReportBug, onEditBug, onProjectChange, onSendEod, onEodSettings }) => {
  const renderStage = () => {
    switch (stage) {
      case 'requirements': return <QmsRequirementsStage requirements={data?.requirements} onApprove={onApproveReq} onReject={onRejectReq} onAdd={onAddReq} onNext={() => onStageChange('testplan')} />;
      case 'testplan': return <QmsTestPlanStage project={data?.project} onUpdate={onUpdateProject} onNext={() => onStageChange('testcases')} onBack={() => onStageChange('requirements')} />;
      case 'testcases': return <QmsTestCasesStage testCases={data?.testCases} onAdd={onAddTestCase} onNext={() => onStageChange('testing')} onBack={() => onStageChange('testplan')} />;
      case 'testing': return <QmsTestingStage testCases={data?.testCases} bugs={data?.bugs} metrics={data?.metrics} onPass={onPassTC} onFail={onFailTC} onReportBug={onReportBug} onEditBug={onEditBug} onNext={() => onStageChange('uat')} onBack={() => onStageChange('testcases')} />;
      case 'uat': return <QmsUatStage metrics={data?.metrics} bugs={data?.bugs} project={data?.project} onUpdateProject={onUpdateProject} onEditBug={onEditBug} onNext={() => onStageChange('verification')} onBack={() => onStageChange('testing')} />;
      case 'verification': return <QmsVerificationStage project={data?.project} onUpdateProject={onUpdateProject} onBack={() => onStageChange('uat')} />;
      default: return null;
    }
  };
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">QA Management System</h1>
          <p className="text-gray-600 mt-1">Complete QA workflow management — requirements to production</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onEodSettings} className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 font-medium border border-gray-300">⚙️ EOD Settings</button>
          <button onClick={onSendEod} className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 font-medium">📤 QA EOD</button>
        </div>
      </div>
      <QmsWorkflowSteps currentStage={stage} onStageChange={onStageChange} />
      <QmsMetrics metrics={data?.metrics} stage={stage} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">{renderStage()}</div>
        <div><QmsSidebar data={data} projects={data?.projects} currentProjectId={data?.project?.projectId} onProjectChange={onProjectChange} onReportBug={onReportBug} onEditBug={onEditBug} /></div>
      </div>
    </div>
  );
};

// ============================================================================
// QUICKTRACK VIEWS
// ============================================================================

const getQtPriorityStyle = (p) => {
  const styles = { urgent: { bg: '#fef2f2', color: '#dc2626' }, high: { bg: '#fff7ed', color: '#ea580c' }, medium: { bg: '#fffbeb', color: '#d97706' }, low: { bg: '#f0fdf4', color: '#16a34a' } };
  return styles[p] || styles.medium;
};
const getQtStatusStyle = (s) => {
  const styles = { open: { bg: '#eff6ff', color: '#2563eb' }, in_progress: { bg: '#fefce8', color: '#ca8a04' }, done: { bg: '#f0fdf4', color: '#16a34a' } };
  return styles[s] || styles.open;
};
const getQtCategoryStyle = (c) => {
  const styles = { bug: { bg: '#fef2f2', color: '#dc2626' }, feature: { bg: '#f0f9ff', color: '#0284c7' }, chore: { bg: '#f5f3ff', color: '#7c3aed' }, general: { bg: '#f9fafb', color: '#6b7280' } };
  return styles[c] || styles.general;
};
const QuickTrackMetrics = ({ metrics }) => {
  const cards = [
    { label: 'Total Tasks', value: metrics?.total || 0, icon: '📋', color: '#667eea' },
    { label: 'Open', value: metrics?.open || 0, icon: '🔵', color: '#2563eb' },
    { label: 'In Progress', value: metrics?.inProgress || 0, icon: '🟡', color: '#ca8a04' },
    { label: 'Done', value: metrics?.done || 0, icon: '🟢', color: '#16a34a' }
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">{c.label}</span>
            <span className="text-2xl">{c.icon}</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: c.color }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
};

const QuickTrackFilters = ({ filterStatus, filterPriority, filterCategory, onFilterStatus, onFilterPriority, onFilterCategory, onReset, onAddTask }) => (
  <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-gray-500 uppercase">Status</label>
        <select value={filterStatus} onChange={e => onFilterStatus(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[140px] focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-gray-500 uppercase">Priority</label>
        <select value={filterPriority} onChange={e => onFilterPriority(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[140px] focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
          <option value="">All Priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-gray-500 uppercase">Category</label>
        <select value={filterCategory} onChange={e => onFilterCategory(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[140px] focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
          <option value="">All Categories</option>
          <option value="bug">Bug</option>
          <option value="feature">Feature</option>
          <option value="chore">Chore</option>
          <option value="general">General</option>
        </select>
      </div>
      <button onClick={onReset} className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200">Reset</button>
      <div className="flex-1" />
      <button onClick={onAddTask} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2">
        <span className="text-lg leading-none">+</span> New Task
      </button>
    </div>
  </div>
);

const QuickTrackTable = ({ tasks, onStatusChange, onDelete, onEdit }) => {
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
  const isOverdue = (t) => t.status !== 'done' && t.dueDate && t.dueDate < new Date().toISOString().split('T')[0];

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700">Tasks</h2>
        <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{(tasks || []).length} items</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Project</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Assigned To</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Time (min)</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Due Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(tasks || []).map(t => {
              const ps = getQtPriorityStyle(t.priority);
              const cs = getQtCategoryStyle(t.category);
              return (
                <tr key={t.id} className={`hover:bg-gray-50 ${t.status === 'done' ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <div className={`text-sm font-semibold text-gray-900 ${t.status === 'done' ? 'line-through' : ''}`}>{t.title}</div>
                    {t.description && <div className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{t.description}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {t.projectName ? <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{t.projectName}</span> : <span className="text-xs text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold capitalize" style={{ background: ps.bg, color: ps.color }}>{t.priority}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-medium capitalize" style={{ background: cs.bg, color: cs.color }}>{t.category}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{t.assignedTo || '-'}</td>
                  <td className="px-4 py-3">
                    {(t.estimated_minutes > 0 || t.actual_minutes > 0) ? (
                      <div className="text-xs space-y-0.5">
                        <div className="text-gray-500">Est: <span className="font-medium text-gray-700">{t.estimated_minutes || 0}m</span></div>
                        <div className="text-gray-500">Act: <span className={`font-medium ${t.actual_minutes > t.estimated_minutes && t.estimated_minutes > 0 ? 'text-red-600' : 'text-gray-700'}`}>{t.actual_minutes || 0}m</span></div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm ${isOverdue(t) ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                      {formatDate(t.dueDate)}{isOverdue(t) ? ' !' : ''}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <select value={t.status} onChange={e => onStatusChange(t.id, { status: e.target.value })}
                      className="border border-gray-300 rounded px-2 py-1 text-xs font-medium focus:ring-2 focus:ring-indigo-400"
                      style={{ background: getQtStatusStyle(t.status).bg, color: getQtStatusStyle(t.status).color }}>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="done">Done</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => onEdit(t)} className="text-xs text-indigo-500 hover:text-indigo-700 font-medium" title="Edit task">Edit</button>
                      <button onClick={() => onDelete(t.id)} className="text-gray-400 hover:text-red-500 text-sm" title="Delete task">&times;</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(!tasks || tasks.length === 0) && (
          <div className="p-12 text-center text-gray-400">No tasks match the selected filters.</div>
        )}
      </div>
    </div>
  );
};

const AddQuickTaskModal = ({ onClose, onSubmit, projects, teamMembers = [] }) => {
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', category: 'general', assignedTo: '', dueDate: '', projectId: '', estimatedMinutes: '', actualMinutes: '' });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">New Quick Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="general">General</option>
                <option value="bug">Bug</option>
                <option value="feature">Feature</option>
                <option value="chore">Chore</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
              <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="">Unassigned</option>
                {teamMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
            </div>
          </div>
          {projects && projects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="">No Project</option>
                {projects.map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Est. Time (min)</label>
              <input type="number" min="0" placeholder="0" value={form.estimatedMinutes}
                onChange={e => setForm({ ...form, estimatedMinutes: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Actual Time (min)</label>
              <input type="number" min="0" placeholder="0" value={form.actualMinutes}
                onChange={e => setForm({ ...form, actualMinutes: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Create Task</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditQuickTaskModal = ({ task, onClose, onSubmit, projects, user, teamMembers = [] }) => {
  const [form, setForm] = useState({
    title: task.title || '',
    description: task.description || '',
    priority: task.priority || 'medium',
    category: task.category || 'general',
    assignedTo: task.assignedTo || '',
    dueDate: task.dueDate || '',
    projectId: task.projectId || '',
    estimatedMinutes: task.estimatedMinutes || task.estimated_minutes || '',
    actualMinutes: task.actualMinutes || task.actual_minutes || ''
  });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onSubmit(task.id, form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">Edit Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="general">General</option>
                <option value="bug">Bug</option>
                <option value="feature">Feature</option>
                <option value="chore">Chore</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
              <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="">Unassigned</option>
                {teamMembers.map(m => <option key={m.user_id} value={m.user_name}>{m.user_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
            </div>
          </div>
          {projects && projects.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400">
                <option value="">No Project</option>
                {projects.map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Est. Time (min)</label>
              <input type="number" min="0" placeholder="0" value={form.estimatedMinutes}
                onChange={e => setForm({ ...form, estimatedMinutes: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Actual Time (min)</label>
              <input type="number" min="0" placeholder="0" value={form.actualMinutes}
                onChange={e => setForm({ ...form, actualMinutes: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Save Changes</button>
          </div>
        </form>
        <div className="px-6 pb-6">
          <TaskComments taskRef={task.id} taskSource="quicktrack" userName={user?.user_name || user?.name} />
        </div>
      </div>
    </div>
  );
};

const QuickTrackModule = ({ data, filterStatus, filterPriority, filterCategory, onFilterStatus, onFilterPriority, onFilterCategory, onResetFilters, onAddTask, onStatusChange, onDelete, onEdit }) => {
  // Client-side filtering
  let tasks = data?.tasks || [];
  if (filterStatus) tasks = tasks.filter(t => t.status === filterStatus);
  if (filterPriority) tasks = tasks.filter(t => t.priority === filterPriority);
  if (filterCategory) tasks = tasks.filter(t => t.category === filterCategory);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">QuickTrack</h1>
        <p className="text-gray-600 mt-1">Ad-hoc task tracker for quick turnaround items</p>
      </div>
      <QuickTrackMetrics metrics={data?.metrics} />
      <QuickTrackFilters
        filterStatus={filterStatus} filterPriority={filterPriority} filterCategory={filterCategory}
        onFilterStatus={onFilterStatus} onFilterPriority={onFilterPriority} onFilterCategory={onFilterCategory}
        onReset={onResetFilters} onAddTask={onAddTask}
      />
      <QuickTrackTable tasks={tasks} onStatusChange={onStatusChange} onDelete={onDelete} onEdit={onEdit} />
    </div>
  );
};

// ============================================================================
// VENDOR EVENT OBSERVABILITY VIEWS
// ============================================================================

const getVendorEventStatusStyle = (status) => {
  const styles = {
    Accepted: { bg: '#dcfce7', color: '#166534' },
    Rejected: { bg: '#fff7ed', color: '#9a3412' },
    Failed: { bg: '#fee2e2', color: '#991b1b' },
    Pending: { bg: '#f1f5f9', color: '#475569' }
  };
  return styles[status] || styles.Pending;
};

const getWorkflowStyle = (wf) => {
  const styles = {
    Creation: { bg: '#dbeafe', color: '#1e40af' },
    Reattribution: { bg: '#f3e8ff', color: '#6b21a8' },
    Rejection: { bg: '#fef3c7', color: '#92400e' },
    Reengagement: { bg: '#d1fae5', color: '#065f46' }
  };
  return styles[wf] || { bg: '#f1f5f9', color: '#475569' };
};

const VendorEventsMetrics = ({ metrics }) => {
  const cards = [
    { label: 'Total Leads', value: metrics?.totalLeads ?? 0, sub: 'Matching current filters', color: '#3b82f6' },
    { label: "Today's Leads", value: metrics?.todayLeads ?? 0, sub: 'Pacific Time', color: '#22c55e' },
    { label: 'Active Partners', value: metrics?.uniquePartners ?? 0, sub: 'Distinct partner names', color: '#f59e0b' },
    { label: 'Top Partner', value: metrics?.topPartner || '—', sub: 'Highest lead volume', color: '#8b5cf6' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{c.label}</div>
          <div className="text-3xl font-bold mb-1" style={{ color: c.color }}>{c.value}</div>
          <div className="text-xs text-gray-400">{c.sub}</div>
        </div>
      ))}
    </div>
  );
};

const VendorEventsFilters = ({ events, vendors, filterVendor, filterWorkflow, filterStatus, onFilterVendor, onFilterWorkflow, onFilterStatus, onReset }) => {
  const allEvents = events || [];
  const uniqueWorkflows = [...new Set(allEvents.map(e => e.workflow).filter(Boolean))].sort();
  const uniqueStatuses  = [...new Set(allEvents.map(e => e.status).filter(Boolean))].sort();
  const uniqueVendors   = [...new Set(allEvents.map(e => e.partner || e.vendor).filter(Boolean))].sort();
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-5 mb-6">
      <h2 className="text-sm font-semibold text-gray-700 mb-3">Filters</h2>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Partner</label>
          <select value={filterVendor} onChange={e => onFilterVendor(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[170px] focus:ring-2 focus:ring-red-400 focus:border-red-400">
            <option value="">All Partners</option>
            {uniqueVendors.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Workflow</label>
          <select value={filterWorkflow} onChange={e => onFilterWorkflow(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[170px] focus:ring-2 focus:ring-red-400 focus:border-red-400">
            <option value="">All Workflows</option>
            {uniqueWorkflows.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase">Status</label>
          <select value={filterStatus} onChange={e => onFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[170px] focus:ring-2 focus:ring-red-400 focus:border-red-400">
            <option value="">All Statuses</option>
            {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={onReset} className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200">Reset</button>
      </div>
    </div>
  );
};

const VendorEventsTable = ({ events }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const allEvents = events || [];
  const totalPages = Math.max(1, Math.ceil(allEvents.length / PAGE_SIZE));
  const pageEvents = allEvents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); setExpandedId(null); }, [events]);
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) + ' PT';
  };
  const utmSourceColor = (src) => {
    if (!src) return { bg: '#f1f5f9', color: '#64748b' };
    const s = src.toLowerCase();
    if (s.includes('google')) return { bg: '#dbeafe', color: '#1e40af' };
    if (s.includes('facebook') || s.includes('meta')) return { bg: '#ede9fe', color: '#5b21b6' };
    if (s.includes('email') || s.includes('mail')) return { bg: '#dcfce7', color: '#166534' };
    if (s.includes('organic') || s.includes('seo')) return { bg: '#fef9c3', color: '#854d0e' };
    return { bg: '#f1f5f9', color: '#374151' };
  };
  const dash = <span className="text-gray-300">—</span>;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700">Lead Events</h2>
        <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{(events || []).length} records</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Contact ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Partner</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Workflow</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Time</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">UTM Source</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">UTM Campaign</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Lead Src ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Fired At</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageEvents.map(ev => {
              const ss = getVendorEventStatusStyle(ev.status);
              const ws = getWorkflowStyle(ev.workflow);
              const utmSrc = utmSourceColor(ev.utmSource);
              const rowKey = ev.id || ev.leadId;
              const isExpanded = expandedId === rowKey;
              const hasExtra = ev.utmMedium || ev.utmContent || ev.utmNetwork || ev.clickId;
              return (
                <React.Fragment key={rowKey}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{ev.leadId || dash}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{ev.partner || ev.vendor || dash}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap" style={{ background: ws.bg, color: ws.color }}>{ev.workflow}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ev.date || dash}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ev.time || dash}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {ev.utmSource
                        ? <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ background: utmSrc.bg, color: utmSrc.color }}>{ev.utmSource}</span>
                        : dash}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[160px]">
                      {ev.utmCampaign ? <span title={ev.utmCampaign} className="block truncate">{ev.utmCampaign}</span> : dash}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {ev.leadSourceId
                        ? <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-mono">{ev.leadSourceId}</span>
                        : dash}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(ev.firedAt)}</td>
                    <td className="px-4 py-3">
                      {hasExtra && (
                        <button onClick={() => setExpandedId(isExpanded ? null : rowKey)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
                          {isExpanded ? 'Less ▲' : 'More ▼'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-blue-50">
                      <td colSpan={10} className="px-6 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          {ev.utmMedium && (
                            <div><span className="text-gray-500 uppercase font-semibold block">UTM Medium</span><span className="text-gray-800">{ev.utmMedium}</span></div>
                          )}
                          {ev.utmContent && (
                            <div><span className="text-gray-500 uppercase font-semibold block">UTM Content</span><span className="text-gray-800">{ev.utmContent}</span></div>
                          )}
                          {ev.utmNetwork && (
                            <div><span className="text-gray-500 uppercase font-semibold block">UTM Network</span><span className="text-gray-800">{ev.utmNetwork}</span></div>
                          )}
                          {ev.clickId && (
                            <div><span className="text-gray-500 uppercase font-semibold block">Click ID</span><span className="text-gray-800 font-mono">{ev.clickId}</span></div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {allEvents.length === 0 && (
          <div className="p-12 text-center text-gray-400">No records match the selected filters.</div>
        )}
        {allEvents.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, allEvents.length)}–{Math.min(page * PAGE_SIZE, allEvents.length)} of {allEvents.length} records
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">«</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '...'
                  ? <span key={`ellipsis-${i}`} className="px-2 py-1 text-xs text-gray-400">…</span>
                  : <button key={p} onClick={() => setPage(p)}
                      className={`px-2.5 py-1 text-xs rounded border font-medium ${page === p ? 'bg-red-600 text-white border-red-600' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>{p}</button>
                )}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AddVendorModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ vendorName: '', vendorUrl: '', contactEmail: '' });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.vendorName.trim()) return; onSubmit(form); };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Add New Vendor</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name *</label>
            <input type="text" value={form.vendorName} onChange={e => setForm({ ...form, vendorName: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor URL</label>
            <input type="url" value={form.vendorUrl} onChange={e => setForm({ ...form, vendorUrl: e.target.value })}
              placeholder="https://example.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
            <input type="email" value={form.contactEmail} onChange={e => setForm({ ...form, contactEmail: e.target.value })}
              placeholder="contact@vendor.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Add Vendor</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const getHttpStatusStyle = (code) => {
  if (!code && code !== 0) return { bg: '#f1f5f9', color: '#64748b', label: 'Not Checked' };
  if (code === 0) return { bg: '#fee2e2', color: '#991b1b', label: 'Unreachable' };
  if (code >= 200 && code < 300) return { bg: '#dcfce7', color: '#166534', label: `${code} OK` };
  if (code >= 300 && code < 400) return { bg: '#dbeafe', color: '#1e40af', label: `${code} Redirect` };
  if (code >= 400 && code < 500) return { bg: '#fff7ed', color: '#9a3412', label: `${code} Client Error` };
  return { bg: '#fee2e2', color: '#991b1b', label: `${code} Server Error` };
};

const VENDOR_WORKFLOWS = [
  { key: 'New Contact',    color: 'bg-blue-100 text-blue-700' },
  { key: 'Reattribution',  color: 'bg-purple-100 text-purple-700' },
  { key: 'Reengagement',   color: 'bg-amber-100 text-amber-700' },
  { key: 'Reject',         color: 'bg-red-100 text-red-700' },
];

const VendorList = ({ vendors, events, onDelete }) => {
  // Count events per vendor per workflow
  const counts = {};
  (events || []).forEach(ev => {
    const name = ev.partner || ev.vendor;
    if (!name) return;
    if (!counts[name]) counts[name] = {};
    const wf = ev.workflow || '';
    counts[name][wf] = (counts[name][wf] || 0) + 1;
  });
  // Match workflow key loosely (e.g. "Reject" matches "Rejection")
  const getCount = (vendorName, wfKey) => {
    const vc = counts[vendorName] || {};
    return Object.entries(vc).reduce((sum, [k, n]) => sum + (k.toLowerCase().startsWith(wfKey.toLowerCase()) ? n : 0), 0);
  };
  return (
  <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
    <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
      <h2 className="text-sm font-semibold text-gray-700">Registered Vendors</h2>
      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{(vendors || []).length} vendors</span>
    </div>
    {(!vendors || vendors.length === 0) ? (
      <div className="p-8 text-center text-gray-400 text-sm">No vendors registered yet. Click "+ New Vendor" to add one.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Partner Name</th>
              {VENDOR_WORKFLOWS.map(w => (
                <th key={w.key} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">{w.key}</th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(vendors || []).map(v => (
              <tr key={v.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-semibold text-gray-900">{v.vendorName}</td>
                {VENDOR_WORKFLOWS.map(w => {
                  const n = getCount(v.vendorName, w.key);
                  return (
                    <td key={w.key} className="px-4 py-3">
                      {n > 0
                        ? <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${w.color}`}>{n}</span>
                        : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                  );
                })}
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${v.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {v.status || 'active'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => { if (confirm(`Delete vendor "${v.vendorName}"?`)) onDelete(v.id); }}
                    className="text-xs text-red-500 hover:text-red-700 font-medium">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
  );
};

const downloadCsv = (filename, headers, rows) => {
  const escape = (val) => {
    const s = String(val ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const toPacificDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
};

const toPacificDateTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const VendorEventsModule = ({ data, filterVendor, filterWorkflow, filterStatus, onFilterVendor, onFilterWorkflow, onFilterStatus, onResetFilters, onAddVendor, onDeleteVendor }) => {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

  const isTodayPacific = (dateStr) => toPacificDate(dateStr) === today;

  const handleDownloadEvents = () => {
    const events = (data?.events || []).filter(ev => isTodayPacific(ev.firedAt));
    const headers = ['Lead ID', 'Vendor', 'Workflow', 'Status', 'Fired At (PT)'];
    const rows = events.map(ev => [ev.leadId, ev.vendor, ev.workflow, ev.status, toPacificDateTime(ev.firedAt)]);
    downloadCsv(`vendor-events-${today}.csv`, headers, rows);
  };

  const handleDownloadVendors = () => {
    const vendors = data?.vendors || [];
    const headers = ['Vendor Name', 'URL', 'Contact Email', 'Status', 'Last Check Status', 'Last Checked At (PT)'];
    const rows = vendors.map(v => [v.vendorName, v.vendorUrl || '', v.contactEmail || '', v.status, v.lastCheckStatus ?? '', v.lastCheckAt ? toPacificDateTime(v.lastCheckAt) : '']);
    downloadCsv(`vendors-${today}.csv`, headers, rows);
  };

  const todayEvents = (data?.events || []).filter(ev => isTodayPacific(ev.firedAt));

  return (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="flex justify-between items-start mb-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Vendor Event Observability</h1>
        <p className="text-gray-600 mt-1">Real-time monitoring of vendor lead events across all workflow lines</p>
      </div>
      <div className="flex gap-2">
        <div className="relative group">
          <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 flex items-center gap-2 whitespace-nowrap border border-gray-300">
            Export CSV
          </button>
          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 hidden group-hover:block min-w-[200px]">
            <button onClick={handleDownloadEvents} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg">
              Today's Events ({todayEvents.length})
            </button>
            <button onClick={handleDownloadVendors} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg border-t border-gray-100">
              Vendor List ({(data?.vendors || []).length})
            </button>
          </div>
        </div>
        <button onClick={onAddVendor} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-2 whitespace-nowrap">
          <span className="text-lg leading-none">+</span> New Vendor
        </button>
      </div>
    </div>
    <VendorEventsMetrics metrics={data?.metrics} />
    <VendorList vendors={data?.vendors} events={data?.events} onDelete={onDeleteVendor} />
    <VendorEventsFilters
      events={data?.events}
      vendors={data?.vendors}
      filterVendor={filterVendor} filterWorkflow={filterWorkflow} filterStatus={filterStatus}
      onFilterVendor={onFilterVendor} onFilterWorkflow={onFilterWorkflow} onFilterStatus={onFilterStatus}
      onReset={onResetFilters}
    />
    <VendorEventsTable events={data?.events} />
  </div>
  );
};

// ============================================================================
// LOGIN VIEW
// ============================================================================

const AuthInput = ({ label, type = 'text', value, onChange, placeholder, required, autoComplete }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      required={required} autoComplete={autoComplete}
      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
    />
  </div>
);

const AuthAlert = ({ type, message }) => type === 'error' ? (
  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />{message}
  </div>
) : (
  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{message}</div>
);

const LoginBranding = () => (
  <div className="text-center mb-8">
    <div className="text-5xl mb-3">🛰️</div>
    <h1 className="text-3xl font-bold text-white tracking-wide">ORBIT</h1>
    <p className="text-blue-200 mt-1 text-sm">Enterprise Operations Hub</p>
  </div>
);

// ---- Forgot Password panel ----
const ForgotPasswordPanel = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      setResult({ ok: true, data });
    } catch { setResult({ ok: false, msg: 'Network error. Please try again.' }); }
    setLoading(false);
  };

  if (result?.ok) {
    return (
      <div className="p-8 space-y-4">
        <div className="text-center">
          <div className="text-4xl mb-3">✉️</div>
          <h3 className="font-semibold text-gray-900">Reset Link Generated</h3>
          <p className="text-sm text-gray-500 mt-1">{result.data.message}</p>
        </div>
        {result.data.resetUrl && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-medium text-blue-700 mb-1">Reset URL (share securely):</p>
            <p className="text-xs text-blue-600 break-all font-mono">{result.data.resetUrl}</p>
            <p className="text-xs text-blue-500 mt-1">Expires in: {result.data.expiresIn}</p>
          </div>
        )}
        <button onClick={onBack} className="w-full text-sm text-blue-600 hover:text-blue-800 font-medium">← Back to Sign In</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-8 space-y-4">
      <div className="text-center mb-2">
        <h3 className="font-semibold text-gray-900">Reset your password</h3>
        <p className="text-xs text-gray-500 mt-1">Enter your email and we'll generate a secure reset link</p>
      </div>
      {result && !result.ok && <AuthAlert type="error" message={result.msg} />}
      <AuthInput label="Work Email" type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="you@company.com" required autoComplete="email" />
      <button type="submit" disabled={loading}
        className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors">
        {loading ? 'Generating…' : 'Send Reset Link'}
      </button>
      <button type="button" onClick={onBack} className="w-full text-sm text-gray-500 hover:text-gray-700">← Back to Sign In</button>
    </form>
  );
};

// ---- Reset Password page (standalone, shown when ?reset_token= in URL) ----
const ResetPasswordView = ({ token, onDone }) => {
  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: form.newPassword, confirmPassword: form.confirmPassword })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Reset failed'); }
      else { setSuccess(true); }
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <LoginBranding />
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {success ? (
            <div className="p-8 text-center space-y-4">
              <div className="text-4xl">✅</div>
              <h3 className="font-semibold text-gray-900">Password Reset Successfully</h3>
              <p className="text-sm text-gray-500">You can now sign in with your new password.</p>
              <button onClick={onDone} className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700">
                Go to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-8 space-y-4">
              <div className="text-center mb-2">
                <h3 className="font-semibold text-gray-900">Set new password</h3>
                <p className="text-xs text-gray-500 mt-1">Choose a strong password of at least 8 characters</p>
              </div>
              {error && <AuthAlert type="error" message={error} />}
              <AuthInput label="New Password" type="password" value={form.newPassword}
                onChange={e => set('newPassword', e.target.value)} placeholder="At least 8 characters"
                required autoComplete="new-password" />
              <AuthInput label="Confirm New Password" type="password" value={form.confirmPassword}
                onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••"
                required autoComplete="new-password" />
              <button type="submit" disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors">
                {loading ? 'Saving…' : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Main Login View ----
const LoginView = ({ onLogin }) => {
  const [mode, setMode] = useState('login');        // 'login' | 'register' | 'forgot'
  const [authMethod, setAuthMethod] = useState('password');
  const [form, setForm] = useState({ email: '', password: '', name: '', confirmPassword: '' });
  const [ssoIssuer, setSsoIssuer] = useState('');
  const [ssoEmail, setSsoEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const clearMessages = () => setError('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages(); setLoading(true);
    try {
      const endpoint = mode === 'register' ? '/api/auth/register' : '/api/auth/login';
      const body = mode === 'register'
        ? { email: form.email, name: form.name, password: form.password, confirmPassword: form.confirmPassword }
        : { email: form.email, password: form.password };
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Authentication failed');
      else onLogin(data.token, data.user);
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    clearMessages(); setLoading(true);
    try {
      const redirectUri = window.location.origin;
      const res = await fetch(`/api/auth/google/url?redirectUri=${encodeURIComponent(redirectUri)}`);
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Google sign-in is not configured'); setLoading(false); return; }
      sessionStorage.setItem('oauth_provider', 'google');
      sessionStorage.setItem('oauth_redirect_uri', redirectUri);
      window.location.href = data.url;
    } catch { setError('Failed to initialize Google sign-in'); }
    setLoading(false);
  };

  const handleSsoLogin = async () => {
    clearMessages();
    if (!ssoIssuer && !ssoEmail) { setError('Enter your work email or SSO provider URL'); return; }
    setLoading(true);
    try {
      const redirectUri = window.location.origin;
      const params = new URLSearchParams({ redirectUri });
      if (ssoIssuer) params.set('issuerUrl', ssoIssuer);
      if (ssoEmail) params.set('email', ssoEmail);
      const res = await fetch(`/api/auth/sso/url?${params}`);
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'SSO is not configured'); setLoading(false); return; }
      sessionStorage.setItem('oauth_provider', 'sso');
      sessionStorage.setItem('oauth_redirect_uri', redirectUri);
      sessionStorage.setItem('sso_issuer', ssoIssuer);
      window.location.href = data.url;
    } catch { setError('Failed to initialize SSO sign-in'); }
    setLoading(false);
  };

  const handleDemoLogin = async () => {
    clearMessages(); setLoading(true);
    try {
      const res = await fetch('/api/auth/demo', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Demo login failed');
      else onLogin(data.token, data.user);
    } catch { setError('Demo login failed. Make sure the database is seeded.'); }
    setLoading(false);
  };

  const methodTab = (id, label, icon) => (
    <button key={id} onClick={() => { setAuthMethod(id); clearMessages(); }}
      className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${authMethod === id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
      <span className="mr-1.5">{icon}</span>{label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <LoginBranding />

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">

          {/* Forgot Password panel */}
          {mode === 'forgot' ? (
            <ForgotPasswordPanel onBack={() => { setMode('login'); clearMessages(); }} />
          ) : (
            <>
              {/* Sign In / Create Account tabs */}
              <div className="flex border-b border-gray-100">
                {[['login', 'Sign In'], ['register', 'Create Account']].map(([m, label]) => (
                  <button key={m} onClick={() => { setMode(m); clearMessages(); if (m === 'register') setAuthMethod('password'); }}
                    className={`flex-1 py-4 text-sm font-semibold transition-colors ${mode === m ? 'text-blue-700 border-b-2 border-blue-600 bg-blue-50/40' : 'text-gray-400 hover:text-gray-600'}`}>
                    {label}
                  </button>
                ))}
              </div>

              <div className="p-8">
                {/* Auth method tabs (Sign In only) */}
                {mode === 'login' && (
                  <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
                    {methodTab('password', 'Password', '🔑')}
                    {methodTab('google', 'Google', '🔵')}
                    {methodTab('sso', 'SSO', '🏢')}
                  </div>
                )}

                {error && <AuthAlert type="error" message={error} />}

                {/* PASSWORD FORM */}
                {(authMethod === 'password' || mode === 'register') && (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {mode === 'register' && (
                      <AuthInput label="Full Name" value={form.name} onChange={e => set('name', e.target.value)}
                        placeholder="John Smith" required autoComplete="name" />
                    )}
                    <AuthInput label="Work Email" type="email" value={form.email} onChange={e => set('email', e.target.value)}
                      placeholder="you@company.com" required autoComplete="email" />
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs font-medium text-gray-600">Password</label>
                        {mode === 'login' && (
                          <button type="button" onClick={() => { setMode('forgot'); clearMessages(); }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                            Forgot password?
                          </button>
                        )}
                      </div>
                      <input type="password" required value={form.password} onChange={e => set('password', e.target.value)}
                        placeholder={mode === 'register' ? 'At least 8 characters' : '••••••••'}
                        autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                    </div>
                    {mode === 'register' && (
                      <AuthInput label="Confirm Password" type="password" value={form.confirmPassword}
                        onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••"
                        required autoComplete="new-password" />
                    )}
                    <button type="submit" disabled={loading}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors">
                      {loading ? 'Please wait…' : mode === 'register' ? 'Create Account' : 'Sign In'}
                    </button>
                  </form>
                )}

                {/* GOOGLE */}
                {authMethod === 'google' && mode === 'login' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 text-center">Sign in using your Google Workspace account.</p>
                    <button onClick={handleGoogleLogin} disabled={loading}
                      className="w-full flex items-center justify-center gap-3 border-2 border-gray-200 rounded-lg py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 transition-colors">
                      <svg width="18" height="18" viewBox="0 0 18 18">
                        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                        <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
                      </svg>
                      {loading ? 'Redirecting…' : 'Continue with Google'}
                    </button>
                  </div>
                )}

                {/* SSO */}
                {authMethod === 'sso' && mode === 'login' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 text-center">Sign in via Okta, Azure AD, Ping, or any OIDC provider.</p>
                    <AuthInput label="Work Email" type="email" value={ssoEmail} onChange={e => setSsoEmail(e.target.value)} placeholder="you@company.com" />
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <div className="flex-1 h-px bg-gray-200" />or<div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <AuthInput label="SSO Provider URL" type="url" value={ssoIssuer} onChange={e => setSsoIssuer(e.target.value)} placeholder="https://your-org.okta.com" />
                    <button onClick={handleSsoLogin} disabled={loading}
                      className="w-full bg-indigo-600 text-white py-3 rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors">
                      {loading ? 'Redirecting…' : 'Continue with SSO'}
                    </button>
                  </div>
                )}

                {/* Demo */}
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <button onClick={handleDemoLogin} disabled={loading}
                    className="w-full py-2.5 rounded-lg text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 border border-dashed border-gray-200 transition-colors">
                    {loading ? 'Loading…' : '⚡ Continue with Demo Account'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-blue-200 text-xs mt-6 opacity-70">ORBIT Enterprise © {new Date().getFullYear()}</p>
      </div>
    </div>
  );
};

// ---- Change Password Modal ----
const ChangePasswordModal = ({ authToken, onClose }) => {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Failed to change password');
      else setSuccess(true);
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Shield size={16} className="text-blue-600" /> Change Password
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {success ? (
          <div className="p-6 text-center space-y-3">
            <div className="text-3xl">✅</div>
            <p className="font-medium text-gray-900">Password changed successfully</p>
            <button onClick={onClose} className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700">Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && <AuthAlert type="error" message={error} />}
            <AuthInput label="Current Password" type="password" value={form.currentPassword}
              onChange={e => set('currentPassword', e.target.value)} placeholder="••••••••"
              required autoComplete="current-password" />
            <AuthInput label="New Password" type="password" value={form.newPassword}
              onChange={e => set('newPassword', e.target.value)} placeholder="At least 8 characters"
              required autoComplete="new-password" />
            <AuthInput label="Confirm New Password" type="password" value={form.confirmPassword}
              onChange={e => set('confirmPassword', e.target.value)} placeholder="••••••••"
              required autoComplete="new-password" />
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={loading} className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">
                {loading ? 'Saving…' : 'Update Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// AUDIT TRAIL VIEW
// ============================================================================

const ACTION_BADGE = {
  CREATE:  { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Create' },
  UPDATE:  { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'Update' },
  DELETE:  { bg: 'bg-red-100',    text: 'text-red-800',    label: 'Delete' },
  LOGIN:   { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Login' },
  LOGOUT:  { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'Logout' },
  VIEW:    { bg: 'bg-sky-100',    text: 'text-sky-800',    label: 'View' },
  READ:    { bg: 'bg-sky-100',    text: 'text-sky-800',    label: 'Read' },
  EXPORT:  { bg: 'bg-amber-100',  text: 'text-amber-800',  label: 'Export' },
  IMPORT:  { bg: 'bg-teal-100',   text: 'text-teal-800',   label: 'Import' },
  APPROVE: { bg: 'bg-emerald-100',text: 'text-emerald-800',label: 'Approve' },
  REJECT:  { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Reject' },
};

const MODULE_BADGE = {
  plantrack:    { bg: 'bg-indigo-100',  text: 'text-indigo-800' },
  devops:       { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  quality:      { bg: 'bg-pink-100',    text: 'text-pink-800' },
  timeboard:    { bg: 'bg-orange-100',  text: 'text-orange-800' },
  leaveboard:   { bg: 'bg-teal-100',    text: 'text-teal-800' },
  system:       { bg: 'bg-gray-100',    text: 'text-gray-700' },
  dashboard:    { bg: 'bg-violet-100',  text: 'text-violet-800' },
  dataops:      { bg: 'bg-cyan-100',    text: 'text-cyan-800' },
  learntrack:   { bg: 'bg-rose-100',    text: 'text-rose-800' },
  quicktrack:   { bg: 'bg-yellow-100',  text: 'text-yellow-800' },
  vendorevents: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800' },
};

const AuditActionBadge = ({ action }) => {
  const a = (action || '').toUpperCase();
  const style = ACTION_BADGE[a] || { bg: 'bg-yellow-100', text: 'text-yellow-800', label: action };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label || action}
    </span>
  );
};

const AuditModuleBadge = ({ module }) => {
  const key = (module || '').toLowerCase();
  const style = MODULE_BADGE[key] || { bg: 'bg-blue-100', text: 'text-blue-800' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text} capitalize`}>
      {module || 'system'}
    </span>
  );
};

const AuditTrailView = ({ logs, stats, filters, onFilterChange, onLoadMore, hasMore, loading }) => {
  const [localFilters, setLocalFilters] = useState({ ...filters });

  const handleFilter = (key, value) => {
    const updated = { ...localFilters, [key]: value };
    setLocalFilters(updated);
  };

  const applyFilters = () => {
    onFilterChange({ ...localFilters, offset: 0 });
  };

  const resetFilters = () => {
    const empty = { module: '', action: '', entityType: '', dateFrom: '', dateTo: '', search: '' };
    setLocalFilters(empty);
    onFilterChange({ ...empty, offset: 0 });
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return ts; }
  };

  const maxModuleCount = stats?.byModule?.length ? Math.max(...stats.byModule.map(m => m.count)) : 1;

  const knownModules = ['plantrack', 'devops', 'quality', 'timeboard', 'leaveboard', 'dataops', 'learntrack', 'quicktrack', 'vendorevents', 'system', 'dashboard'];
  const knownActions = ['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'VIEW', 'READ', 'EXPORT', 'IMPORT', 'APPROVE', 'REJECT'];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield size={24} className="text-blue-600" /> Audit Trail
          </h1>
          <p className="text-gray-500 text-sm mt-1">Comprehensive system-wide activity logging and monitoring</p>
        </div>
        <button onClick={applyFilters} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Events', value: (stats?.total || 0).toLocaleString(), icon: <Activity size={18} className="text-blue-500" />, color: 'border-blue-200' },
          { label: "Today's Events", value: (stats?.today || 0).toLocaleString(), icon: <Clock size={18} className="text-green-500" />, color: 'border-green-200' },
          { label: 'Unique Users', value: (stats?.uniqueUsers || 0).toLocaleString(), icon: <Users size={18} className="text-purple-500" />, color: 'border-purple-200' },
          { label: 'Last 7 Days', value: (stats?.lastWeek || 0).toLocaleString(), icon: <BarChart3 size={18} className="text-orange-500" />, color: 'border-orange-200' },
        ].map(card => (
          <div key={card.label} className={`bg-white rounded-lg shadow-sm border-l-4 ${card.color} p-4`}>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 font-medium">{card.label}</p>
              {card.icon}
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Module Breakdown + Action Breakdown */}
      {stats && (stats.byModule?.length > 0 || stats.byAction?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* By Module */}
          {stats.byModule?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Events by Module</h3>
              <div className="space-y-2">
                {stats.byModule.map(m => (
                  <div key={m.module} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-24 truncate capitalize">{m.module || 'system'}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${Math.round((m.count / maxModuleCount) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-700 w-8 text-right">{m.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* By Action */}
          {stats.byAction?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Events by Action</h3>
              <div className="flex flex-wrap gap-2">
                {stats.byAction.map(a => (
                  <div key={a.action_type} className="flex items-center gap-1">
                    <AuditActionBadge action={a.action_type} />
                    <span className="text-xs text-gray-500">{a.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={localFilters.search || ''}
              onChange={e => handleFilter('search', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyFilters()}
              className="w-full pl-7 pr-2 py-2 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          <select
            value={localFilters.module || ''}
            onChange={e => handleFilter('module', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="">All Modules</option>
            {knownModules.map(m => <option key={m} value={m} className="capitalize">{m}</option>)}
          </select>
          <select
            value={localFilters.action || ''}
            onChange={e => handleFilter('action', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="">All Actions</option>
            {knownActions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <input
            type="date"
            value={localFilters.dateFrom || ''}
            onChange={e => handleFilter('dateFrom', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            title="From date"
          />
          <input
            type="date"
            value={localFilters.dateTo || ''}
            onChange={e => handleFilter('dateTo', e.target.value)}
            className="border border-gray-200 rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            title="To date"
          />
          <div className="flex gap-2">
            <button onClick={applyFilters} className="flex-1 bg-blue-600 text-white rounded px-3 py-2 text-xs hover:bg-blue-700">Apply</button>
            <button onClick={resetFilters} className="flex-1 bg-gray-100 text-gray-600 rounded px-3 py-2 text-xs hover:bg-gray-200">Reset</button>
          </div>
        </div>
      </div>

      {/* Log Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Shield size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No audit events found matching your filters.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold w-36">Timestamp</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">User</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Action</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Module</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Entity</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Details</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold w-24">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log, i) => (
                    <tr key={log.id || i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap font-mono text-xs">
                        {formatTime(log.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{log.user_name || '—'}</div>
                        {log.user_id && <div className="text-gray-400 text-xs">ID: {log.user_id}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <AuditActionBadge action={log.action_type} />
                      </td>
                      <td className="px-4 py-3">
                        <AuditModuleBadge module={log.module} />
                      </td>
                      <td className="px-4 py-3">
                        {log.entity_name && <div className="font-medium text-gray-700">{log.entity_name}</div>}
                        {log.entity_type && <div className="text-gray-400">{log.entity_type}{log.entity_id ? ` #${log.entity_id}` : ''}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={log.details || ''}>
                        {log.details || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-400 font-mono whitespace-nowrap">
                        {log.ip_address && log.ip_address !== 'unknown' ? log.ip_address : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div className="p-4 text-center border-t border-gray-100">
                <button
                  onClick={onLoadMore}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 font-medium"
                >
                  Load More Events
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const PlaceholderView = ({ title, subtitle }) => (
  <div className="max-w-7xl mx-auto px-4 py-6">
    <div className="mb-6">
      <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
      <p className="text-gray-600 mt-1">{subtitle}</p>
    </div>
    <div className="bg-white rounded-lg shadow p-12 text-center">
      <div className="text-6xl mb-4 opacity-30">⚙️</div>
      <h3 className="text-2xl font-semibold text-gray-900 mb-2">Coming Soon</h3>
      <p className="text-gray-600">This section will be populated with data from Cloudflare Workers.</p>
    </div>
  </div>
);

// ---- Setup View (first-run admin creation wizard) ----
const SetupView = ({ onSetupComplete }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ orgName: '', name: '', email: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/auth/setup', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgName: form.orgName, name: form.name, email: form.email, password: form.password })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Setup failed'); }
      else {
        localStorage.setItem('orbit_token', data.token);
        localStorage.setItem('orbit_user', JSON.stringify(data.user));
        onSetupComplete(data.token, data.user);
      }
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🚀</span>
          </div>
          <h1 className="text-2xl font-bold">Welcome to ORBIT</h1>
          <p className="text-blue-100 mt-1 text-sm">Let's set up your workspace</p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-white text-blue-600' : 'bg-white/30 text-white'}`}>1</div>
            <div className={`h-1 w-8 rounded ${step >= 2 ? 'bg-white' : 'bg-white/30'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-white text-blue-600' : 'bg-white/30 text-white'}`}>2</div>
          </div>
        </div>
        <div className="p-8">
          {step === 1 ? (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Organization Setup</h2>
              <p className="text-gray-500 text-sm mb-6">Enter your organization name to get started.</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
                <input type="text" value={form.orgName} onChange={e => setForm(f => ({ ...f, orgName: e.target.value }))}
                  placeholder="e.g. Acme Corp" autoFocus
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
              </div>
              <button onClick={() => { if (!form.orgName.trim()) return; setStep(2); }} disabled={!form.orgName.trim()}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                Continue
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Create Admin Account</h2>
              <p className="text-gray-500 text-sm mb-6">This will be the primary administrator account.</p>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required autoFocus
                    placeholder="John Doe" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required
                    placeholder="admin@company.com" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required
                    placeholder="Minimum 8 characters" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                  <input type="password" value={form.confirmPassword} onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))} required
                    placeholder="Repeat password" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setStep(1)} className="px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium">Back</button>
                <button type="submit" disabled={loading}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? 'Creating...' : 'Create Admin Account & Launch ORBIT'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Team Management Section ----
const TeamManagementSection = ({ authToken }) => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [addMemberTeamId, setAddMemberTeamId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [teamForm, setTeamForm] = useState({ teamName: '', description: '', department: '', color: '#667eea' });

  const COLORS = ['#667eea', '#48bb78', '#ed8936', '#f56565', '#4299e1', '#9f7aea', '#38b2ac', '#e53e3e'];

  const load = async () => {
    setLoading(true);
    try {
      const [teamsRes, usersRes] = await Promise.all([
        fetch('/api/teams', { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch('/api/users', { headers: { Authorization: `Bearer ${authToken}` } })
      ]);
      const teamsData = await teamsRes.json();
      const usersData = await usersRes.json();
      setTeams(Array.isArray(teamsData) ? teamsData : []);
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAddTeam = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/teams', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(teamForm)
      });
      if (res.ok) {
        setShowAddTeam(false);
        setTeamForm({ teamName: '', description: '', department: '', color: '#667eea' });
        load();
      }
    } catch { }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm('Delete this team?')) return;
    try {
      await fetch(`/api/teams/${teamId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
      setActionMsg('Team deleted');
      setTimeout(() => setActionMsg(''), 3000);
      load();
    } catch { }
  };

  const handleAddMember = async (teamId) => {
    if (!selectedUserId) return;
    try {
      await fetch(`/api/teams/${teamId}/members`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ userId: parseInt(selectedUserId) })
      });
      setSelectedUserId('');
      setAddMemberTeamId(null);
      load();
    } catch { }
  };

  const handleRemoveMember = async (teamId, userId) => {
    try {
      await fetch(`/api/teams/${teamId}/members/${userId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
      load();
    } catch { }
  };

  const getMemberNames = (team) => {
    if (!team.members) return [];
    try { return typeof team.members === 'string' ? JSON.parse(team.members) : team.members; }
    catch { return []; }
  };

  if (loading) return <div className="mt-6 bg-white rounded-lg shadow p-6 text-center text-gray-500">Loading teams...</div>;

  return (
    <div className="mt-6 bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-semibold text-gray-900">Team Management</h2>
        </div>
        <button onClick={() => setShowAddTeam(!showAddTeam)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Team
        </button>
      </div>

      {actionMsg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{actionMsg}</div>
      )}

      {showAddTeam && (
        <form onSubmit={handleAddTeam} className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-medium text-gray-900 mb-3">Create New Team</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input type="text" placeholder="Team Name *" required value={teamForm.teamName}
              onChange={e => setTeamForm(f => ({ ...f, teamName: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
            <input type="text" placeholder="Department" value={teamForm.department}
              onChange={e => setTeamForm(f => ({ ...f, department: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
          </div>
          <input type="text" placeholder="Description" value={teamForm.description}
            onChange={e => setTeamForm(f => ({ ...f, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none mb-3" />
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-gray-600">Color:</span>
            {COLORS.map(c => (
              <button key={c} type="button" onClick={() => setTeamForm(f => ({ ...f, color: c }))}
                className={`w-6 h-6 rounded-full transition-transform ${teamForm.color === c ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : 'hover:scale-110'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Create Team</button>
            <button type="button" onClick={() => setShowAddTeam(false)} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg text-sm font-medium hover:bg-gray-300">Cancel</button>
          </div>
        </form>
      )}

      {teams.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No teams yet. Create your first team above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {teams.map(team => {
            const members = getMemberNames(team);
            const isExpanded = expandedTeam === team.id;
            return (
              <div key={team.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedTeam(isExpanded ? null : team.id)}>
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: team.color || '#667eea' }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{team.team_name}</span>
                      {team.department && <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{team.department}</span>}
                      <span className="text-xs text-gray-400">{members.length} member{members.length !== 1 ? 's' : ''}</span>
                    </div>
                    {team.description && <p className="text-sm text-gray-500 truncate">{team.description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={e => { e.stopPropagation(); handleDeleteTeam(team.id); }}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Members</h4>
                    {members.length === 0 ? (
                      <p className="text-sm text-gray-400 mb-2">No members yet.</p>
                    ) : (
                      <div className="space-y-1 mb-2">
                        {members.map(m => (
                          <div key={m.user_id} className="flex items-center justify-between p-2 bg-white rounded border border-gray-100">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700">
                                {(m.user_name || m.email || '?').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900">{m.user_name || m.email}</span>
                                {m.member_role === 'lead' && (
                                  <span className="ml-2 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Lead</span>
                                )}
                              </div>
                            </div>
                            <button onClick={() => handleRemoveMember(team.id, m.user_id)} className="p-1 text-gray-400 hover:text-red-500">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {addMemberTeamId === team.id ? (
                      <div className="flex items-center gap-2 mt-2">
                        <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none">
                          <option value="">Select user...</option>
                          {users.filter(u => !members.some(m => m.user_id === u.id)).map(u => (
                            <option key={u.id} value={u.id}>{u.user_name || u.email}</option>
                          ))}
                        </select>
                        <button onClick={() => handleAddMember(team.id)} disabled={!selectedUserId}
                          className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">Add</button>
                        <button onClick={() => { setAddMemberTeamId(null); setSelectedUserId(''); }}
                          className="px-3 py-2 text-gray-600 bg-gray-200 rounded-lg text-sm hover:bg-gray-300">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setAddMemberTeamId(team.id)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                        <Plus className="w-3.5 h-3.5" /> Add Member
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---- User Management Section (admin only) ----
const UserManagementSection = ({ authToken }) => {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', name: '', role: 'user', password: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addResult, setAddResult] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [assigningUserId, setAssigningUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, teamsRes] = await Promise.all([
        fetch('/api/users', { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch('/api/teams', { headers: { Authorization: `Bearer ${authToken}` } })
      ]);
      const usersData = await usersRes.json();
      const teamsData = await teamsRes.json();
      setUsers(Array.isArray(usersData) ? usersData : []);
      setTeams(Array.isArray(teamsData) ? teamsData : []);
    } catch { setUsers([]); setTeams([]); }
    setLoading(false);
  };

  const getUserTeams = (userId) =>
    teams.filter(t => (t.members || []).some(m => m.user_id === userId));

  const getUnassignedTeams = (userId) =>
    teams.filter(t => !(t.members || []).some(m => m.user_id === userId));

  const handleAssignTeam = async (userId, teamId) => {
    try {
      await fetch(`/api/teams/${teamId}/members`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ userId })
      });
      setAssigningUserId(null);
      load();
    } catch { /* ignore */ }
  };

  const handleRemoveFromTeam = async (userId, teamId) => {
    try {
      await fetch(`/api/teams/${teamId}/members/${userId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } });
      load();
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddLoading(true); setAddResult(null);
    try {
      const res = await fetch('/api/users', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(addForm)
      });
      const data = await res.json();
      if (!res.ok) setAddResult({ type: 'error', message: data.error || 'Failed to create user' });
      else {
        setAddResult({ type: 'success', message: 'User created.', resetUrl: data.resetUrl });
        setAddForm({ email: '', name: '', role: 'user', password: '' });
        load();
      }
    } catch { setAddResult({ type: 'error', message: 'Network error' }); }
    setAddLoading(false);
  };

  const handleToggleActive = async (u) => {
    try {
      await fetch(`/api/users/${u.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ isActive: !u.is_active })
      });
      setActionMsg(`User ${u.is_active ? 'deactivated' : 'activated'} successfully`);
      setTimeout(() => setActionMsg(''), 3000);
      load();
    } catch { /* ignore */ }
  };

  const handleRoleChange = async (u, newRole) => {
    try {
      await fetch(`/api/users/${u.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ role: newRole })
      });
      setActionMsg('Role updated');
      setTimeout(() => setActionMsg(''), 3000);
      load();
    } catch { /* ignore */ }
  };

  const handleAdminReset = async (u) => {
    try {
      const res = await fetch(`/api/users/${u.id}/reset-password`, {
        method: 'POST', headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (data.resetUrl) {
        setActionMsg(`Reset link: ${data.resetUrl}`);
      } else {
        setActionMsg('Reset link generated and sent');
        setTimeout(() => setActionMsg(''), 4000);
      }
    } catch { /* ignore */ }
  };

  const fmt = (ts) => ts ? new Date(ts.endsWith('Z') ? ts : ts + 'Z').toLocaleDateString() : 'Never';

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Users size={20} className="text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
        </div>
        <button onClick={() => { setShowAddUser(v => !v); setAddResult(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
          + Add User
        </button>
      </div>
      <p className="text-gray-500 text-sm mb-4">Manage user accounts, roles, and password resets for your organization.</p>

      {actionMsg && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 break-all">{actionMsg}</div>
      )}

      {showAddUser && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="font-medium text-gray-800 mb-3 text-sm">Add New User</h3>
          {addResult && (
            <div className={`mb-3 p-2 rounded text-xs ${addResult.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {addResult.message}
              {addResult.resetUrl && <div className="mt-1 font-mono break-all">Invite URL: {addResult.resetUrl}</div>}
            </div>
          )}
          <form onSubmit={handleAddUser} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required placeholder="Full Name" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <input required type="email" placeholder="Email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <select value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400">
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="developer">Developer</option>
              <option value="manager">Manager</option>
            </select>
            <input type="password" placeholder="Password (leave blank for invite link)" value={addForm.password}
              onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))}
              className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <div className="sm:col-span-2 flex gap-2">
              <button type="submit" disabled={addLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60">
                {addLoading ? 'Creating…' : 'Create User'}
              </button>
              <button type="button" onClick={() => setShowAddUser(false)}
                className="px-4 py-2 border border-gray-200 text-gray-600 rounded text-sm hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-gray-400 text-sm">Loading users…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">User</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Role</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Teams</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Last Login</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => {
                const userTeams = getUserTeams(u.id);
                const unassigned = getUnassignedTeams(u.id);
                const isAssigning = assigningUserId === u.id;
                return (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{u.user_name}</div>
                      <div className="text-xs text-gray-400">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select value={u.user_role} onChange={e => handleRoleChange(u, e.target.value)}
                        className="border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400">
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="developer">Developer (DevOps)</option>
                        <option value="dataops">DataOps</option>
                        <option value="project">Project Team</option>
                        <option value="qa">Quality Assurance</option>
                        <option value="devops_manager">DevOps Manager</option>
                        <option value="manager">Manager</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5 min-w-[180px]">
                        {/* Current team pills */}
                        {userTeams.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {userTeams.map(t => (
                              <span key={t.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: t.color || '#667eea' }}>
                                {t.team_name}
                                <button onClick={() => handleRemoveFromTeam(u.id, t.id)}
                                  className="hover:opacity-70 ml-0.5 leading-none" title="Remove from team">
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No team assigned</span>
                        )}
                        {/* Assign team control */}
                        {teams.length === 0 ? null : isAssigning ? (
                          <div className="flex items-center gap-1.5">
                            <select autoFocus defaultValue=""
                              onChange={e => { if (e.target.value) handleAssignTeam(u.id, parseInt(e.target.value)); }}
                              className="border border-indigo-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white">
                              <option value="" disabled>Select a team…</option>
                              {unassigned.map(t => (
                                <option key={t.id} value={t.id}>{t.team_name}</option>
                              ))}
                            </select>
                            <button onClick={() => setAssigningUserId(null)}
                              className="px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded hover:bg-gray-200">
                              Cancel
                            </button>
                          </div>
                        ) : unassigned.length > 0 ? (
                          <button onClick={() => setAssigningUserId(u.id)}
                            className="w-fit inline-flex items-center gap-1 px-2.5 py-1 rounded bg-indigo-50 border border-indigo-200 text-xs font-medium text-indigo-600 hover:bg-indigo-100 hover:border-indigo-400">
                            <Plus className="w-3.5 h-3.5" /> Assign Team
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{fmt(u.last_login_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleAdminReset(u)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                          Reset PW
                        </button>
                        <span className="text-gray-300">|</span>
                        <button onClick={() => handleToggleActive(u)}
                          className={`text-xs font-medium ${u.is_active ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-800'}`}>
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400 text-sm">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SettingsView = ({ onClearData, user, authToken }) => {
  const moduleOptions = [
    { key: 'plantrack', label: 'PlanTrack (Projects, Milestones, Tasks)' },
    { key: 'quicktrack', label: 'QuickTrack (Quick Tasks)' },
    { key: 'dataops', label: 'DataOps (Pipelines, Reports)' },
    { key: 'qms', label: 'Quality / QMS (Requirements, Test Cases, Bugs)' },
    { key: 'timeboard', label: 'TimeBoard (Timesheets, Entries)' },
    { key: 'leaveboard', label: 'LeaveBoard (Leave Requests, Balances)' },
    { key: 'learntrack', label: 'LearnTrack (Courses, Skills, Badges)' },
    { key: 'vendors', label: 'Vendors (Vendors, Events)' }
  ];

  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [result, setResult] = useState(null);

  const allSelected = selected.length === moduleOptions.length;

  const toggleModule = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : moduleOptions.map(m => m.key));
  };

  const handleClear = async () => {
    setClearing(true);
    setResult(null);
    try {
      const modules = allSelected ? ['all'] : selected;
      const res = await onClearData(modules);
      if (res?.success) {
        setResult({ type: 'success', message: `Cleared ${res.tablesCleared} table(s) successfully. You can now enter your real data.` });
        setSelected([]);
      } else {
        setResult({ type: 'error', message: res?.error || 'Failed to clear data.' });
      }
    } catch {
      setResult({ type: 'error', message: 'Network error. Please try again.' });
    }
    setClearing(false);
    setConfirmOpen(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your system data and configurations.</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trash2 className="w-6 h-6 text-red-600" />
          <h2 className="text-xl font-semibold text-gray-900">Data Management</h2>
        </div>
        <p className="text-gray-600 mb-6">
          Clear test/demo data from selected modules so you can replace it with real data.
          This deletes all records from the selected modules but keeps the database structure intact.
        </p>

        {result && (
          <div className={`mb-4 p-4 rounded-lg ${result.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {result.message}
          </div>
        )}

        <div className="mb-6">
          <label className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-200 cursor-pointer">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 text-red-600 rounded" />
            <span className="font-semibold text-gray-900">Select All Modules</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {moduleOptions.map(mod => (
              <label key={mod.key} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={selected.includes(mod.key)} onChange={() => toggleModule(mod.key)} className="w-4 h-4 text-red-600 rounded" />
                <span className="text-sm text-gray-700">{mod.label}</span>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={() => setConfirmOpen(true)}
          disabled={selected.length === 0 || clearing}
          className="px-5 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Clear Selected Data
        </button>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirm Data Deletion</h3>
            </div>
            <p className="text-gray-600 mb-2">
              This will permanently delete all data from <strong>{selected.length}</strong> module(s):
            </p>
            <ul className="text-sm text-gray-700 mb-4 ml-4 list-disc">
              {selected.map(key => (
                <li key={key}>{moduleOptions.find(m => m.key === key)?.label}</li>
              ))}
            </ul>
            <p className="text-red-600 text-sm font-medium mb-6">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmOpen(false)} disabled={clearing} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
              <button onClick={handleClear} disabled={clearing} className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                {clearing ? 'Clearing...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Management — admin only */}
      {user?.role === 'admin' && (
        <div className="mt-6">
          <UserManagementSection authToken={authToken} />
        </div>
      )}

      {/* Team Management — admin only */}
      {user?.role === 'admin' && (
        <TeamManagementSection authToken={authToken} />
      )}
    </div>
  );
};

// ============================================================================
// CONTROLLER (Application Logic)
// ============================================================================

const App = () => {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [burnCharts, setBurnCharts] = useState(null);
  const [modules, setModules] = useState([]);
  const [devOpsData, setDevOpsData] = useState(null);
  const [devOpsViewMode, setDevOpsViewMode] = useState('sprint');
  const [devOpsGroup, setDevOpsGroup] = useState('all');
  const [showAddDevOpsTask, setShowAddDevOpsTask] = useState(false);
  const [showAddDataOpsTask, setShowAddDataOpsTask] = useState(false);
  const [selectedDevOpsTask, setSelectedDevOpsTask] = useState(null);
  const [selectedBug, setSelectedBug] = useState(null);
  const [planTrackData, setPlanTrackData] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showAddSprint, setShowAddSprint] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showSprintsView, setShowSprintsView] = useState(false);
  const [showMilestoneEngine, setShowMilestoneEngine] = useState(false);
  const [showEmailSummary, setShowEmailSummary] = useState(false);
  const [showPostUpdateReport, setShowPostUpdateReport] = useState(false);
  const [showSendEod, setShowSendEod] = useState(false);
  const [eodModule, setEodModule] = useState('devops');
  const [showEodSettings, setShowEodSettings] = useState(false);
  const [showQaEod, setShowQaEod] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [editingMilestoneId, setEditingMilestoneId] = useState(null);
  const [editingMilestoneData, setEditingMilestoneData] = useState(null);
  const [dataOpsData, setDataOpsData] = useState(null);
  const [dataOpsViewMode, setDataOpsViewMode] = useState('dashboard');
  const [showAddPipeline, setShowAddPipeline] = useState(false);
  const [showAddReport, setShowAddReport] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [learnTrackData, setLearnTrackData] = useState(null);
  const [learnTrackViewMode, setLearnTrackViewMode] = useState('dashboard');
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [learnFilterSkill, setLearnFilterSkill] = useState('');
  const [learnFilterGroup, setLearnFilterGroup] = useState('');
  const [timeBoardData, setTimeBoardData] = useState(null);
  const [leaveBoardData, setLeaveBoardData] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().split('T')[0];
  });
  const [showRequestLeave, setShowRequestLeave] = useState(false);
  const [showTimeEntry, setShowTimeEntry] = useState(null);
  const [quickTrackData, setQuickTrackData] = useState(null);
  const [qtFilterStatus, setQtFilterStatus] = useState('');
  const [qtFilterPriority, setQtFilterPriority] = useState('');
  const [qtFilterCategory, setQtFilterCategory] = useState('');
  const [showAddQuickTask, setShowAddQuickTask] = useState(false);
  const [editingQuickTask, setEditingQuickTask] = useState(null);
  const [vendorEventsData, setVendorEventsData] = useState(null);
  const [veFilterVendor, setVeFilterVendor] = useState('');
  const [veFilterWorkflow, setVeFilterWorkflow] = useState('');
  const [veFilterStatus, setVeFilterStatus] = useState('');
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [qualityData, setQualityData] = useState(null);
  const [reportsData, setReportsData] = useState(null);
  const [taskManageData, setTaskManageData] = useState(null);
  const [taskManageFilters, setTaskManageFilters] = useState({ source: '', status: '', priority: '', assignee: '', project: '', search: '' });
  const [qmsStage, setQmsStage] = useState('requirements');
  const [qmsProjectId, setQmsProjectId] = useState(null);
  const [showAddRequirement, setShowAddRequirement] = useState(false);
  const [showAddTestCase, setShowAddTestCase] = useState(false);
  const [showReportBug, setShowReportBug] = useState(false);
  const [reportBugPrefill, setReportBugPrefill] = useState(null);
  const [editingBug, setEditingBug] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [allTeamMembers, setAllTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditFilters, setAuditFilters] = useState({ module: '', action: '', entityType: '', dateFrom: '', dateTo: '', search: '' });
  const [auditLoading, setAuditLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);

  // Auth init: check setup status, localStorage token, handle OAuth callbacks
  useEffect(() => {
    const initAuth = async () => {
      // Check for OAuth callback (code in URL params)
      const params = new URLSearchParams(window.location.search);

      // Check for password reset token
      const rt = params.get('reset_token');
      if (rt) {
        setResetToken(rt);
        window.history.replaceState({}, document.title, '/');
        setAuthChecked(true);
        return;
      }

      // Check if first-run setup is needed (no users exist)
      try {
        const setupRes = await fetch('/api/auth/setup-status');
        const setupData = await setupRes.json();
        if (setupData.needsSetup) {
          setNeedsSetup(true);
          setAuthChecked(true);
          return;
        }
      } catch { /* ignore, proceed normally */ }

      const code = params.get('code');
      const state = params.get('state');
      if (code && state) {
        const provider = sessionStorage.getItem('oauth_provider');
        const redirectUri = sessionStorage.getItem('oauth_redirect_uri') || window.location.origin;
        sessionStorage.removeItem('oauth_provider');
        sessionStorage.removeItem('oauth_redirect_uri');
        try {
          let endpoint = '/api/auth/google/callback';
          const body = { code, redirectUri };
          if (provider === 'sso' || (state && state.startsWith('sso:'))) {
            endpoint = '/api/auth/sso/callback';
            body.issuerUrl = sessionStorage.getItem('sso_issuer') || '';
            sessionStorage.removeItem('sso_issuer');
          }
          const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
          const data = await res.json();
          if (data.token) {
            localStorage.setItem('orbit_token', data.token);
            localStorage.setItem('orbit_user', JSON.stringify(data.user));
            setAuthToken(data.token);
            setUser(data.user);
            window.history.replaceState({}, document.title, '/');
            setAuthChecked(true);
            return;
          }
        } catch { /* fall through to login */ }
        window.history.replaceState({}, document.title, '/');
      }

      // Restore from localStorage
      const storedToken = localStorage.getItem('orbit_token');
      if (storedToken) {
        try {
          const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${storedToken}` } });
          if (res.ok) {
            const u = await res.json();
            setAuthToken(storedToken);
            setUser(u);
            setAuthChecked(true);
            return;
          }
        } catch { /* token invalid */ }
        localStorage.removeItem('orbit_token');
        localStorage.removeItem('orbit_user');
      }
      setAuthChecked(true);
    };
    initAuth();
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [currentPage, devOpsViewMode, dataOpsViewMode, currentWeekStart, veFilterVendor, veFilterWorkflow, veFilterStatus, user]);

  useEffect(() => {
    if (user) {
      ModulesModel.getTeamMembers().then(members => setAllTeamMembers(members));
    }
  }, [user]);

  useEffect(() => {
    if (!user || currentPage !== 'vendorevents') return;
    const interval = setInterval(() => {
      ModulesModel.getVendorEventsData({ vendor: veFilterVendor, workflow: veFilterWorkflow, status: veFilterStatus })
        .then(data => setVendorEventsData(data));
    }, 60000);
    return () => clearInterval(interval);
  }, [user, currentPage, veFilterVendor, veFilterWorkflow, veFilterStatus]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (currentPage === 'dashboard') {
        const [data, charts] = await Promise.all([DashboardModel.getStats(), DashboardModel.getBurnCharts()]);
        setDashboardData(data);
        setBurnCharts(charts);
      } else if (currentPage === 'modules') {
        const data = await ModulesModel.getModules();
        setModules(data);
      } else if (currentPage === 'devops') {
        const devops = await ModulesModel.getDevOpsPipeline();
        if (devOpsViewMode === 'kanban') {
          const kanban = await ModulesModel.getDevOpsKanban();
          devops.kanbanPipeline = kanban.pipeline;
        }
        setDevOpsData(devops);
      } else if (currentPage === 'plantrack') {
        const data = await ModulesModel.getPlanTrackData();
        setPlanTrackData(data);
        if (selectedProjectId) {
          const detail = await ModulesModel.getProjectDetail(selectedProjectId);
          setProjectDetail(detail);
        }
      } else if (currentPage === 'dataops') {
        const data = await ModulesModel.getDataOpsData();
        setDataOpsData(data);
      } else if (currentPage === 'learntrack') {
        const data = await ModulesModel.getLearnTrackData();
        setLearnTrackData(data);
      } else if (currentPage === 'timeboard') {
        const data = await ModulesModel.getTimeBoardData(currentWeekStart);
        setTimeBoardData(data);
      } else if (currentPage === 'leaveboard') {
        const data = await ModulesModel.getLeaveBoardData();
        setLeaveBoardData(data);
      } else if (currentPage === 'quality') {
        const data = await ModulesModel.getQualityData(qmsProjectId);
        setQualityData(data);
        if (data?.project?.currentStage) setQmsStage(data.project.currentStage);
      } else if (currentPage === 'quicktrack') {
        const [data, ptData] = await Promise.all([ModulesModel.getQuickTrackData(), ModulesModel.getPlanTrackData()]);
        setQuickTrackData(data);
        setAllProjects(ptData?.projects || []);
      } else if (currentPage === 'vendorevents') {
        const data = await ModulesModel.getVendorEventsData({ vendor: veFilterVendor, workflow: veFilterWorkflow, status: veFilterStatus });
        setVendorEventsData(data);
      } else if (currentPage === 'reports') {
        const data = await ModulesModel.getReportsData();
        setReportsData(data);
      } else if (currentPage === 'tasks') {
        const data = await ModulesModel.getTaskManageData(taskManageFilters);
        setTaskManageData(data);
      } else if (currentPage === 'audit') {
        setAuditLoading(true);
        const [logsData, statsData] = await Promise.all([
          ModulesModel.getAuditLogs({ ...auditFilters, offset: 0 }),
          ModulesModel.getAuditStats()
        ]);
        setAuditLogs(logsData.logs || []);
        setAuditHasMore(logsData.hasMore || false);
        setAuditOffset(0);
        setAuditStats(statsData);
        setAuditLoading(false);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const handleSelectProject = async (projectId) => {
    setSelectedProjectId(projectId);
    setLoading(true);
    try {
      const detail = await ModulesModel.getProjectDetail(projectId);
      setProjectDetail(detail);
    } catch (error) {
      console.error('Error loading project detail:', error);
    }
    setLoading(false);
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('orbit_token', token);
    localStorage.setItem('orbit_user', JSON.stringify(userData));
    setAuthToken(token);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${authToken}` } });
    } catch { /* ignore */ }
    localStorage.removeItem('orbit_token');
    localStorage.removeItem('orbit_user');
    setAuthToken(null);
    setUser(null);
  };

  const handleBackToProjects = () => {
    setSelectedProjectId(null);
    setProjectDetail(null);
  };

  const handleAuditFilterChange = async (newFilters) => {
    setAuditFilters(newFilters);
    setAuditLoading(true);
    try {
      const logsData = await ModulesModel.getAuditLogs({ ...newFilters, offset: 0 });
      setAuditLogs(logsData.logs || []);
      setAuditHasMore(logsData.hasMore || false);
      setAuditOffset(0);
    } catch { /* ignore */ }
    setAuditLoading(false);
  };

  const handleAuditLoadMore = async () => {
    const nextOffset = auditOffset + 50;
    setAuditLoading(true);
    try {
      const logsData = await ModulesModel.getAuditLogs({ ...auditFilters, offset: nextOffset });
      setAuditLogs(prev => [...prev, ...(logsData.logs || [])]);
      setAuditHasMore(logsData.hasMore || false);
      setAuditOffset(nextOffset);
    } catch { /* ignore */ }
    setAuditLoading(false);
  };

  const handleRefreshProjectDetail = async () => {
    if (!selectedProjectId) return;
    try {
      const detail = await ModulesModel.getProjectDetail(selectedProjectId);
      setProjectDetail(detail);
    } catch (error) {
      console.error('Error refreshing project detail:', error);
    }
  };

  const handleCreateProject = async (formData) => {
    await ModulesModel.createProject(formData);
    setShowAddProject(false);
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
  };

  const handleCreateSprint = async (formData) => {
    await ModulesModel.createSprint(formData);
    setShowAddSprint(false);
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
  };

  const handleUpdateSprint = async (sprintId, data) => {
    await ModulesModel.updateSprint(sprintId, data);
    const refreshed = await ModulesModel.getPlanTrackData();
    setPlanTrackData(refreshed);
  };

  const handleUpdateProject = async (formData) => {
    await ModulesModel.updateProject(selectedProjectId, formData);
    setShowEditProject(false);
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
    const detail = await ModulesModel.getProjectDetail(selectedProjectId);
    setProjectDetail(detail);
  };

  // Milestone Engine handlers
  const refreshPlanTrack = async () => {
    const data = await ModulesModel.getPlanTrackData();
    setPlanTrackData(data);
    if (selectedProjectId) {
      const detail = await ModulesModel.getProjectDetail(selectedProjectId);
      setProjectDetail(detail);
    }
  };

  const handleCreateMilestone = async (formData) => {
    await ModulesModel.createMilestone(formData);
    setShowAddMilestone(false);
    await refreshPlanTrack();
  };

  const handleUpdateMilestone = async (msId, data) => {
    await ModulesModel.updateMilestone(msId, data);
    setEditingMilestoneId(null); setEditingMilestoneData(null);
    await refreshPlanTrack();
  };

  const handleDeleteMilestone = async (msId) => {
    await ModulesModel.deleteMilestone(msId);
    setEditingMilestoneId(null); setEditingMilestoneData(null);
    await refreshPlanTrack();
  };

  const handleCompleteMilestone = async (msId) => {
    const r = await ModulesModel.completeMilestone(msId);
    if (!r.success) alert(r.error || 'Failed to complete milestone');
    await refreshPlanTrack();
  };

  const handleEditMilestone = async (msId) => {
    const detail = await ModulesModel.getMilestoneDetail(msId);
    if (detail) { setEditingMilestoneId(msId); setEditingMilestoneData(detail); }
  };

  const handleSendEmailSummary = async (to) => {
    return await ModulesModel.sendProjectStatusEmail(to);
  };

  // DevOps handlers
  const handleCreateDevOpsTask = async (formData) => {
    await ModulesModel.createDevOpsTask(formData);
    setShowAddDevOpsTask(false);
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  const handleCreateDataOpsTask = async (formData) => {
    await ModulesModel.createDataOpsTask(formData);
    setShowAddDataOpsTask(false);
    await refreshDataOps();
  };

  const handleDevOpsTaskClick = async (taskId) => {
    if (String(taskId).startsWith('QT-')) return;
    // Bugs: fetch full bug data and open BugDetailPanel
    if (String(taskId).startsWith('BUG-')) {
      const numericId = parseInt(String(taskId).replace('BUG-', ''), 10);
      const bug = await ModulesModel.getBug(numericId);
      if (bug && bug.id) setSelectedBug(bug);
      return;
    }
    // Try to find the task in current data first
    let task = null;
    if (devOpsData?.pipeline) {
      for (const items of Object.values(devOpsData.pipeline)) {
        task = items.find(t => t.id === taskId);
        if (task) break;
      }
    }
    if (devOpsData?.kanbanPipeline && !task) {
      for (const items of Object.values(devOpsData.kanbanPipeline)) {
        task = items.find(t => t.id === taskId);
        if (task) break;
      }
    }
    // Fallback: fetch from API
    if (!task) {
      task = await ModulesModel.getDevOpsTask(taskId);
    }
    if (task) setSelectedDevOpsTask(task);
  };

  const handleDevOpsStatusChange = async (taskId, newStatus) => {
    if (String(taskId).startsWith('QT-')) {
      const qtId = parseInt(String(taskId).replace('QT-', ''), 10);
      const qtStatusMap = { backlog: 'open', ready: 'open', in_progress: 'in_progress', review: 'in_progress', done: 'done', completed: 'done' };
      await ModulesModel.updateQuickTask(qtId, { status: qtStatusMap[newStatus] || 'open' });
    } else if (String(taskId).startsWith('BUG-')) {
      const bugId = parseInt(String(taskId).replace('BUG-', ''), 10);
      // Sprint column → bug status: backlog→open, in_progress→in_progress, review→resolved, done→closed
      const bugStatusMap = { backlog: 'open', ready: 'open', in_progress: 'in_progress', review: 'resolved', done: 'closed', completed: 'closed' };
      await ModulesModel.updateBug(bugId, { status: bugStatusMap[newStatus] || 'open' });
    } else {
      await ModulesModel.updateDevOpsTask(taskId, { status: newStatus });
    }
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  const handleDevOpsTaskUpdate = async (taskId, changes) => {
    await ModulesModel.updateDevOpsTask(taskId, changes);
    setSelectedDevOpsTask(null);
    const devops = await ModulesModel.getDevOpsPipeline();
    if (devOpsViewMode === 'kanban') {
      const kanban = await ModulesModel.getDevOpsKanban();
      devops.kanbanPipeline = kanban.pipeline;
    }
    setDevOpsData(devops);
  };

  // LearnTrack handlers
  const refreshLearnTrackData = async () => {
    const data = await ModulesModel.getLearnTrackData();
    setLearnTrackData(data);
  };

  const handleCourseClick = async (courseId) => {
    const course = await ModulesModel.getCourseDetail(courseId);
    if (course) setSelectedCourse(course);
  };

  const handleCreateCourse = async (formData) => {
    await ModulesModel.createCourse(formData);
    setShowAddCourse(false);
    await refreshLearnTrackData();
  };

  const handleUpdateCourse = async (courseId, data) => {
    await ModulesModel.updateCourse(courseId, data);
    setSelectedCourse(null);
    await refreshLearnTrackData();
  };

  const handleDeleteCourse = async (courseId) => {
    await ModulesModel.deleteCourse(courseId);
    setSelectedCourse(null);
    await refreshLearnTrackData();
  };

  const handleEnrollUser = async (data) => {
    await ModulesModel.enrollUser(data);
    await refreshLearnTrackData();
    if (selectedCourse) {
      const updated = await ModulesModel.getCourseDetail(selectedCourse.id);
      setSelectedCourse(updated);
    }
  };

  const handleUpdateEnrollment = async (enrollmentId, data) => {
    await ModulesModel.updateEnrollment(enrollmentId, data);
    await refreshLearnTrackData();
  };

  // TimeBoard handlers
  const refreshTimeBoardData = async () => {
    const data = await ModulesModel.getTimeBoardData(currentWeekStart);
    setTimeBoardData(data);
  };

  const handleCreateTimeEntry = async (formData) => {
    await ModulesModel.createTimesheetEntry({ ...formData, weekStart: currentWeekStart });
    setShowTimeEntry(null);
    await refreshTimeBoardData();
  };

  const handleDeleteTimeEntry = async (id) => {
    await ModulesModel.deleteTimesheetEntry(id);
    await refreshTimeBoardData();
  };

  const handleSubmitTimesheet = async (id) => {
    await ModulesModel.submitTimesheet(id);
    await refreshTimeBoardData();
  };

  const handleApproveTimesheet = async (id) => {
    await ModulesModel.approveTimesheet(id);
    await refreshTimeBoardData();
  };

  const handleWeekChange = (direction) => {
    const current = new Date(currentWeekStart);
    current.setDate(current.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeekStart(current.toISOString().split('T')[0]);
  };

  // LeaveBoard handlers
  const refreshLeaveBoardData = async () => {
    const data = await ModulesModel.getLeaveBoardData();
    setLeaveBoardData(data);
  };

  const handleCreateLeaveRequest = async (formData) => {
    await ModulesModel.createLeaveRequest(formData);
    setShowRequestLeave(false);
    await refreshLeaveBoardData();
  };

  const handleUpdateLeaveRequest = async (id, data) => {
    await ModulesModel.updateLeaveRequest(id, data);
    await refreshLeaveBoardData();
  };

  // QMS handlers
  const refreshQualityData = async () => {
    const data = await ModulesModel.getQualityData(qmsProjectId);
    setQualityData(data);
  };

  const handleQmsStageChange = async (stage) => {
    setQmsStage(stage);
    if (qualityData?.project?.projectId) {
      await ModulesModel.updateQaProject(qualityData.project.projectId, { currentStage: stage });
    }
  };

  const handleQmsProjectChange = async (projectId) => {
    setQmsProjectId(projectId);
    const data = await ModulesModel.getQualityData(projectId);
    setQualityData(data);
    if (data?.project?.currentStage) setQmsStage(data.project.currentStage);
  };

  const handleClearData = async (modules) => {
    return await ModulesModel.clearData(modules);
  };

  const handleCreateRequirement = async (data) => {
    const result = await ModulesModel.createRequirement(data);
    if (result?.success === false) { alert('Failed to save requirement: ' + (result.error || 'Unknown error')); return; }
    setShowAddRequirement(false);
    await refreshQualityData();
  };

  const handleApproveRequirement = async (id) => {
    await ModulesModel.updateRequirement(id, { status: 'approved' });
    await refreshQualityData();
  };

  const handleRejectRequirement = async (id) => {
    await ModulesModel.updateRequirement(id, { status: 'rejected' });
    await refreshQualityData();
  };

  const handleCreateTestCase = async (data) => {
    const result = await ModulesModel.createTestCase(data);
    if (result?.success === false) { alert('Failed to save test case: ' + (result.error || 'Unknown error')); return; }
    setShowAddTestCase(false);
    await refreshQualityData();
  };

  const handlePassTestCase = async (id) => {
    await ModulesModel.updateTestCase(id, { status: 'pass' });
    await refreshQualityData();
  };

  const handleFailTestCase = async (id) => {
    await ModulesModel.updateTestCase(id, { status: 'fail' });
    await refreshQualityData();
  };

  const handleReportBug = (tc) => {
    setReportBugPrefill(tc);
    setShowReportBug(true);
  };

  const handleCreateBug = async (data) => {
    const result = await ModulesModel.createBug(data);
    if (result?.success === false) { alert('Failed to save bug: ' + (result.error || 'Unknown error')); return; }
    setShowReportBug(false);
    setReportBugPrefill(null);
    await refreshQualityData();
  };

  const handleEditBug = (bug) => { setEditingBug(bug); };

  const handleUpdateBug = async (id, data) => {
    const result = await ModulesModel.updateBug(id, data);
    if (result?.success === false) { alert('Failed to update bug: ' + (result.error || 'Unknown error')); return; }
    setEditingBug(null);
    await refreshQualityData();
  };

  const handleDeleteBug = async (id) => {
    try {
      const r = await fetch(`${'/api'}/quality/bugs/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
    } catch (e) { alert('Failed to delete bug'); return; }
    setEditingBug(null);
    await refreshQualityData();
  };

  const handleUpdateQaProject = async (id, data) => {
    await ModulesModel.updateQaProject(id, data);
    await refreshQualityData();
  };

  // QuickTrack handlers
  const refreshQuickTrackData = async () => {
    const data = await ModulesModel.getQuickTrackData();
    setQuickTrackData(data);
  };

  const handleCreateQuickTask = async (data) => {
    await ModulesModel.createQuickTask(data);
    setShowAddQuickTask(false);
    await refreshQuickTrackData();
  };

  const handleUpdateQuickTask = async (taskId, data) => {
    await ModulesModel.updateQuickTask(taskId, data);
    await refreshQuickTrackData();
  };

  const handleDeleteQuickTask = async (taskId) => {
    await ModulesModel.deleteQuickTask(taskId);
    await refreshQuickTrackData();
  };

  const handleEditQuickTask = (task) => setEditingQuickTask(task);

  const handleSaveQuickTask = async (taskId, data) => {
    await ModulesModel.updateQuickTask(taskId, data);
    setEditingQuickTask(null);
    await refreshQuickTrackData();
  };

  // Vendor Events handlers
  const handleVeFilterVendor = (v) => setVeFilterVendor(v);
  const handleVeFilterWorkflow = (v) => setVeFilterWorkflow(v);
  const handleVeFilterStatus = (v) => setVeFilterStatus(v);
  const handleVeResetFilters = () => { setVeFilterVendor(''); setVeFilterWorkflow(''); setVeFilterStatus(''); };

  // Task Management handlers
  const handleTaskManageFilter = async (newFilters) => {
    const merged = { ...taskManageFilters, ...newFilters };
    setTaskManageFilters(merged);
    const data = await ModulesModel.getTaskManageData(merged);
    setTaskManageData(data);
  };
  const handleTaskManageBulk = async (tasks, updates) => {
    await ModulesModel.bulkUpdateTasks(tasks, updates);
    const data = await ModulesModel.getTaskManageData(taskManageFilters);
    setTaskManageData(data);
  };
  const handleTaskManageRefresh = async () => {
    const data = await ModulesModel.getTaskManageData(taskManageFilters);
    setTaskManageData(data);
  };

  const handleCreateVendor = async (data) => {
    await ModulesModel.createVendor(data);
    setShowAddVendor(false);
    const refreshed = await ModulesModel.getVendorEventsData({ vendor: veFilterVendor, workflow: veFilterWorkflow, status: veFilterStatus });
    setVendorEventsData(refreshed);
  };

  const refreshVendorData = async () => {
    const refreshed = await ModulesModel.getVendorEventsData({ vendor: veFilterVendor, workflow: veFilterWorkflow, status: veFilterStatus });
    setVendorEventsData(refreshed);
  };


  const handleDeleteVendor = async (vendorId) => {
    await ModulesModel.deleteVendor(vendorId);
    await refreshVendorData();
  };

  // DataOps handlers
  const refreshDataOps = async () => {
    const data = await ModulesModel.getDataOpsData();
    setDataOpsData(data);
  };

  const handleDataOpsTaskClick = async (taskId) => {
    if (String(taskId).startsWith('QT-')) return;
    let task = null;
    if (dataOpsData?.sprintBoard) {
      for (const items of Object.values(dataOpsData.sprintBoard)) {
        task = items.find(t => t.id === taskId);
        if (task) break;
      }
    }
    if (!task) task = await ModulesModel.getDevOpsTask(taskId);
    if (task) setSelectedDevOpsTask(task);
  };

  const handleDataOpsStatusChange = async (taskId, newStatus) => {
    await ModulesModel.updateDevOpsTask(taskId, { status: newStatus });
    await refreshDataOps();
  };

  const handleSelectPipeline = async (pipelineId) => {
    const pipeline = await ModulesModel.getPipeline(pipelineId);
    if (pipeline) setSelectedPipeline(pipeline);
  };

  const handleCreatePipeline = async (formData) => {
    await ModulesModel.createPipeline(formData);
    setShowAddPipeline(false);
    await refreshDataOps();
  };

  const handleUpdatePipeline = async (pipelineId, data) => {
    await ModulesModel.updatePipeline(pipelineId, data);
    setSelectedPipeline(null);
    await refreshDataOps();
  };

  const handleDeletePipeline = async (pipelineId) => {
    await ModulesModel.deletePipeline(pipelineId);
    setSelectedPipeline(null);
    await refreshDataOps();
  };

  const handleTriggerPipeline = async (pipelineId) => {
    await ModulesModel.updatePipeline(pipelineId, { action: 'trigger' });
    await refreshDataOps();
  };

  const handleSelectReport = async (reportId) => {
    const report = await ModulesModel.getReport(reportId);
    await ModulesModel.updateReport(reportId, { action: 'view' });
    if (report) setSelectedReport(report);
  };

  const handleCreateReport = async (formData) => {
    await ModulesModel.createReport(formData);
    setShowAddReport(false);
    await refreshDataOps();
  };

  const handleUpdateReport = async (reportId, data) => {
    await ModulesModel.updateReport(reportId, data);
    setSelectedReport(null);
    await refreshDataOps();
  };

  const handleDeleteReport = async (reportId) => {
    await ModulesModel.deleteReport(reportId);
    setSelectedReport(null);
    await refreshDataOps();
  };

  const handleToggleFavorite = async (reportId, isFavorite) => {
    await ModulesModel.updateReport(reportId, { isFavorite });
    await refreshDataOps();
    if (selectedReport && selectedReport.reportId === reportId) {
      const updated = await ModulesModel.getReport(reportId);
      setSelectedReport(updated);
    }
  };

  const renderPage = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-4xl mb-4">🛰️</div>
            <div className="text-gray-600">Loading...</div>
          </div>
        </div>
      );
    }

    // Block access to pages the user's role doesn't allow
    if (!canAccessPage(user?.role, currentPage)) {
      return dashboardData ? <DashboardView data={dashboardData} burnCharts={burnCharts} onNavigate={setCurrentPage} user={user} /> : null;
    }

    switch (currentPage) {
      case 'dashboard':
        return dashboardData ? <DashboardView data={dashboardData} burnCharts={burnCharts} onNavigate={setCurrentPage} user={user} /> : null;
      case 'modules':
        return modules.length > 0 ? <ModulesView modules={modules} onNavigate={setCurrentPage} user={user} /> : null;
      case 'plantrack':
        if (showMilestoneEngine) {
          return <MilestoneEngineView
            onBack={() => setShowMilestoneEngine(false)}
            projects={planTrackData?.projects || []}
          />;
        }
        if (showSprintsView) {
          return <SprintsManagementView
            sprints={planTrackData?.sprints || []}
            onBack={() => setShowSprintsView(false)}
            onCreateSprint={handleCreateSprint}
            onUpdateSprint={handleUpdateSprint}
          />;
        }
        if (selectedProjectId && projectDetail) {
          return <ProjectDetailView detail={projectDetail} onBack={handleBackToProjects} onRefresh={handleRefreshProjectDetail} user={user} onEdit={() => setShowEditProject(true)}
            onCreateMilestone={() => setShowAddMilestone(true)}
            onEditMilestone={handleEditMilestone}
            onCompleteMilestone={handleCompleteMilestone}
          />;
        }
        return planTrackData ? <PlanTrackView data={planTrackData} onSelectProject={handleSelectProject} onShowAddProject={() => setShowAddProject(true)} onShowSprints={() => setShowSprintsView(true)} onShowMilestones={() => setShowMilestoneEngine(true)} onShowEmailSummary={() => setShowEmailSummary(true)} onShowPostUpdateReport={() => setShowPostUpdateReport(true)} /> : null;
      case 'devops':
        return devOpsData ? (
          <DevOpsModule
            data={devOpsData}
            viewMode={devOpsViewMode}
            group={devOpsGroup}
            onViewModeChange={(mode) => { setDevOpsViewMode(mode); }}
            onGroupChange={setDevOpsGroup}
            onAddTask={() => setShowAddDevOpsTask(true)}
            onTaskClick={handleDevOpsTaskClick}
            onStatusChange={handleDevOpsStatusChange}
            onSendEod={() => { setEodModule('devops'); setShowSendEod(true); }}
            onEodSettings={() => setShowEodSettings(true)}
          />
        ) : null;
      case 'dataops':
        return dataOpsData ? (
          <DataOpsModule
            data={dataOpsData}
            viewMode={dataOpsViewMode}
            onViewModeChange={setDataOpsViewMode}
            onAddPipeline={() => setShowAddPipeline(true)}
            onAddReport={() => setShowAddReport(true)}
            onPipelineClick={handleSelectPipeline}
            onReportClick={handleSelectReport}
            onTriggerPipeline={handleTriggerPipeline}
            onToggleFavorite={handleToggleFavorite}
            onTaskClick={handleDataOpsTaskClick}
            onStatusChange={handleDataOpsStatusChange}
            onAddTask={() => setShowAddDataOpsTask(true)}
            onSendEod={() => { setEodModule('dataops'); setShowSendEod(true); }}
            onEodSettings={() => setShowEodSettings(true)}
          />
        ) : null;
      case 'learntrack':
        return learnTrackData ? (
          <LearnTrackModule
            data={learnTrackData}
            viewMode={learnTrackViewMode}
            onViewModeChange={setLearnTrackViewMode}
            onAddCourse={() => setShowAddCourse(true)}
            onCourseClick={handleCourseClick}
            onEnroll={handleEnrollUser}
            onUpdateProgress={handleUpdateEnrollment}
            filterSkill={learnFilterSkill}
            filterGroup={learnFilterGroup}
            onFilterSkill={setLearnFilterSkill}
            onFilterGroup={setLearnFilterGroup}
          />
        ) : null;
      case 'timeboard':
        return timeBoardData ? (
          <TimeBoardModule
            data={timeBoardData}
            weekStart={currentWeekStart}
            onWeekChange={handleWeekChange}
            onAddEntry={(date, category) => setShowTimeEntry({ date, category })}
            onDeleteEntry={handleDeleteTimeEntry}
            onSubmitTimesheet={handleSubmitTimesheet}
            onApproveTimesheet={handleApproveTimesheet}
          />
        ) : null;
      case 'leaveboard':
        return leaveBoardData ? (
          <LeaveBoardModule
            data={leaveBoardData}
            onRequestLeave={() => setShowRequestLeave(true)}
            onApproveRequest={(id) => handleUpdateLeaveRequest(id, { status: 'approved', approvedBy: user.name })}
            onRejectRequest={(id) => handleUpdateLeaveRequest(id, { status: 'rejected' })}
          />
        ) : null;
      case 'quality':
        return qualityData ? (
          <QmsModule
            data={qualityData}
            stage={qmsStage}
            onStageChange={handleQmsStageChange}
            onApproveReq={handleApproveRequirement}
            onRejectReq={handleRejectRequirement}
            onAddReq={() => setShowAddRequirement(true)}
            onUpdateProject={handleUpdateQaProject}
            onAddTestCase={() => setShowAddTestCase(true)}
            onPassTC={handlePassTestCase}
            onFailTC={handleFailTestCase}
            onReportBug={handleReportBug}
            onEditBug={handleEditBug}
            onProjectChange={handleQmsProjectChange}
            onSendEod={() => setShowQaEod(true)}
            onEodSettings={() => setShowEodSettings(true)}
          />
        ) : null;
      case 'quicktrack':
        return quickTrackData ? (
          <QuickTrackModule
            data={quickTrackData}
            filterStatus={qtFilterStatus}
            filterPriority={qtFilterPriority}
            filterCategory={qtFilterCategory}
            onFilterStatus={setQtFilterStatus}
            onFilterPriority={setQtFilterPriority}
            onFilterCategory={setQtFilterCategory}
            onResetFilters={() => { setQtFilterStatus(''); setQtFilterPriority(''); setQtFilterCategory(''); }}
            onAddTask={() => setShowAddQuickTask(true)}
            onStatusChange={handleUpdateQuickTask}
            onDelete={handleDeleteQuickTask}
            onEdit={handleEditQuickTask}
            projects={allProjects}
          />
        ) : null;
      case 'vendorevents':
        return vendorEventsData ? (
          <VendorEventsModule
            data={vendorEventsData}
            filterVendor={veFilterVendor}
            filterWorkflow={veFilterWorkflow}
            filterStatus={veFilterStatus}
            onFilterVendor={handleVeFilterVendor}
            onFilterWorkflow={handleVeFilterWorkflow}
            onFilterStatus={handleVeFilterStatus}
            onResetFilters={handleVeResetFilters}
            onAddVendor={() => setShowAddVendor(true)}
            onDeleteVendor={handleDeleteVendor}
          />
        ) : null;
      case 'tasks':
        return <TaskManagementView
          data={taskManageData}
          filters={taskManageFilters}
          onFilterChange={handleTaskManageFilter}
          onBulkUpdate={handleTaskManageBulk}
          onRefresh={handleTaskManageRefresh}
        />;
      case 'reports':
        return <ReportsView data={reportsData} />;
      case 'audit':
        return <AuditTrailView
          logs={auditLogs}
          stats={auditStats}
          filters={auditFilters}
          onFilterChange={handleAuditFilterChange}
          onLoadMore={handleAuditLoadMore}
          hasMore={auditHasMore}
          loading={auditLoading}
        />;
      case 'settings':
        return <SettingsView onClearData={handleClearData} user={user} authToken={authToken} />;
      default:
        return <DashboardView data={dashboardData} burnCharts={burnCharts} onNavigate={setCurrentPage} />;
    }
  };

  // Auth guard: show loading spinner, login page, or main app
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-800 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-5xl mb-4">🛰️</div>
          <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  if (resetToken) {
    return <ResetPasswordView token={resetToken} onDone={() => setResetToken(null)} />;
  }

  if (needsSetup) {
    return <SetupView onSetupComplete={(token, userData) => { setNeedsSetup(false); handleLogin(token, userData); }} />;
  }

  if (!user) {
    return <LoginView onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} onMenuClick={() => setMenuOpen(true)} onLogout={handleLogout} onChangePassword={() => setShowChangePassword(true)} />
      <Navigation
        currentPage={currentPage}
        onNavigate={(page) => { setCurrentPage(page); setSelectedProjectId(null); setProjectDetail(null); setShowSprintsView(false); setShowMilestoneEngine(false); }}
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        user={user}
      />
      <main>
        {renderPage()}
      </main>
      {showAddProject && <AddProjectModal onClose={() => setShowAddProject(false)} onSubmit={handleCreateProject} />}
      {showEmailSummary && <EmailSummaryModal onClose={() => setShowEmailSummary(false)} onSend={handleSendEmailSummary} />}
      {showPostUpdateReport && <PostUpdateReportModal onClose={() => setShowPostUpdateReport(false)} />}
      {showSendEod && <SendEodModal module={eodModule} onClose={() => setShowSendEod(false)} />}
      {showQaEod && <SendQaEodModal onClose={() => setShowQaEod(false)} />}
      {showEodSettings && <EodSettingsModal onClose={() => setShowEodSettings(false)} />}
      {showAddSprint && <AddSprintModal onClose={() => setShowAddSprint(false)} onSubmit={handleCreateSprint} />}
      {showEditProject && projectDetail && <EditProjectModal
        project={projectDetail.project}
        sprints={projectDetail.allSprints || planTrackData?.sprints || []}
        onClose={() => setShowEditProject(false)}
        onSubmit={handleUpdateProject}
      />}
      {showAddDevOpsTask && <AddDevOpsTaskModal onClose={() => setShowAddDevOpsTask(false)} onSubmit={handleCreateDevOpsTask} teamMembers={allTeamMembers.filter(m => m.team_module === 'devops')} />}
      {showAddDataOpsTask && <AddDevOpsTaskModal onClose={() => setShowAddDataOpsTask(false)} onSubmit={handleCreateDataOpsTask} teamMembers={allTeamMembers.filter(m => m.team_module === 'dataops')} />}
      {selectedDevOpsTask && <TaskDetailPanel task={selectedDevOpsTask} onClose={() => setSelectedDevOpsTask(null)} onUpdate={handleDevOpsTaskUpdate} user={user} teamMembers={allTeamMembers} />}
      {selectedBug && <BugDetailPanel bug={selectedBug} onClose={() => setSelectedBug(null)} user={user} readOnly={true} />}
      {showTimeEntry && <TimeEntryModal date={showTimeEntry.date} category={showTimeEntry.category} onClose={() => setShowTimeEntry(null)} onSubmit={handleCreateTimeEntry} />}
      {showRequestLeave && <RequestLeaveModal balance={leaveBoardData?.balances} onClose={() => setShowRequestLeave(false)} onSubmit={handleCreateLeaveRequest} />}
      {showAddPipeline && <AddPipelineModal onClose={() => setShowAddPipeline(false)} onSubmit={handleCreatePipeline} />}
      {showAddReport && <AddReportModal onClose={() => setShowAddReport(false)} onSubmit={handleCreateReport} />}
      {selectedPipeline && <PipelineDetailModal pipeline={selectedPipeline} onClose={() => setSelectedPipeline(null)} onUpdate={handleUpdatePipeline} onDelete={handleDeletePipeline} onTrigger={handleTriggerPipeline} />}
      {selectedReport && <ReportDetailModal report={selectedReport} onClose={() => setSelectedReport(null)} onUpdate={handleUpdateReport} onDelete={handleDeleteReport} onToggleFavorite={handleToggleFavorite} />}
      {showAddCourse && <AddCourseModal onClose={() => setShowAddCourse(false)} onSubmit={handleCreateCourse} skills={learnTrackData?.skills} />}
      {selectedCourse && <CourseDetailModal course={selectedCourse} onClose={() => setSelectedCourse(null)} onUpdate={handleUpdateCourse} onDelete={handleDeleteCourse} onEnroll={handleEnrollUser} />}
      {showAddVendor && <AddVendorModal onClose={() => setShowAddVendor(false)} onSubmit={handleCreateVendor} />}
      {showAddQuickTask && <AddQuickTaskModal onClose={() => setShowAddQuickTask(false)} onSubmit={handleCreateQuickTask} projects={allProjects} teamMembers={allTeamMembers} />}
      {editingQuickTask && <EditQuickTaskModal task={editingQuickTask} onClose={() => setEditingQuickTask(null)} onSubmit={handleSaveQuickTask} projects={allProjects} user={user} teamMembers={allTeamMembers} />}
      {showAddRequirement && <AddRequirementModal onClose={() => setShowAddRequirement(false)} onSubmit={handleCreateRequirement} projectId={qualityData?.project?.projectId} />}
      {showAddTestCase && <AddTestCaseModal onClose={() => setShowAddTestCase(false)} onSubmit={handleCreateTestCase} projectId={qualityData?.project?.projectId} />}
      {showReportBug && <ReportBugModal onClose={() => { setShowReportBug(false); setReportBugPrefill(null); }} onSubmit={handleCreateBug} projectId={qualityData?.project?.projectId} testCases={qualityData?.testCases} prefillTc={reportBugPrefill} />}
      {editingBug && <EditBugModal bug={editingBug} onClose={() => setEditingBug(null)} onSave={handleUpdateBug} onDelete={handleDeleteBug} />}
      {showChangePassword && <ChangePasswordModal authToken={authToken} onClose={() => setShowChangePassword(false)} />}
      {showAddMilestone && <AddMilestoneModal
        onClose={() => setShowAddMilestone(false)}
        onSubmit={handleCreateMilestone}
        projectId={selectedProjectId}
        projects={planTrackData?.projects || []}
      />}
      {editingMilestoneData && <EditMilestoneModal
        milestone={editingMilestoneData}
        allMilestones={planTrackData?.milestones || []}
        onClose={() => { setEditingMilestoneId(null); setEditingMilestoneData(null); }}
        onSubmit={(data) => handleUpdateMilestone(editingMilestoneId, data)}
        onDelete={handleDeleteMilestone}
      />}
      <footer className="text-center py-8 text-gray-600 text-sm">
        ORBIT System v1.0 | React + Cloudflare Pages/Workers MVC Architecture
      </footer>
    </div>
  );
};

export default App;